"use client";

import { safeGetItem, safeSetItem } from "@/lib/storage/safe-storage";
import Image from "next/image";
import Link from "next/link";
import {
  Fragment,
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
  type Ref,
} from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { v4 as uuidv4 } from "uuid";
import { ApiError, createProject, forkProject } from "@/lib/api";
import {
  cloneErrorMessage,
  isRetryableCloneError,
} from "@/lib/clone/error-copy";
import { createClient } from "@/lib/supabase/client";
import { useEntitlementRevalidate } from "@/lib/billing/use-entitlement-revalidate";
import { setPendingAttachments } from "@/lib/pending-attachments";
import {
  persistPendingAttachments,
  restorePendingAttachments,
} from "@/lib/pending-attachments-idb";
import { PromptAttachments } from "@/components/prompt-attachments";
import { StickyPromptBar } from "@/components/home/sticky-prompt-bar";
import {
  useImprovePrompt,
  type UseImprovePromptReturn,
} from "@/hooks/use-improve-prompt";
import {
  useTypewriterPlaceholder,
  PROMPT_HINT_PHRASES,
  PROMPT_HINT_TAILS,
} from "@/hooks/use-typewriter-placeholder";
import { ImproveButton, RevertButton } from "@/components/prompt-improve";
import { customToast } from "@/lib/toast";
import {
  Sparkles,
  Star,
  Quote,
  Code2,
  Zap,
  Globe,
  AlertCircle,
  Shield,
  Clock,
  RotateCcw,
  BarChart3,
  Search,
  Check,
  Loader2,
  LayoutTemplate,
  Layers,
  X,
  Users,
  CalendarDays,
  KanbanSquare,
  Bot,
  MessageCircle,
  Briefcase,
  ArrowLeft,
  ArrowRight,
  Eye,
  LayoutDashboardIcon,
  Menu,
  ThumbsUp,
  Repeat2,
} from "lucide-react";
import { FooterThemeToggle } from "@/components/footer-theme-toggle";
import { AnnualUpgradePrompt } from "@/components/annual-upgrade-prompt";
import { PausedBanner } from "@/components/paused-banner";
import { PaymentIssueBanner } from "@/components/payment-issue-banner";
import {
  type TemplateId,
  TEMPLATE_OPTIONS,
  TEMPLATE_STARTER_EXAMPLES,
  isTemplateId,
  selectTemplateForPrompt,
} from "@/lib/templates";

import { PandaLogo, PandaEmoji } from "@/components/panda-logo";
import { PandaBrandLogo } from "@/components/panda-brand-logo";
import {
  CreditsResetCard,
  type CreditsResetAction,
} from "@/components/credits/credits-reset-card";
import {
  deriveCreditView,
  buildCreditsResetActions,
} from "@/lib/credits/display";
import { HomeUserAccountMenu } from "@/components/home-user-account-menu";
import { HeroPromptTypewriterOverlay } from "@/components/hero-prompt-typewriter";
import { CommunityTemplateCard } from "@/components/community/community-template-card";
import { normalizeCommunityPreviewUrl } from "@/lib/community/preview-url";
import { SignupModal } from "@/components/auth/signup-modal";
import { LoginModal } from "@/components/auth/login-modal";
import { NeverBuiltSurvey } from "@/components/home/never-built-survey";
import { PricingSection } from "@/components/pricing/pricing-section";
import { WithPandaComparison } from "@/components/home/with-panda-comparison";
import { clearPendingAuth, useAuthModalSync } from "@/lib/pending-auth";
import {
  stashHomePrompt,
  readHomePromptStash,
  clearHomePromptStash,
  saveHomePromptDraft,
  readHomePromptDraft,
  clearHomePromptDraft,
} from "@/lib/home-prompt-stash";
import {
  handleStartFreeClick,
  type StartFreeSource,
} from "@/lib/marketing/start-free";
import {
  stashCloneIntent,
  readCloneIntent,
  clearCloneIntent,
  type CloneIntent,
  type CloneTarget,
} from "@/lib/home-clone-stash";
import { decideCloneResume } from "@/lib/home-clone-resume";
import { cn } from "@/lib/utils";
import { MobileNavLinks } from "@/components/mobile-nav-links";
import { navLinksByKeys, type NavKey } from "@/lib/nav-links";
import { hasHigherPlan } from "@/lib/plans";
import { useTheme } from "next-themes";
import { PersonalizationWizardInline } from "@/components/onboarding/personalization-wizard-inline";
import { type PersonalizationAnswers } from "@/lib/onboarding/personalization";
import {
  claimOnboardingOffer,
  flushPendingOnboardingShown,
  onboardingWizardIsOwed,
  sendWithRetries,
} from "@/lib/onboarding/shown-marker";
import { isEmailVerificationGatePending } from "@/lib/onboarding/verification-gate";
import {
  canOpenOnboarding,
  homeOnboardingEntryContext,
} from "@/lib/onboarding/entry-point";

import { Skeleton } from "@/components/ui/skeleton";
import { captureUtm, syncUtmToServer } from "@/lib/utm";
// Type-only: erased at compile time, so next/cache and the Supabase server
// client never reach the browser bundle.
import type { HomepageBuild } from "@/lib/community/homepage-builds";
import type { HomepageTemplate } from "@/lib/community/homepage-templates";
import type { HeroContent } from "@/lib/site-content";
// Runtime import, unlike the line above: lib/hero-markup.ts is deliberately
// dependency-free so the parser can be shared with the Slack routes without
// pulling next/cache or the Supabase server client into the browser bundle.
import { heroVariantId, parseHeroMarkup } from "@/lib/hero-markup";
// Same reasoning: lib/hero-wrapping.ts is a pure planner with no runtime
// dependency of its own, shared by the renderer below and its unit tests.
import { planHeroLine, type HeroNode } from "@/lib/hero-wrapping";

// A logged-out prompt must resume into exactly ONE project no matter how many
// auth tabs/callbacks fire (signup, email verification, magic link in a new
// tab). The pending project id is minted once at submit and threaded through
// the stash + the `pid` URL param, so every resume path converges on the same
// /project/<id>. This is the idempotency key that stops the duplicate builds /
// double credit charges the QA hit. Returns the candidate when it's a real
// uuid, otherwise a fresh one (legacy links without `pid`, tampering).
const PENDING_PROJECT_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function resolvePendingProjectId(
  candidate: string | null | undefined,
): string {
  return candidate && PENDING_PROJECT_ID_RE.test(candidate)
    ? candidate
    : uuidv4();
}

// How one surface paints the two things hero markup can mark. Passed in per
// call site rather than baked into the renderer, because the correct answer
// differs by surface — see the note on each constant below.
interface HeroPaint {
  /** Class for a **marked** run. */
  accent: string;
  /** Class for a __marked__ run. */
  bold: string;
}

// The accent word KEEPS the brand ramp — a deliberate exception to PANDA-2025,
// decided after seeing it both ways.
//
// It is not a CTA and nothing about it is an action: it is the brand's one warm
// note on an otherwise green page, and that ticket's rule is about controls that
// read as danger. Every actual control around it — the submit, the composer
// ring, the nav CTA — is green.
//
// Note for anyone auditing colour by grep: the v15 pair is #FF6A2C→#FF2D55, the
// v15 hero's own warm ramp, NOT the three brand hexes most palette guards scan
// for. Both ramps are pinned in
// tests/unit/components/non-modal-green-palette.test.ts
// (KEPT_BRAND), which marks them as intentional exceptions rather than
// oversights — so a future sweep neither greens them silently nor reports them
// as a miss. Keep both strings intact if you touch this.
const V15_HERO_ACCENT_CLASS =
  "bg-linear-to-r from-[#FF6A2C] to-[#FF2D55] bg-clip-text text-transparent";
const LEGACY_HERO_ACCENT_CLASS =
  "font-[family-name:var(--font-rubik),sans-serif] font-medium tracking-normal bg-linear-to-r from-[#FF5606] via-[#D52A26] to-[#AA0346] bg-clip-text text-transparent";

// Bold must never render LIGHTER than the copy around it, which is what a flat
// `font-bold` would do inside a headline that is already `font-extrabold` — an
// editor asking for emphasis would get de-emphasis. So each surface names a
// weight above its own: font-black (900) over the extrabold headings, and
// semibold + full-contrast text over the muted, normal-weight subheadings.
// Geist and Rubik are both loaded as variable fonts spanning 900 (app/layout),
// so these are real weights, not synthesised ones.
const V15_HERO_PAINT: HeroPaint = {
  accent: V15_HERO_ACCENT_CLASS,
  bold: "font-black",
};
const V15_SUBHEADING_PAINT: HeroPaint = {
  accent: V15_HERO_ACCENT_CLASS,
  bold: "font-semibold text-foreground",
};
const LEGACY_HERO_PAINT: HeroPaint = {
  accent: LEGACY_HERO_ACCENT_CLASS,
  bold: "font-bold",
};
const LEGACY_SUBHEADING_PAINT: HeroPaint = {
  accent: LEGACY_HERO_ACCENT_CLASS,
  bold: "font-semibold text-foreground",
};

// Renders editable hero copy: **word** becomes the brand gradient span, __word__
// becomes bold, and Enter becomes a line break — so a non-technical editor can
// restyle the headline from Slack without markup, and without any path that
// would put author-supplied HTML into the page. The grammar and its guard rails
// live in lib/hero-markup.ts; this function only paints what that returns.
//
// It also decides where the copy may WRAP. Each line is planned into phrases by
// lib/hero-wrapping.ts and rendered as nested spans, so the browser is left with
// break opportunities only where a break reads as intentional — never after a
// connector like "Get", "and" or "by", which is how the headline kept wrapping
// as "Build your app. Get / customers.". Each nesting level is one rung of that
// module's ladder: the inner runs are held at every width, the outer ones only
// from the width that can fit them, so a long phrase relaxes on a small phone
// instead of forcing a horizontal scroll.

/** The space between two planned nodes — the break the plan allows there. */
function joinWithSpaces(nodes: ReactNode[]): ReactNode[] {
  return nodes.flatMap((node, index) => (index === 0 ? [node] : [" ", node]));
}

function renderHeroNode(
  node: HeroNode,
  paint: HeroPaint,
  key: string,
): ReactNode {
  if (node.kind === "run") {
    const painted = node.spans.map((span, spanIndex) => (
      <span
        key={`${key}-${spanIndex}`}
        // `|| undefined` so an unmarked run — most of the copy — emits a bare
        // <span> instead of class="" on every word group.
        className={
          cn(span.accent && paint.accent, span.bold && paint.bold) || undefined
        }
      >
        {span.text}
      </span>
    ));
    // A lone word needs no wrapper, and must not get one: `whitespace-nowrap`
    // would take away the break `break-words` relies on to keep a 60-character
    // word inside a 360px screen.
    return node.hold ? (
      <span key={key} className="whitespace-nowrap">
        {painted}
      </span>
    ) : (
      <Fragment key={key}>{painted}</Fragment>
    );
  }

  return (
    <span key={key} className={node.className}>
      {joinWithSpaces(
        node.children.map((child, childIndex) =>
          renderHeroNode(child, paint, `${key}-${childIndex}`),
        ),
      )}
    </span>
  );
}

function renderHighlighted(text: string, paint: HeroPaint) {
  return parseHeroMarkup(text).flatMap((line, lineIndex) => {
    const nodes = joinWithSpaces(
      planHeroLine(line).map((node, nodeIndex) =>
        renderHeroNode(node, paint, `hero-${lineIndex}-${nodeIndex}`),
      ),
    );
    return lineIndex === 0
      ? nodes
      : [<br key={`hero-br-${lineIndex}`} />, ...nodes];
  });
}

interface DBTemplate {
  id: string;
  slug: string;
  name: string;
  description: string;
  category: string | null;
  icon_name: string | null;
  screenshot_url: string | null;
  starter_prompt: string | null;
  source_project_id: string | null;
  use_count: number;
  sort_order: number;
}
import { signOutCurrentDevice } from "@/lib/auth/logout";
import {
  trackTemplateUsed,
  identifyUser,
  trackHomepagePathSelected,
  trackHomepagePromptSubmitted,
  trackHomepageBusinessUrlSubmitted,
  trackHomepagePromptTyped,
  trackHomepageCloneClicked,
  trackHomepageCloneFailed,
  trackHomepageCloneVerificationRequired,
  trackHomepageCloneVerificationResumed,
  trackHomepageViewLiveClicked,
  trackHomepageViewed,
  initAnalytics,
  type HomepageCloneSurface,
  trackPreSignupIntentCaptured,
  trackPreSignupIntentRestored,
  trackPreSignupIntentLost,
  trackAuthReturnedToProjectAction,
  trackUpgradeEntrypointClicked,
  identifyOnboardingSegment,
  trackOnboardingShown,
} from "@/lib/analytics";
import { buildPostHogPersonProperties } from "@/lib/auth-user-metadata";

const EXAMPLES = TEMPLATE_STARTER_EXAMPLES;

// Hero mascot removed in the conversion redesign — it distracted from the
// prompt box (ticket). Kept commented so it's trivial to restore.
// const HERO_MASCOT_SRC = "/pirate-panda-mascot.svg";

const HERO_IDEA_CHIPS = [
  "Dog walking marketplace",
  "Recruitment platform",
  "Fitness coaching app",
  "Property management platform",
];

// Conversion redesign: the Community, "Panda vs traditional" comparison, and
// inline Pricing sections were removed from the homepage (approved in the
// ticket). They're flag-gated rather than deleted so the previous page stays
// intact and can be revisited — flip this to `true` to bring them all back.
const SHOW_LEGACY_HOME_SECTIONS: boolean = false;

// ── v15 homepage (client rebuild) ───────────────────────────────────────────
// The prompt-first redesign per panda-home-v15.html. ON by default; flip to
// `false` to fall back to the previous homepage (kept intact below this branch
// so it stays fully revisitable). Typed `boolean` so the legacy return path
// below is never flagged as unreachable code.
const SHOW_HOME_V15: boolean = true;

// The "Enhance prompt" (✨) trigger is hidden for now at the client's request.
// Flagged off rather than deleted so it's a one-line restore; the `improve`
// state stays wired, so flipping this back to `true` is the only switch needed.
// Typed `boolean` so TS keeps the render branch live (not narrowed to
// unreachable/unused).
const SHOW_ENHANCE_PROMPT: boolean = false;

// The example prompts for the animated typewriter hint now live in one shared
// place (hooks/use-typewriter-placeholder → PROMPT_HINT_PHRASES) so the hero,
// the bottom CTA, and the pricing box all cycle the same list.

// Templates FALLBACK grid — rendered only when the DB has no real clonable
// templates (empty gallery, or local dev with no service role). When real
// templates ARE available the homepage renders those instead (real screenshot,
// "Clone this"); see getHomepageTemplates / initialTemplates. These fallback
// cards are prompt inspiration, NOT clonable sources: each has a `fill` prompt
// and no source project, so clicking one loads the prompt into the hero box and
// the AI writes the app from scratch. The copy ("Use this prompt") and the
// illustrative art below both have to keep saying exactly that — see the note
// on V15_TEMPLATE_IMAGES.
type V15TemplateCard = {
  name: string;
  desc: string;
  fill: string;
};
const V15_TEMPLATE_CARDS: V15TemplateCard[] = [
  {
    name: "Booking app that takes payments",
    desc: "Slots, calendars, payments.",
    fill: "Build a booking app that takes online payments, with services, calendars, and time slots.",
  },
  {
    name: "CRM for my team",
    desc: "Pipeline, contacts, deals.",
    fill: "Build a CRM with a sales pipeline, contacts, deals, and an activity feed.",
  },
  {
    name: "SaaS landing page with pricing",
    desc: "Hero, features, pricing, signups.",
    fill: "Build a SaaS landing page with a hero, features, a pricing section, and email signups.",
  },
  {
    name: "Online store with checkout",
    desc: "Products, cart, checkout.",
    fill: "Build an online store with product listings, a shopping cart, and checkout.",
  },
];

// Illustrative art for the four homepage template cards (order matches
// V15_TEMPLATE_CARDS above). Deliberately generic photography, NOT app
// screenshots: these cards only prefill a prompt, so a real project screenshot
// here would promise a visual result the AI cannot be relied on to reproduce.
const V15_TEMPLATE_IMAGES = [
  "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?auto=format&fit=crop&w=720&h=480&q=82",
  "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=720&h=480&q=82",
  "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=720&h=480&q=82",
  "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=720&h=480&q=82",
];

// ⚠️ PLACEHOLDER social proof for the redesign preview — these are NOT real
// customers. The reference wireframe ships these as placeholders and its own
// handoff note says to replace them with verified real quotes before go-live;
// our "real data only" rule means the same. Swap the copy below for real
// testimonials (one edit), or set SHOW_HOME_TESTIMONIALS to false to ship the
// page WITHOUT the proof + testimonials sections until real quotes exist.
const SHOW_HOME_TESTIMONIALS: boolean = true;

// ⚠️ PLACEHOLDER, FABRICATED testimonials for the "What founders are saying"
// marquee. These are NOT real customers — the names/quotes are invented and the
// set is intentionally region-diverse so the wall reads as global. They exist
// only to demo the layout in the preview. DO NOT ship to production as-is:
// publishing fake testimonials with invented customers is deceptive-advertising
// risk (e.g. FTC). Replace every entry with verified real quotes before go-live
// (or set SHOW_HOME_TESTIMONIALS to false to hide the section until then).
const HOME_TESTIMONIALS: { quote: string; name: string; role: string }[] = [
  {
    quote:
      "Went from idea to first paying customer in 4 days. No dev, no agency. Just Panda.",
    name: "James K.",
    role: "Founder, BookEasy",
  },
  {
    quote:
      "The template gallery answered a question I'd had for months — what should I build? Launched in a week.",
    name: "Sofia R.",
    role: "Indie maker, PetPro",
  },
  {
    quote:
      "My booking app brought in 60 customers in the first two weeks. I've never written a line of code.",
    name: "David A.",
    role: "Owner, FixIt Local",
  },
  {
    quote:
      "I shipped my store over a weekend and made my first sale on Monday. Panda handled everything.",
    name: "Wei Chen",
    role: "Co-founder, ShopLink",
  },
  {
    quote:
      "We replaced a ₹4L agency quote with Panda — same product, live in days, not months.",
    name: "Priya Nair",
    role: "Founder & CEO, TutorNest",
  },
  {
    quote:
      "Bookings doubled after I rebuilt our site with Panda. No engineers involved at all.",
    name: "Mateo García",
    role: "Solo founder, Reservaya",
  },
  {
    quote:
      "From a rough sketch to a real, paying salon app in under a week. My customers love it.",
    name: "Aisha Khan",
    role: "Salon owner, GlowCare",
  },
  {
    quote:
      "Our analytics dashboard would've taken months. Panda had it ready before our launch call.",
    name: "Liang Wei",
    role: "Product lead, DataDash",
  },
  {
    quote:
      "The first 100 orders came through the store Panda built — I just described what I wanted.",
    name: "Ananya Iyer",
    role: "Maker, CraftCart",
  },
  {
    quote:
      "My coaching app paid for itself in week one. No code, no agency, no waiting.",
    name: "Tomás Silva",
    role: "Fitness coach, FitPlan",
  },
  {
    quote:
      "Sold out our first event through a site I put together in a single afternoon.",
    name: "Mei Lin",
    role: "Event organizer, EventHop",
  },
  {
    quote:
      "Tenants book their own viewings now. Panda saved me from hiring a developer.",
    name: "Omar Haddad",
    role: "Landlord, RentEasy",
  },
  {
    quote:
      "Connected 300 farmers to buyers with a marketplace I built entirely on my own.",
    name: "Chidi Okoro",
    role: "Co-founder, AgroLink",
  },
  {
    quote:
      "Launched my food-ordering app in two days. Orders started coming in the same night.",
    name: "Yuki Tanaka",
    role: "Restaurant owner, BentoBox",
  },
  {
    quote:
      "Clients book and pay in one smooth flow — and I built the whole thing with Panda.",
    name: "Emma Brooks",
    role: "Freelance designer, StudioSpace",
  },
];

// Varied avatar colors so the testimonial founders read as distinct real
// people, not one templated block. Light + dark pairs, assigned by index.
const AVATAR_COLORS = [
  "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
  "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
  "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
];

// Star ratings for the placeholder testimonials (aligned by index with
// HOME_TESTIMONIALS). Intentionally varied — mostly 5 with some 4.5 and 4 — so
// the wall doesn't read as fake "all 5 stars". Replace alongside the real
// testimonials when they land.
const TESTIMONIAL_RATINGS = [
  5, 4.5, 5, 5, 4.5, 4, 5, 4.5, 5, 5, 4, 4.5, 5, 5, 4.5,
];

// First initial of each name part for the avatar circle, e.g. "Marcus T." → "MT".
function initialsFor(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

// Focus the hero prompt box, whichever homepage version is rendering.
//
// The id differs by flag: the v15 hero renders BuildComposerV15
// (#v15-build-input), the legacy hero renders PromptComposer (#prompt-input).
// Several shared handlers hard-coded the legacy id, so under v15 — the live
// layout — they optional-chained into nothing and silently did nothing. That
// is what made `?focus=prompt` (the landing spot for every /pricing free-tier
// CTA) a no-op: the visitor arrived on the homepage and no box was focused.
function focusHeroPrompt({
  preventScroll = false,
}: { preventScroll?: boolean } = {}) {
  const el =
    document.getElementById("v15-build-input") ??
    document.getElementById("prompt-input");
  el?.focus({ preventScroll });
}

/**
 * Mobile-only nav menu for the homepage header (hidden from sm: up, where the
 * same destinations are laid out as visible links). Holds the secondary nav —
 * Pricing, plus Log in (signed out) or Dashboard (signed in). The primary
 * "Start free" CTA is NOT repeated here: the mobile bar already renders it
 * beside the burger, so listing it in the dropdown too showed two identical
 * Start-free buttons on screen at once.
 */

/**
 * The entries the homepage's desktop bar shows, in this order — a curated
 * subset of NAV_LINKS, not the whole menu (Home is the page you're on, and
 * Templates/Discord belong to the fuller menus). Auth gating comes from each
 * link's own `requiresAuth`, so Projects and Referrals appear only once signed
 * in, exactly as before.
 */
const HOME_NAV_KEYS: readonly NavKey[] = ["pricing", "projects", "referrals"];

/** Shared styling for those links — previously duplicated per link, and the
 *  two copies had already drifted (Pricing had whitespace-nowrap, Projects
 *  did not, so a two-word label could wrap). */
const HOME_NAV_LINK_CLS =
  "hidden whitespace-nowrap text-[13.5px] font-medium text-muted-foreground transition-colors hover:text-foreground sm:inline-flex";

function HomeMobileNavMenu({
  signedIn,
  onLogin,
}: {
  signedIn: boolean;
  onLogin: () => void;
}) {
  const [open, setOpen] = useState(false);

  // Lock page scroll + close on Escape while the full-screen menu is open,
  // matching the AppHeader / pricing overlays so behavior is identical.
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = original;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        aria-label={open ? "Close menu" : "Open menu"}
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 sm:hidden"
      >
        {open ? (
          <X className="h-5 w-5" strokeWidth={2} />
        ) : (
          <Menu className="h-5 w-5" strokeWidth={2} />
        )}
      </button>

      {open &&
        // Portaled to <body> so the fixed-position overlay escapes the top
        // nav's `backdrop-blur` ancestor. A backdrop-filter ancestor becomes
        // the containing block for `position: fixed` children, which would
        // otherwise trap this overlay inside the ~58px nav and leave the page
        // showing through below it. Pricing/AppHeader avoid this by rendering
        // their overlay as a sibling of the blurred nav; here we portal.
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Main menu"
            className="fixed inset-0 z-[70] flex flex-col bg-background sm:hidden"
          >
            {/* Top bar: theme toggle (left) + close X (right) — same as the
                AppHeader / pricing mobile menus. */}
            <div className="flex h-14 items-center justify-between px-4">
              <FooterThemeToggle />
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="flex h-6 w-6 items-center justify-center text-[#1A1A1B] transition-opacity hover:opacity-70 dark:text-foreground"
              >
                <X size={24} strokeWidth={2} />
              </button>
            </div>

            {/* Log in renders inside MobileNavLinks (directly under Home) for
                signed-out visitors. "Start free" stays out of the menu — it
                lives in the bar beside the burger (always visible on mobile),
                so repeating it here put two identical Start-free buttons on
                screen. */}
            <MobileNavLinks
              signedIn={signedIn}
              activeKey="home"
              onNavigate={() => setOpen(false)}
              onLogin={onLogin}
            />
          </div>,
          document.body,
        )}
    </>
  );
}

// Renders a 5-star row for a rating, supporting half stars (e.g. 4.5 → four
// full + one half). Empty positions show as faint outlines so varied ratings
// read as genuine rather than a uniform block of 5s.
function StarRating({ rating }: { rating: number }) {
  return (
    <div
      className="flex items-center gap-0.5"
      role="img"
      aria-label={`${rating} out of 5 stars`}
    >
      {Array.from({ length: 5 }).map((_, s) => {
        if (rating >= s + 1) {
          return (
            <Star
              key={s}
              className="h-4 w-4 fill-orange-400 text-orange-400"
              aria-hidden
            />
          );
        }
        if (rating >= s + 0.5) {
          return (
            <span key={s} className="relative inline-flex h-4 w-4" aria-hidden>
              <Star className="absolute inset-0 h-4 w-4 text-orange-400/35" />
              <Star className="absolute inset-0 h-4 w-4 fill-orange-400 text-orange-400 [clip-path:inset(0_50%_0_0)]" />
            </span>
          );
        }
        return (
          <Star key={s} className="h-4 w-4 text-orange-400/35" aria-hidden />
        );
      })}
    </div>
  );
}

// "Apps built this week" live counter, count-up animated (0 → count) the first
// time the real number arrives. Renders nothing until there's real data;
// respects prefers-reduced-motion (jumps straight to the final value).
//   • variant="pill" (hero): a glassy pill + the "most built this week" line.
//   • variant="showcase" (proof card): a big gradient number with a label.
function LiveAppsCounter({
  count,
  mostBuilt,
  variant = "pill",
}: {
  count: number | null;
  mostBuilt: string[];
  variant?: "pill" | "showcase";
}) {
  const showCount = count !== null && count > 0;
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (count === null || count <= 0) return;
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    if (prefersReduced) {
      // No animation — set the real value on the next frame (kept out of the
      // effect body so it doesn't trigger a synchronous-setState warning).
      raf = requestAnimationFrame(() => setDisplay(count));
      return () => cancelAnimationFrame(raf);
    }
    let startTs: number | null = null;
    const duration = 1200;
    const step = (ts: number) => {
      if (startTs === null) startTs = ts;
      const progress = Math.min((ts - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setDisplay(Math.round(eased * count));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [count]);

  // Big-number "showcase" — used inside the proof card. Just the metric, no
  // pill, no "most built" line. Fail-closed like the pill.
  if (variant === "showcase") {
    if (!showCount) return null;
    return (
      <>
        <span className="inline-flex items-center gap-2 rounded-full border border-black/[0.06] bg-black/[0.03] px-3.5 py-1.5 dark:border-white/10 dark:bg-white/[0.05]">
          <span className="relative flex h-2 w-2 shrink-0" aria-hidden>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500/70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          <span className="font-[family-name:var(--font-rubik),sans-serif] text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Apps built with Panda
          </span>
        </span>
        <div className="mt-6 bg-linear-to-br from-[#FF5606] via-[#D52A26] to-[#AA0346] bg-clip-text font-[family-name:var(--font-rubik),sans-serif] text-6xl font-extrabold leading-none tabular-nums tracking-tight text-transparent sm:text-7xl lg:text-8xl">
          {display.toLocaleString("en-US")}
        </div>
        <div className="mx-auto mt-6 h-px w-16 bg-linear-to-r from-transparent via-[#D52A26]/40 to-transparent" />
        <p className="mx-auto mt-5 max-w-md font-[family-name:var(--font-rubik),sans-serif] text-sm leading-6 text-muted-foreground sm:text-base">
          Real products shipped by founders building with Panda — no developers,
          no agencies.
        </p>
      </>
    );
  }

  if (!showCount && mostBuilt.length === 0) return null;

  return (
    <div className="mx-auto mt-6 flex flex-col items-center gap-2">
      {showCount && (
        <div className="inline-flex items-center gap-2.5 rounded-full border border-white/70 bg-white/85 px-4 py-1.5 shadow-sm backdrop-blur-md dark:border-white/10 dark:bg-white/[0.06]">
          <span className="relative flex h-2 w-2 shrink-0" aria-hidden>
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500/70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          <span className="font-[family-name:var(--font-rubik),sans-serif] text-sm leading-5 text-muted-foreground">
            <span className="font-semibold tabular-nums text-foreground">
              {display.toLocaleString("en-US")}
            </span>{" "}
            apps built with Panda
          </span>
        </div>
      )}
      {mostBuilt.length > 0 && (
        <p className="font-[family-name:var(--font-rubik),sans-serif] text-xs text-muted-foreground">
          <span className="font-medium text-foreground/70">
            Most built this week:
          </span>{" "}
          {mostBuilt.join(" · ")}
        </p>
      )}
    </div>
  );
}

function starterPromptForTemplate(id: TemplateId): string {
  const ex = EXAMPLES.find((e) => e.templateId === id);
  if (ex) return ex.prompt;
  const opt = TEMPLATE_OPTIONS.find((t) => t.id === id);
  return opt?.description ?? "";
}

const TEMPLATE_CARD_ICONS: Record<TemplateId, typeof Code2> = {
  "base-nextjs": Sparkles,
  "tpl-landing": Zap,
  "tpl-dashboard": BarChart3,
  "tpl-portfolio": Layers,
  "tpl-blog": Globe,
  "tpl-store": LayoutTemplate,
  "tpl-crm": Users,
  "tpl-booking": CalendarDays,
  "tpl-project": KanbanSquare,
  "tpl-ai": Bot,
  "tpl-social": MessageCircle,
  "tpl-marketplace": Briefcase,
};

const ICON_BY_NAME: Record<string, typeof Code2> = {
  Sparkles,
  Zap,
  BarChart3,
  Layers,
  Globe,
  LayoutTemplate,
  Users,
  CalendarDays,
  KanbanSquare,
  Bot,
  MessageCircle,
  Briefcase,
  Code2,
};

const HOMEPAGE_TEMPLATE_ORDER: string[] = [
  "tpl-landing",
  "base-nextjs",
  "tpl-dashboard",
  "tpl-portfolio",
  "tpl-blog",
  "tpl-crm",
  "tpl-booking",
  "tpl-store",
];
const homepageTemplateRank = (slug: string) => {
  const i = HOMEPAGE_TEMPLATE_ORDER.indexOf(slug);
  return i === -1 ? Number.POSITIVE_INFINITY : i;
};

/** Curated preview art per template (Unsplash). */
const TEMPLATE_PREVIEW_IMAGES: Record<
  TemplateId,
  { src: string; alt: string }
> = {
  "base-nextjs": {
    src: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=720&h=480&q=82",
    alt: "Developer workspace — blank Next.js starter",
  },
  "tpl-landing": {
    src: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=720&h=480&q=82",
    alt: "SaaS landing page with hero and pricing",
  },
  "tpl-dashboard": {
    src: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=720&h=480&q=82",
    alt: "Analytics dashboard with charts and KPIs",
  },
  "tpl-portfolio": {
    src: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=720&h=480&q=82",
    alt: "Creative portfolio with project grid",
  },
  "tpl-blog": {
    src: "https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=720&h=480&q=82",
    alt: "Blog with article cards and search",
  },
  "tpl-store": {
    src: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=720&h=480&q=82",
    alt: "E-Commerce store with products and cart",
  },
  "tpl-crm": {
    src: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=720&h=480&q=82",
    alt: "CRM dashboard with sales pipeline and contacts",
  },
  "tpl-booking": {
    src: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?auto=format&fit=crop&w=720&h=480&q=82",
    alt: "Booking platform with calendar and services",
  },
  "tpl-project": {
    src: "https://images.unsplash.com/photo-1611224923853-80b023f02d71?auto=format&fit=crop&w=720&h=480&q=82",
    alt: "Project manager with kanban board and tasks",
  },
  "tpl-ai": {
    src: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=720&h=480&q=82",
    alt: "AI SaaS platform with API and code examples",
  },
  "tpl-social": {
    src: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=720&h=480&q=82",
    alt: "Community forum with discussions and members",
  },
  "tpl-marketplace": {
    src: "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=720&h=480&q=82",
    alt: "Job board with listings and company profiles",
  },
};

const HOW_IT_WORKS: {
  step: string;
  title: string;
  description: string;
  image: string;
  imageLabel: string;
}[] = [
  {
    step: "01",
    title: "AI-Powered Design Assistance",
    description:
      "Let AI help you effortlessly create stunning, tailored designs. Our tool analyzes your needs and crafts designs in minutes.",
    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=900&h=620&q=80",
    imageLabel: "AI ideation and layout generation",
  },
  {
    step: "02",
    title: "Customize & Preview",
    description:
      "Customize every detail of your design in real-time. See instant previews and make adjustments on the fly.",
    image:
      "https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&w=900&h=620&q=80",
    imageLabel: "Live preview and fine-tuning",
  },
  {
    step: "03",
    title: "Launch Instantly",
    description:
      "When you're ready, launch your creation with one click and share it instantly with the world.",
    image:
      "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=900&h=620&q=80",
    imageLabel: "Deploy and share your product",
  },
];

// The old PRICING_PLANS block (dead since the /pricing page took over) was
// removed in PANDA-1414: it still carried the $19/$49/$99, 650/2,000/4,000-
// credit catalog from two pricing models ago, and any future re-wire would
// have resurrected prices that match nothing we charge. Pricing renders from
// lib/pricing/credit-tiers.ts + lib/plans.ts — never from a local copy here.

type PlanIconComp = React.FC<React.SVGProps<SVGSVGElement>>;

const IdeaIcon: PlanIconComp = (props) => (
  <svg viewBox="0 0 18 22" fill="currentColor" {...props}>
    <path d="M10.0859 8.40039C10.2754 8.04027 10.7192 7.89642 11.085 8.0791C11.4553 8.26427 11.6058 8.71456 11.4209 9.08496L10.75 8.75C11.3655 9.05773 11.4159 9.08349 11.4199 9.08594V9.08691L11.4131 9.10059L11.4014 9.12305C11.3924 9.1393 11.3803 9.15946 11.3662 9.18262C11.3381 9.22893 11.299 9.28853 11.248 9.35645C11.1461 9.49232 10.9943 9.66551 10.7812 9.83594C10.473 10.0825 10.0502 10.3093 9.5 10.4229V14H12.3633C13.5577 14 14.4012 15.1706 14.0234 16.3037L13.6904 17.3037C13.4809 17.9321 12.9379 18.3795 12.2979 18.4785L11.918 19.6201C11.5436 20.743 10.4922 21.5 9.30859 21.5H8.19141C7.00796 21.4999 5.95738 20.7428 5.58301 19.6201L5.20215 18.4785C4.56271 18.3791 4.02097 17.9316 3.81152 17.3037L3.47754 16.3037C3.09985 15.1706 3.94341 14.0002 5.1377 14H8V10.4229C7.44968 10.3093 7.02704 10.0825 6.71875 9.83594C6.50556 9.66538 6.35293 9.49238 6.25098 9.35645C6.19996 9.28842 6.16095 9.22895 6.13281 9.18262C6.11879 9.15953 6.10757 9.13928 6.09863 9.12305L6.08691 9.10059L6.08008 9.08691L6.0791 9.08594C6.07961 9.0852 6.10128 9.07436 6.75 8.75L6.0791 9.08496C5.89415 8.71463 6.04388 8.26437 6.41406 8.0791C6.77987 7.8962 7.2246 8.04009 7.41406 8.40039C7.41444 8.40103 7.41444 8.40233 7.41504 8.40332C7.42007 8.41161 7.43206 8.43056 7.45117 8.45605C7.48985 8.50762 7.55694 8.58462 7.65625 8.66406C7.84537 8.81533 8.18229 9 8.75 9C9.31771 8.99996 9.65467 8.81533 9.84375 8.66406C9.94295 8.58468 10.0092 8.5076 10.0479 8.45605C10.0671 8.43044 10.0789 8.41163 10.084 8.40332L10.0811 8.41016L10.082 8.40918L10.084 8.4043L10.0869 8.39941L10.0859 8.40039ZM7.00586 19.1455C7.17597 19.6558 7.6535 19.9999 8.19141 20H9.30859C9.84658 20 10.324 19.6559 10.4941 19.1455L10.71 18.5H6.79102L7.00586 19.1455ZM5.1377 15.5C4.96725 15.5002 4.84744 15.6673 4.90137 15.8291L5.23438 16.8291C5.26843 16.9311 5.3641 17 5.47168 17H12.0303C12.1377 16.9998 12.2326 16.931 12.2666 16.8291L12.6006 15.8291C12.6545 15.6672 12.5339 15.5 12.3633 15.5H5.1377ZM8.75 0C13.5753 0 17.5 3.87408 17.5 8.66895C17.5 10.3034 17.0427 11.8341 16.248 13.1396C16.0327 13.4935 15.5706 13.606 15.2168 13.3906C14.8632 13.1752 14.7516 12.7141 14.9668 12.3604C15.623 11.2824 16 10.02 16 8.66895C16 4.71688 12.7613 1.5 8.75 1.5C4.73871 1.5 1.5 4.71688 1.5 8.66895C1.50002 10.02 1.87702 11.2824 2.5332 12.3604C2.74844 12.7141 2.63676 13.1752 2.2832 13.3906C1.92939 13.606 1.46733 13.4935 1.25195 13.1396C0.457284 11.8341 1.56536e-05 10.3034 0 8.66895C0 3.87408 3.92474 0 8.75 0Z" />
  </svg>
);

const ChartIncreaseIcon: PlanIconComp = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M3 2.25C3.41421 2.25 3.75 2.58579 3.75 3V20.25H21C21.4142 20.25 21.75 20.5858 21.75 21C21.75 21.4142 21.4142 21.75 21 21.75H3C2.58579 21.75 2.25 21.4142 2.25 21V3C2.25 2.58579 2.58579 2.25 3 2.25ZM17.0312 5.41406C17.3297 5.17533 17.76 5.1995 18.0303 5.46973L20.0303 7.46973C20.3232 7.76262 20.3232 8.23738 20.0303 8.53027C19.7374 8.82317 19.2626 8.82317 18.9697 8.53027L18.1748 7.73535C17.8215 11.658 16.258 14.1496 14.1943 15.667C11.8842 17.3656 9.07172 17.75 7 17.75C6.58579 17.75 6.25 17.4142 6.25 17C6.25 16.5858 6.58579 16.25 7 16.25C8.92825 16.25 11.3658 15.8844 13.3057 14.458C14.9652 13.2377 16.3573 11.1689 16.6797 7.61719L15.4688 8.58594C15.1453 8.84469 14.6728 8.7922 14.4141 8.46875C14.1553 8.1453 14.2078 7.67282 14.5312 7.41406L17.0312 5.41406Z" />
  </svg>
);

const FreePlanIcon: PlanIconComp = (props) => (
  <svg viewBox="0 0 28 28" fill="currentColor" {...props}>
    <path d="M14 0L17.9499 10.0501L28 14L17.9499 17.9499L14 28L10.0501 17.9499L0 14L10.0501 10.0501L14 0Z" />
  </svg>
);

const HobbyPlanIcon: PlanIconComp = (props) => (
  <svg viewBox="0 0 27 26" fill="currentColor" {...props}>
    <path d="M13.3149 0L16.5983 9.48083L26.6297 9.67376L18.6275 15.7262L21.5439 25.3262L13.3149 19.586L5.08595 25.3262L8.00234 15.7262L0.000149727 9.67376L10.0316 9.48083L13.3149 0Z" />
  </svg>
);

const ProPlanIcon: PlanIconComp = (props) => (
  <svg viewBox="0 0 25 28" fill="currentColor" {...props}>
    <path d="M12.1245 0L14.9175 9.16238L24.2489 7L17.7105 14L24.2489 21L14.9175 18.8376L12.1245 28L9.33151 18.8376L0.000156403 21L6.53851 14L0.000156403 7L9.33151 9.16238L12.1245 0Z" />
  </svg>
);

const ScalePlanIcon: PlanIconComp = (props) => (
  <svg viewBox="0 0 27 28" fill="currentColor" {...props}>
    <path d="M13.3149 0L15.0411 8.6874L21.5439 2.67376L17.8341 10.7166L26.6297 9.67376L18.9009 14L26.6297 18.3262L17.8341 17.2834L21.5439 25.3262L15.0411 19.3126L13.3149 28L11.5888 19.3126L5.08595 25.3262L8.79577 17.2834L0.000149727 18.3262L7.72894 14L0.000149727 9.67376L8.79577 10.7166L5.08595 2.67376L11.5888 8.6874L13.3149 0Z" />
  </svg>
);

const PRICING_PLAN_ICONS: Record<
  string,
  { Icon: PlanIconComp; className: string }
> = {
  Free: { Icon: FreePlanIcon, className: "text-[#22A2FD]" },
  Hobby: { Icon: HobbyPlanIcon, className: "text-[#FDB022]" },
  Pro: { Icon: ProPlanIcon, className: "text-[#FF5606]" },
  Scale: { Icon: ScalePlanIcon, className: "text-[#B90C3B]" },
};

interface UserProject {
  id: string;
  title: string;
  prompt: string;
  plan: { title?: string; features?: string[] } | null;
  created_at: string;
  updated_at: string;
}

interface CommunityProject {
  id: string;
  user_id: string;
  title: string;
  prompt: string;
  screenshot_url?: string | null;
  vercelUrl?: string | null;
  created_at: string;
  updated_at: string;
  likes?: number;
  hasLiked?: boolean;
  remixCount?: number;
  authorName?: string;
  avatarSrc?: string;
  avatarEmoji?: string;
  avatarColor?: string;
  avatarType?: string;
  showBadge?: boolean;
}

type HeroMode = "idea" | "business";

interface PromptComposerProps {
  /** "" for the hero (keeps id="prompt-input" for the focus helpers); "cta-"
   *  for the footer copy, so the two cards' element ids never collide. */
  idPrefix: string;
  cardRef?: Ref<HTMLDivElement>;
  heroMode: HeroMode;
  setHeroMode: (m: HeroMode) => void;
  prompt: string;
  setPrompt: (v: string) => void;
  focused: boolean;
  setFocused: (v: boolean) => void;
  ideaError: string | null;
  setIdeaError: (v: string | null) => void;
  generatingIdea: boolean;
  onSubmitIdea: () => void;
  attachFiles: File[];
  setAttachFiles: (f: File[]) => void;
  businessUrl: string;
  setBusinessUrl: (v: string) => void;
  businessUrlError: string | null;
  setBusinessUrlError: (v: string | null) => void;
  businessUrlLimitReached: boolean;
  setBusinessUrlLimitReached: (v: boolean) => void;
  generatingGrowth: boolean;
  onSubmitGrowth: () => void;
  normaliseWebsiteUrl: (
    raw: string,
  ) => { ok: true; url: string } | { ok: false; error: string | null };
  onUpgrade: () => void;
  /** Whether the account has a higher plan to upgrade to. false on Scale (top
   * of the ladder), where the "Upgrade" link is hidden — an upgrade wouldn't
   * raise the project limit. */
  canUpgradePlan: boolean;
}

// Shared hero/footer prompt card: the mode tabs ("I have an idea" / "I already
// have a business") + the chat card. Module-level so each instance is a stable
// React type — it re-renders on keystroke but never REMOUNTS, so the textarea
// keeps focus. The hero and footer copy pass different idPrefix values so their
// element ids don't duplicate; all state/handlers are passed in from Home so
// behaviour is identical to the original inline card.
function PromptComposer({
  idPrefix,
  cardRef,
  heroMode,
  setHeroMode,
  prompt,
  setPrompt,
  focused,
  setFocused,
  ideaError,
  setIdeaError,
  generatingIdea,
  onSubmitIdea,
  attachFiles,
  setAttachFiles,
  businessUrl,
  setBusinessUrl,
  businessUrlError,
  setBusinessUrlError,
  businessUrlLimitReached,
  setBusinessUrlLimitReached,
  generatingGrowth,
  onSubmitGrowth,
  normaliseWebsiteUrl,
  onUpgrade,
  canUpgradePlan,
}: PromptComposerProps) {
  const promptInputId = `${idPrefix}prompt-input`;
  const ideaErrorId = `${idPrefix}idea-error`;
  const businessUrlInputId = `${idPrefix}business-url-input`;
  const businessUrlErrorId = `${idPrefix}business-url-error`;
  return (
    <>
      {/* Mode tabs */}
      <div className="mt-7 flex items-center justify-center sm:mt-10">
        <div className="inline-flex items-center gap-1 rounded-full border border-white/60 bg-white/70 p-1 shadow-sm backdrop-blur-md dark:border-white/10 dark:bg-white/[0.06]">
          <button
            type="button"
            onClick={() => {
              if (heroMode !== "idea") {
                setHeroMode("idea");
                trackHomepagePathSelected("idea");
              }
            }}
            aria-pressed={heroMode === "idea"}
            className={cn(
              "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-center font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium leading-5 tracking-normal transition-all sm:px-4 sm:py-2 sm:text-base sm:leading-6",
              heroMode === "idea"
                ? "bg-white text-[#1A1A1B] shadow-sm dark:bg-white/10 dark:text-foreground"
                : "text-[#1A1A1B]/60 hover:text-[#1A1A1B] dark:text-muted-foreground dark:hover:text-foreground",
            )}
          >
            <IdeaIcon
              className={cn(
                "hidden h-4 w-4 sm:block",
                heroMode === "idea"
                  ? "text-[#22A2FD]"
                  : "text-muted-foreground",
              )}
            />
            I have an idea
          </button>
          <button
            type="button"
            onClick={() => {
              if (heroMode !== "business") {
                setHeroMode("business");
                trackHomepagePathSelected("existing_business");
              }
            }}
            aria-pressed={heroMode === "business"}
            className={cn(
              "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-center font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium leading-5 tracking-normal transition-all sm:px-4 sm:py-2 sm:text-base sm:leading-6",
              heroMode === "business"
                ? "bg-white text-[#1A1A1B] shadow-sm dark:bg-white/10 dark:text-foreground"
                : "text-[#1A1A1B]/60 hover:text-[#1A1A1B] dark:text-muted-foreground dark:hover:text-foreground",
            )}
          >
            <ChartIncreaseIcon
              className={cn(
                "hidden h-4 w-4 sm:block",
                heroMode === "business"
                  ? "text-violet-500"
                  : "text-muted-foreground",
              )}
            />
            I already have a business
          </button>
        </div>
      </div>

      {/* Chat card */}
      <div ref={cardRef} className="relative mx-auto mt-6 w-full max-w-3xl">
        <div className="rounded-3xl border border-white/60 bg-white/95 shadow-[0_18px_50px_-24px_rgba(99,102,241,0.18),0_6px_18px_-10px_rgba(255,90,60,0.10)] backdrop-blur dark:border-white/10 dark:bg-[#11131d]/90 dark:shadow-[0_24px_60px_-28px_rgba(0,0,0,0.8)]">
          <div className="relative overflow-hidden rounded-3xl text-card-foreground">
            {heroMode === "business" ? (
              <>
                <div className="flex min-h-28 flex-col px-5 pt-4 pb-2 sm:min-h-27">
                  <input
                    id={businessUrlInputId}
                    type="url"
                    inputMode="url"
                    autoComplete="off"
                    placeholder="Enter your website URL e.g. panda.new"
                    aria-label="Your existing website URL"
                    aria-invalid={Boolean(businessUrlError)}
                    aria-describedby={
                      businessUrlError ? businessUrlErrorId : undefined
                    }
                    className="block w-full bg-transparent text-left text-[16px] leading-relaxed text-card-foreground caret-violet-500 outline-none placeholder:text-muted-foreground/50"
                    value={businessUrl}
                    onChange={(e) => {
                      setBusinessUrl(e.target.value);
                      if (businessUrlError) setBusinessUrlError(null);
                      if (businessUrlLimitReached)
                        setBusinessUrlLimitReached(false);
                    }}
                    onBlur={() => {
                      const trimmed = businessUrl.trim();
                      if (!trimmed) {
                        setBusinessUrlError(null);
                        setBusinessUrlLimitReached(false);
                        return;
                      }
                      const result = normaliseWebsiteUrl(trimmed);
                      if (!result.ok) {
                        setBusinessUrlError(
                          result.error ??
                            "That doesn't look like a valid website URL.",
                        );
                        setBusinessUrlLimitReached(false);
                        return;
                      }
                      if (result.url !== trimmed) {
                        setBusinessUrl(result.url);
                      }
                      setBusinessUrlError(null);
                      setBusinessUrlLimitReached(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                      e.preventDefault();
                      onSubmitGrowth();
                    }}
                  />
                  {businessUrlError && (
                    <div
                      id={businessUrlErrorId}
                      role="alert"
                      className="mt-2.5 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50/80 px-3 py-2 text-[13px] leading-snug text-rose-700 shadow-sm dark:border-rose-500/30 dark:bg-rose-500/[0.08] dark:text-rose-200"
                    >
                      <AlertCircle
                        className="mt-0.5 h-4 w-4 shrink-0 text-rose-500 dark:text-rose-400"
                        strokeWidth={2}
                      />
                      <span className="flex-1">
                        {businessUrlError}
                        {businessUrlLimitReached && canUpgradePlan && (
                          <>
                            {" "}
                            To create more,{" "}
                            <button
                              type="button"
                              onClick={() => {
                                trackUpgradeEntrypointClicked({
                                  entry_point:
                                    "home_business_url_limit_upgrade",
                                  surface: "home",
                                });
                                onUpgrade();
                              }}
                              className="font-semibold underline underline-offset-2 hover:text-rose-800 dark:hover:text-rose-100"
                            >
                              Upgrade
                            </button>
                            .
                          </>
                        )}
                      </span>
                    </div>
                  )}
                  <p className="mt-auto pt-2 text-[12px] text-muted-foreground/80">
                    Panda will scan your website and start building your revenue
                    growth plan
                  </p>
                </div>
                <div className="flex items-center justify-end px-3 pb-3 pt-1 sm:px-4 sm:pb-4">
                  <button
                    type="button"
                    onClick={onSubmitGrowth}
                    disabled={generatingGrowth}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-full bg-linear-to-r from-[#FF5606] via-[#D52A26] to-[#AA0346] px-5 py-2.5 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-white shadow-md shadow-orange-500/30 transition-all",
                      generatingGrowth
                        ? "cursor-not-allowed opacity-60"
                        : "hover:shadow-lg hover:shadow-orange-500/40",
                    )}
                  >
                    <Sparkles className="h-4 w-4 text-white" />
                    {generatingGrowth ? "Starting…" : "Start growing"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <HeroPromptTypewriterOverlay
                  active={!prompt.trim() && !focused}
                />
                <textarea
                  id={promptInputId}
                  placeholder=""
                  aria-label="Describe the website you want to build"
                  aria-invalid={Boolean(ideaError)}
                  aria-describedby={ideaError ? ideaErrorId : undefined}
                  className="relative z-2 block min-h-28 w-full resize-none bg-transparent px-5 pb-2 pt-4 text-left font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 tracking-normal text-card-foreground caret-orange-500 outline-none placeholder:text-transparent sm:min-h-27"
                  rows={3}
                  value={prompt}
                  onChange={(e) => {
                    setPrompt(e.target.value);
                    if (ideaError) setIdeaError(null);
                  }}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                    if (e.shiftKey) return;
                    e.preventDefault();
                    onSubmitIdea();
                  }}
                />
                {ideaError && (
                  <div
                    id={ideaErrorId}
                    role="alert"
                    className="mx-3 mt-2.5 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50/80 px-3 py-2 text-[13px] leading-snug text-rose-700 shadow-sm sm:mx-4 dark:border-rose-500/30 dark:bg-rose-500/[0.08] dark:text-rose-200"
                  >
                    <AlertCircle
                      className="mt-0.5 h-4 w-4 shrink-0 text-rose-500 dark:text-rose-400"
                      strokeWidth={2}
                    />
                    <span className="flex-1">{ideaError}</span>
                  </div>
                )}
                <div className="flex items-center justify-between px-3 pb-3 pt-1 sm:px-4 sm:pb-4">
                  <div className="flex flex-1 items-center gap-2">
                    <PromptAttachments
                      files={attachFiles}
                      onChange={setAttachFiles}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={onSubmitIdea}
                    disabled={generatingIdea}
                    className={cn(
                      "inline-flex items-center gap-2 rounded-full bg-linear-to-r from-[#FF5606] via-[#D52A26] to-[#AA0346] px-5 py-2.5 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-white shadow-md shadow-orange-500/30 transition-all",
                      generatingIdea
                        ? "cursor-not-allowed opacity-60"
                        : !prompt.trim()
                          ? "cursor-not-allowed opacity-60"
                          : "hover:shadow-lg hover:shadow-orange-500/40",
                    )}
                  >
                    <FreePlanIcon className="h-4 w-4 text-white" />
                    {generatingIdea ? "Starting…" : "Start building"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

interface BuildComposerV15Props {
  /** "" for the hero, "final-" for the bottom CTA — keeps their ids distinct. */
  idPrefix: string;
  variant: "hero" | "compact";
  prompt: string;
  setPrompt: (value: string) => void;
  /** Kicks off the build. Optional text arg builds that EXACT prompt (used by
   *  the "Enhance prompt" accept path so it doesn't wait on async setPrompt). */
  onSubmit: (promptText?: string) => void;
  generating: boolean;
  autoFocus?: boolean;
  /** Called on the first real keystroke (analytics "prompt-typed"). The parent
   *  guards once-per-load, so this can fire on every non-empty change. */
  onPromptTyped?: () => void;
  /** The "Enhance prompt" state — OWNED BY THE PARENT and shared by every
   *  composer (hero + bottom CTA) so they all reflect one enhance/revert state
   *  (both boxes edit the same prompt). The parent records the applied text for
   *  the ?improved=1 build flag. */
  improve: UseImprovePromptReturn;
  /** Attachment state, shared with the parent so picked files flow into
   *  handleGenerate (same plumbing the legacy hero composer used). */
  attachFiles: File[];
  setAttachFiles: (files: File[]) => void;
}

// v15 prompt box — a single idea-only composer used by BOTH the hero and the
// repeated bottom CTA (distinct idPrefix so their ids never collide). Defined
// at module level so typing never remounts it (no focus loss). Shares the
// page's prompt state, so every chip / template / "clone this" fills this box.
function BuildComposerV15({
  idPrefix,
  variant,
  prompt,
  setPrompt,
  onSubmit,
  generating,
  autoFocus,
  onPromptTyped,
  improve,
  attachFiles,
  setAttachFiles,
}: BuildComposerV15Props) {
  const inputId = `${idPrefix}v15-build-input`;
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  // Lovable-style animated typewriter hint — cycles the shared example prompts,
  // backspacing only to the stem they share before typing the next. Paused
  // (holds the first phrase) once the box has text, since the native
  // placeholder isn't visible then anyway.
  //
  // The hero prefixes "Ask Panda to build" (below), so it cycles the verbless
  // tails — otherwise the two leads collide as "Ask Panda to build Build a
  // site…". The compact CTA shows the example alone and keeps the full phrase.
  // Both lists are module constants, so the reference stays stable per variant.
  const animatedPhrase = useTypewriterPlaceholder(
    variant === "hero" ? PROMPT_HINT_TAILS : PROMPT_HINT_PHRASES,
    prompt.trim().length === 0,
  );

  // Cursor in the hero box on load (conversion goal). preventScroll so the page
  // doesn't jump to the textarea.
  useEffect(() => {
    if (autoFocus) taRef.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-grow the textarea to fit its content so the whole box EXPANDS as lines
  // wrap — min-height on the box is only a floor, never a fixed height. The
  // box's flex-col + the row's mt-auto keep the submit row pinned to the bottom
  // as the card grows.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    // Cap the growth so a long (enhanced) prompt scrolls inside the box instead
    // of pushing the "+"/submit row off-screen.
    const MAX = 320;
    ta.style.height = `${Math.min(ta.scrollHeight, MAX)}px`;
    ta.style.overflowY = ta.scrollHeight > MAX ? "auto" : "hidden";
  }, [prompt]);

  // Hero keeps the static "Ask Panda to build" lead in front of the
  // animated example; the bottom CTA shows the animated example on its own
  // (no lead), per the client's spec.
  const placeholder =
    variant === "hero"
      ? `Ask Panda to build ${animatedPhrase}`
      : animatedPhrase;
  const disabled = prompt.trim().length === 0 || generating;

  // "Enhance prompt" state (run / revert / active / loading) is OWNED BY THE
  // PARENT and passed in via `improve`, so the hero and bottom-CTA boxes share
  // ONE enhance/revert state (they edit the same prompt). A successful rewrite
  // replaces the composer text in place; the build button then builds whatever
  // is in the box.

  const submit = () => {
    if (prompt.trim().length === 0) {
      taRef.current?.focus();
      return;
    }
    onSubmit(prompt);
  };

  return (
    <div
      className={cn(
        "relative mx-auto flex w-full flex-col border border-border bg-card text-left shadow-[0_0_80px_-28px_rgba(140,90,255,0.35)] transition-shadow focus-within:border-emerald-600/60 focus-within:shadow-[0_0_0_4px_rgba(5,150,105,0.15)]",
        variant === "hero"
          ? "max-w-[672px] min-h-[187px] rounded-[24px] p-6"
          : "max-w-[448px] min-h-[125px] rounded-[16px] p-4",
      )}
    >
      <textarea
        id={inputId}
        ref={taRef}
        rows={1}
        value={prompt}
        placeholder={placeholder}
        aria-label="Describe the app you want to build"
        onChange={(e) => {
          if (e.target.value.trim()) onPromptTyped?.();
          setPrompt(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        className={cn(
          // Placeholder is brighter (white/90) to pull the eye to it. The text
          // size is FLUID (15px on a 360px phone up to 18px by ~430px) so the
          // animated example prompt shrinks to fit instead of being cut off on
          // narrow screens — see PANDA responsive pass. It pairs with a taller
          // mobile min-height: native textarea placeholders soft-wrap, so the
          // longest phrases need vertical room to wrap into view rather than
          // being clipped by `overflow-hidden`. The mobile floor is expressed in
          // `em` so it tracks the fluid font: ~2 lines (compact) / ~3 lines
          // (hero) at ANY size the clamp resolves to — a fixed px floor would be
          // too short once the clamp reaches 18px on larger phones. Above `sm`
          // the copy fits on one line, so both drop back to a single-line floor.
          "mb-3 block w-full resize-none overflow-hidden border-0 bg-transparent px-1 pt-1 font-[family-name:var(--font-geist),sans-serif] text-[clamp(15px,4.2vw,18px)] leading-relaxed text-foreground outline-none placeholder:text-muted-foreground/70 dark:placeholder:text-white/90",
          variant === "hero" ? "min-h-[5.2em] sm:min-h-[56px]" : "min-h-[3.7em] sm:min-h-[28px]",
        )}
      />
      {/* Bottom row — "+" attachments on the left, circular submit arrow on the
          right. The Enhance trigger is hidden for now (at the client's request
          — see below). */}
      <div className="mt-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <PromptAttachments
            files={attachFiles}
            onChange={setAttachFiles}
            buttonClassName="h-11 w-11"
          />
          {/* Enhance prompt (✨) hidden for now — flip SHOW_ENHANCE_PROMPT to
              restore. `improve` stays wired so the flag is the only switch. */}
          {SHOW_ENHANCE_PROMPT &&
            (improve.active ? (
              <RevertButton onClick={improve.revert} />
            ) : (
              <ImproveButton
                onClick={improve.run}
                loading={improve.loading}
                disabled={prompt.trim().length < 8 || generating}
              />
            ))}
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={disabled}
          aria-label="Build my app"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-panda-gradient-success text-white shadow-[0_10px_24px_-8px_rgba(0,122,85,0.55)] transition-transform hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M12 19V5M5 12l7-7 7 7"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

/**
 * PANDA-811: given a raw /api/credits payload, decide whether the returning-user
 * "Your credits are back." card should show on the home hero, and return its
 * generic build actions (or null to stay hidden). Mirrors the project page's
 * detection exactly — hasDailyCreditsReset on the raw pre-roll shape — and shares
 * the SAME `panda:credits-reset-shown` localStorage key so the card fires once
 * per reset window across both surfaces (home + in-project chat). Pure + guarded;
 * never throws (private mode / SSR safe).
 */
function detectHomeCreditsReset(
  creds: Record<string, unknown>,
): CreditsResetAction[] | null {
  try {
    const view = deriveCreditView({
      plan: typeof creds.plan === "string" ? creds.plan : "free",
      credits_remaining:
        typeof creds.credits_remaining === "number"
          ? creds.credits_remaining
          : null,
      credits_total:
        typeof creds.credits_total === "number" ? creds.credits_total : null,
      daily_limit:
        typeof creds.daily_limit === "number" ? creds.daily_limit : null,
      daily_used:
        typeof creds.daily_used === "number" ? creds.daily_used : null,
      daily_resets_at:
        typeof creds.daily_resets_at === "string"
          ? creds.daily_resets_at
          : null,
    });
    // Server-authoritative signal: /api/credits repairs an overdue reset on read
    // and reports daily_reset_just_restored on the fetch that just restored a
    // capped user's allowance. The old hasDailyCreditsReset(view) heuristic can't
    // fire anymore because the payload is now fresh. `view` is still used for the
    // reset-boundary dedup key below.
    if (creds.daily_reset_just_restored !== true) return null;

    const lsKey = "panda:credits-reset-shown";
    try {
      // Same window already messaged (here or in-project) — don't repeat.
      if (window.localStorage.getItem(lsKey) === view.resetsAtIso) return null;
      window.localStorage.setItem(lsKey, view.resetsAtIso ?? "");
    } catch {
      // localStorage unavailable (private mode) — still show it this load.
    }
    return buildCreditsResetActions();
  } catch {
    return null;
  }
}

export default function Home({
  heroContent,
  initialUser,
  initialBuilds,
  initialTemplates,
  currentYear,
}: {
  heroContent: HeroContent;
  // Session resolved server-side (app/page.tsx) so the first paint has the
  // correct nav — null means "server confirmed logged out", not "unknown".
  initialUser: { email?: string; userMetadata?: Record<string, unknown> } | null;
  // "Built with Panda" cards, already filtered, ranked and capped to 3 by the
  // server. Rendering straight from props keeps them in the initial HTML.
  initialBuilds: HomepageBuild[];
  // Real, clonable gallery templates for the "Templates" cards (screenshot +
  // source project), resolved server-side. Empty → the hardcoded prompt cards
  // below are rendered instead.
  initialTemplates: HomepageTemplate[];
  // © year for the footers, resolved server-side. Reading the clock during
  // render would hydrate against the client's clock — a mismatch every New
  // Year's Eve for as long as a tab spans the boundary.
  currentYear: number;
}) {
  const [prompt, setPrompt] = useState("");
  // Exact prompt text the user accepted via the visible "Improve prompt" flow.
  // When the build fires with this same text, the /project URL carries
  // ?improved=1 so the silent server enhancer is skipped (no double-enhance).
  const improvedHomeTextRef = useRef<string | null>(null);

  // ONE shared "Enhance prompt" state for the whole page — the hero box, the
  // bottom-CTA box, and the sticky bar all edit the same `prompt`, so they
  // share this single enhance/revert state (no per-box copies fighting each
  // other). While a rewrite is applied, record the (possibly-edited) text into
  // improvedHomeTextRef so a build from ANY entry point carries ?improved=1 and
  // the silent server enhancer is skipped. Revert clears `active`, so the stale
  // ref no longer matches the restored original → it's enhanced normally.
  const improve = useImprovePrompt({
    getPrompt: () => prompt,
    setPrompt,
    onNotice: (message, tone, opts) =>
      tone === "error"
        ? customToast.error(message, opts)
        : customToast.success(message, opts),
  });
  useEffect(() => {
    if (improve.active) improvedHomeTextRef.current = prompt;
  }, [prompt, improve.active]);

  const [focused, setFocused] = useState(false);
  const [heroMode, setHeroMode] = useState<"idea" | "business">("idea");
  const [attachFiles, setAttachFiles] = useState<File[]>([]);
  // Guards the auto-resume push to /project/[id] from running twice when
  // both the mount effect and the SIGNED_IN auth listener decide to fire
  // (common race for in-modal email auth: home is already mounted, then
  // signup completes and emits SIGNED_IN while the mount-effect's
  // urlPrompt branch is also resolving).
  const homeResumeFiredRef = useRef(false);
  // Fires the analytics "prompt-typed" event at most once per page load — the
  // two v15 composers (hero + final) share this guard so a user typing in both
  // boxes only counts once.
  const promptTypedRef = useRef(false);
  const handlePromptTyped = () => {
    if (promptTypedRef.current) return;
    promptTypedRef.current = true;
    trackHomepagePromptTyped();
  };

  // Hero-prefill handoff: the /templates and /community pages send the user
  // here with `?prefill=<prompt>` to LOAD the prompt into the hero box (and
  // focus it) WITHOUT auto-building. Deliberately a different param from the
  // `?prompt=` auto-resume flow below, which submits straight to a build. We
  // strip the param after reading so a refresh/back doesn't re-apply it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const prefill = params.get("prefill");
    if (!prefill || !prefill.trim()) return;
    setPrompt(prefill);
    params.delete("prefill");
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
    );
    requestAnimationFrame(() => {
      document
        .getElementById("v15-build-input")
        ?.focus({ preventScroll: true });
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
    // Run once on mount — the param is consumed and stripped immediately.
  }, []);
  // "I already have a business" path — user pastes the URL of an existing
  // site so Growth Mode can run ICP/Offer derivation against it
  // without requiring a Panda-built project.
  const [businessUrl, setBusinessUrl] = useState("");
  // Whether the last submit came from the hero card or the sticky bottom
  // bar — the bar only renders its compact error chip for its own submits
  // so the two surfaces don't double-report.
  const [lastSubmitOrigin, setLastSubmitOrigin] = useState<"hero" | "sticky">(
    "hero",
  );
  // The sticky bottom bar duplicates the hero card's input, so it stays
  // hidden while the hero card is on screen and only slides in once the
  // user scrolls past it (PANDA-989).
  const heroCardRef = useRef<HTMLDivElement>(null);
  const [heroCardInView, setHeroCardInView] = useState(true);
  // The footer "Ready to build?" section has its OWN prompt input, so the
  // sticky bar must also hide once that card is on screen — otherwise the
  // bottom of the page shows two identical inputs at once (PANDA-989).
  const footerCardRef = useRef<HTMLDivElement>(null);
  const [footerCardInView, setFooterCardInView] = useState(false);
  // Live "apps built this week" count for the hero social-proof line. Count of
  // every project created (× APPS_BUILT_MULTIPLIER), served by
  // /api/apps-built-count, so it grows as people build. Stays null until loaded
  // and on any error, so the line hides rather than flashing "0".
  const [appsBuilt, setAppsBuilt] = useState<number | null>(null);
  const [businessUrlError, setBusinessUrlError] = useState<string | null>(null);
  // When the growth-flow project create call returns project_limit_reached,
  // we flag it here so the inline error can render a clickable "Upgrade"
  // link that opens the pricing modal.
  const [businessUrlLimitReached, setBusinessUrlLimitReached] = useState(false);
  const [generatingGrowth, setGeneratingGrowth] = useState(false);
  // Mirrors generatingGrowth/businessUrlError for the Build (idea) flow.
  // We now POST /api/projects from the home page before navigating, so we
  // need a place to surface 401/429/500 inline and a busy state to disable
  // double-click submissions.
  const [generatingIdea, setGeneratingIdea] = useState(false);
  const [ideaError, setIdeaError] = useState<string | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [carouselInitialized, setCarouselInitialized] = useState(false);
  // Track narrow viewports so the templates carousel can compress its
  // "fanned cards" horizontally on mobile (the desktop ±260–440px spread
  // overflows a phone screen). Same fan layout, just a tighter spread.
  const [isNarrowViewport, setIsNarrowViewport] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<TemplateId>("base-nextjs");
  const [templateTouched, setTemplateTouched] = useState(false);
  const [dbTemplates, setDbTemplates] = useState<DBTemplate[]>([]);
  /** Fast screenshot map fetched before full templates payload */
  const [screenshotMap, setScreenshotMap] = useState<Record<string, string>>(
    {},
  );
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  // Seeded from the server-resolved session (initialUser) so the first paint
  // already shows the right nav — never a logged-out flash for logged-in users
  // on refresh (PANDA-1292). The client auth.getUser() effect below re-confirms
  // it (same value → no visible change) and keeps it live via onAuthStateChange.
  const [user, setUser] = useState<{
    email?: string;
    userMetadata?: Record<string, unknown>;
  } | null>(initialUser);

  // ── Logged-out prompt draft (PANDA: don't lose what they typed) ──────────
  //
  // The resume stash only kicks in once the user *submits*. A visitor who types
  // an idea and then refreshes, wanders off to /templates, or closes the tab
  // used to come back to an empty box. Restore whatever they last typed.
  //
  // Functional update, and only into an *empty* box: the `?prefill=` handoff
  // from /templates and the resume-stash pre-fill are both deliberate, more
  // specific intents — whichever of these effects lands second must not clobber
  // them with a stale draft.
  useEffect(() => {
    const draft = readHomePromptDraft();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- localStorage is unreadable during render (SSR), so the restore has to land in a mount effect; it runs once and only into an empty box.
    if (draft) setPrompt((current) => (current.trim() ? current : draft));
  }, []);

  // Mirror of the above: persist while they type. Debounced so we're not
  // hitting localStorage on every keystroke, with a pagehide flush so a draft
  // typed and abandoned inside the debounce window still survives the exit.
  // Logged-out only — a signed-in user's prompt goes straight to /project/[id],
  // so there's nothing to recover and no reason to leave their text on disk.
  const promptRef = useRef(prompt);
  promptRef.current = prompt;
  useEffect(() => {
    if (user) return;
    const flush = () => saveHomePromptDraft(promptRef.current);
    const timer = setTimeout(flush, 300);
    window.addEventListener("pagehide", flush);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("pagehide", flush);
    };
  }, [prompt, user]);

  const [projects, setProjects] = useState<UserProject[]>([]);
  // Auth identity + signup time for the "never built" survey (PANDA-1245).
  // `projectsLoaded` gates the survey so it can't fire before we actually know
  // the user has zero projects (projects starts empty on mount).
  const [authUserId, setAuthUserId] = useState<string | null>(null);
  const [signupAtMs, setSignupAtMs] = useState<number | null>(null);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [recentPrompts, setRecentPrompts] = useState<UserProject[]>([]);
  const [communityProjects, setCommunityProjects] = useState<
    CommunityProject[]
  >([]);
  const [isLoadingCommunity, setIsLoadingCommunity] = useState(false);
  // Loading flags for the hard-nav handlers so the button shows a spinner
  // and can't be double-clicked during the fork/create round-trip + the
  // brief window before window.location.assign unloads the page.
  const [isGenerating, setIsGenerating] = useState(false);
  const [remixingProjectId, setRemixingProjectId] = useState<string | null>(
    null,
  );
  // Slug of the homepage template card whose clone is in flight — locks every
  // template card so a double-click can't create two projects (two credits).
  const [cloningTemplateSlug, setCloningTemplateSlug] = useState<string | null>(
    null,
  );
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [userPlan, setUserPlan] = useState<string>("free");

  // PANDA-811: the "Your credits are back." card for a returning free user whose
  // daily cap has reset. The project page already shows this in-chat; a returning
  // user usually lands HERE (the home hero), so we surface the same card. Null
  // until /api/credits both answers AND hasDailyCreditsReset detects the reset
  // moment; the generic build actions drive the hero composer when tapped.
  const [creditsResetActions, setCreditsResetActions] = useState<
    CreditsResetAction[] | null
  >(null);
  // Unified subscription model now collapses Build + Growth into a single
  // plan. Kept as a separate state for the locked-card lookup but defaults
  // to "free" since "trial" no longer exists.
  const [growthPlan, setGrowthPlan] = useState<string>("free");
  const [billingPeriod, setBillingPeriod] = useState<"annual" | "monthly">(
    "annual",
  );
  const [pricingMode, setPricingMode] = useState<"build" | "growth">("build");

  /**
   * The page's plan/credit read, in one place.
   *
   * It was written out twice — once in each of two mount paths — and read once
   * per load either way. So a purchase completing while the home page sat open
   * left the pricing section's "Current plan" lock on the plan the buyer had
   * just left, exactly as the nav chip above it used to keep the old balance.
   */
  const refreshPlan = useCallback(async () => {
    try {
      const res = await fetch("/api/credits", { cache: "no-store" });
      const creds = await res.json();
      if (creds?.error) return;
      setUserPlan(String(creds.plan ?? "free"));
      const resetActions = detectHomeCreditsReset(creds);
      if (resetActions) setCreditsResetActions(resetActions);
    } catch {
      // Non-critical: the plan lock stays where it is until the next read.
    }
  }, []);
  useEntitlementRevalidate(refreshPlan);

  const [selectedPlan, setSelectedPlan] = useState("Free");
  const [isStudent, setIsStudent] = useState(false);
  const [useStudentDiscount, setUseStudentDiscount] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Lock the page scroll while the full-screen mobile menu is open so
  // taps on links inside the overlay don't move the page underneath.
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [mobileMenuOpen]);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  // Which recovery CTA the auth-error banner shows:
  //   "verification" → expired email link        → "Sign up again"
  //   "session"      → expired/stale OAuth login  → "Sign in with Google"
  const [authErrorMode, setAuthErrorMode] = useState<
    "verification" | "session"
  >("verification");
  const [signupOpen, setSignupOpen] = useState(false);
  const [signupNext, setSignupNext] = useState<string>("/");
  // Pre-fill for the signup modal's email step, set only by the login modal's
  // "No account found — Sign up" handoff. Cleared on close so it never leaks
  // into a later, unrelated signup open.
  const [signupInitialEmail, setSignupInitialEmail] = useState<string>("");
  // Which surface opened the signup modal, for funnel analytics. Set by
  // openSignup and read by <SignupModal source=…>.
  const [signupSource, setSignupSource] = useState<string>("unspecified");
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginNext, setLoginNext] = useState<string>("/");
  // Friendly message shown inside the login modal after a failed OAuth
  // round-trip (Google / Facebook / LinkedIn) — set by the ?error=oauth
  // handler in the mount effect below. Provider-agnostic by design.
  const [loginInitialError, setLoginInitialError] = useState<string | null>(
    null,
  );
  const router = useRouter();

  function openSignup(
    nextPath: string = "/",
    email: string = "",
    source: string = "unspecified",
  ) {
    setSignupNext(nextPath);
    setSignupInitialEmail(email);
    setSignupSource(source);
    setSignupOpen(true);
  }

  // Every "Start free" CTA on this page routes through here: signed-out users
  // get the signup modal (carrying whatever they've typed into the hero),
  // signed-in users go to their projects. See lib/marketing/start-free.ts.
  function startFree(source: StartFreeSource) {
    handleStartFreeClick({
      source,
      signedIn: !!user,
      prompt,
      openSignup: (nextPath) => openSignup(nextPath, "", source),
      navigate: (href) => router.push(href),
    });
  }

  function openLogin(nextPath: string = "/") {
    setLoginNext(nextPath);
    setLoginInitialError(null);
    setLoginOpen(true);
  }

  useEffect(() => {
    if (templateTouched) return;
    setSelectedTemplate(selectTemplateForPrompt(prompt));
  }, [prompt, templateTouched]);

  // Keep the pricing-section highlight in sync with the user's actual
  // subscription. On initial mount userPlan is "free" so Free is
  // highlighted; when /api/credits resolves, the highlight hops to
  // their real plan. The user can still click any card to override
  // selectedPlan manually.
  useEffect(() => {
    const displayName =
      userPlan.charAt(0).toUpperCase() + userPlan.slice(1).toLowerCase();
    setSelectedPlan(displayName);
  }, [userPlan]);

  useEffect(() => {
    const onScroll = () => {
      setHeaderScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── homepage_viewed — the denominator for every homepage funnel (GT-03) ───
  //
  // Two things here are load-bearing and neither is obvious:
  //
  // 1. `initAnalytics()` is called even though <PostHogProvider> already calls
  //    it. That provider is an ANCESTOR of this page, and React flushes child
  //    effects BEFORE parent ones — so on a cold load this effect runs while
  //    `initialized` is still false, and every track function in lib/analytics
  //    opens with `if (!initialized) return`. The view would be dropped exactly
  //    once per session: on the first load, which is the only one that matters.
  //    initAnalytics is documented idempotent, so calling it here just makes
  //    the ordering irrelevant instead of depending on it.
  //
  // 2. The ref guard, not the empty dep array, is what makes this fire once.
  //    React StrictMode double-invokes mount effects in development against the
  //    same instance, so without it every local page load would report two
  //    views and halve the measured rate.
  //
  // `user` is read at mount on purpose: it is seeded from the server-resolved
  // session (initialUser), so it already carries the truth on the first render,
  // and someone who signs in later genuinely did view this page signed out.
  const homepageViewedRef = useRef(false);
  useEffect(() => {
    if (homepageViewedRef.current) return;
    homepageViewedRef.current = true;
    initAnalytics();
    trackHomepageViewed({
      variant: heroVariantId(heroContent.heading, heroContent.subheading),
      isLoggedIn: !!user,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live "apps built this week" count for the hero — counts every project
  // created (see /api/apps-built-count), so it grows as people build.
  // Fail-silent; fetched once on mount (the hero is always visible).
  useEffect(() => {
    fetch("/api/apps-built-count")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && typeof data.count === "number" && data.count > 0) {
          setAppsBuilt(data.count);
        }
      })
      .catch(() => {});
  }, []);

  // Drop visitors straight into the prompt on first paint so they can start
  // typing immediately (conversion redesign). Idea mode only — in business
  // mode #prompt-input doesn't exist (the URL input takes its place).
  // preventScroll stops the page jumping to the textarea, which sits below
  // the fold. Runs once on mount; we intentionally don't refocus on toggles.
  useEffect(() => {
    if (heroMode !== "idea") return;
    focusHeroPrompt({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const heroCard = heroCardRef.current;
    if (!heroCard) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroCardInView(entry.isIntersecting),
      // Flip a little before the card fully leaves the viewport so the bar
      // is already there when the hero input is no longer usable.
      { rootMargin: "-80px 0px 0px 0px" },
    );
    observer.observe(heroCard);
    return () => observer.disconnect();
  }, []);

  // Hide the sticky bar as the footer prompt card approaches, so the bottom of
  // the page never shows the footer input and the sticky bar at the same time.
  // The positive bottom rootMargin fires a little before the card actually
  // reaches the viewport edge, giving a clean hand-off with no overlap flash.
  useEffect(() => {
    const footerCard = footerCardRef.current;
    if (!footerCard) return;
    const observer = new IntersectionObserver(
      ([entry]) => setFooterCardInView(entry.isIntersecting),
      { rootMargin: "0px 0px 120px 0px" },
    );
    observer.observe(footerCard);
    return () => observer.disconnect();
  }, []);

  // Compress the templates carousel's sideways fan below the `sm` breakpoint
  // (640px) — matches Tailwind's `sm:` so the JS-driven transforms and the
  // CSS-driven sizing switch over at the same width.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setIsNarrowViewport(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (carouselInitialized || isLoadingTemplates) return;
    // Build the same sorted/sliced list the carousel renders, then find CRM.
    const sortedSlugs =
      dbTemplates.length > 0
        ? [...dbTemplates]
            .sort(
              (a, b) =>
                homepageTemplateRank(a.slug) - homepageTemplateRank(b.slug),
            )
            .slice(0, 8)
            .map((t) => t.slug)
        : [...TEMPLATE_OPTIONS]
            .sort(
              (a, b) => homepageTemplateRank(a.id) - homepageTemplateRank(b.id),
            )
            .slice(0, 8)
            .map((o) => o.id);
    if (sortedSlugs.length > 0) {
      const crmIdx = sortedSlugs.indexOf("tpl-crm");
      const defaultIdx =
        crmIdx >= 0 ? crmIdx : Math.floor(sortedSlugs.length / 2);
      setCarouselIndex(defaultIdx);
      setCarouselInitialized(true);
    }
  }, [carouselInitialized, isLoadingTemplates, dbTemplates.length]);

  useEffect(() => {
    // Capture UTM params on first visit (first-touch attribution)
    const utmData = captureUtm();
    syncUtmToServer(utmData);

    // Detect auth errors Supabase (GoTrue) appends to the URL as
    // ?error=&error_code=&error_description=. Two very different things land
    // here, so we must NEVER blindly print error_description:
    //   • Expired email-verification link (error_code=otp_expired).
    //   • A stale OAuth login handshake — e.g. "OAuth state not found or
    //     expired". GoTrue can't recover the original destination for a
    //     used-up/expired one-time state, so it bounces the browser to the
    //     Site URL (this homepage) with its raw error. A returning user who
    //     is ALREADY signed in can hit this when the browser re-sends an old
    //     handshake (reopened tab, Back to the Google screen). That user must
    //     see nothing; no user should ever see GoTrue's raw text.
    const urlParams = new URLSearchParams(window.location.search);
    const urlError = urlParams.get("error");
    const urlErrorCode = urlParams.get("error_code");
    if (urlError && urlErrorCode) {
      const description =
        urlParams.get("error_description")?.replace(/\+/g, " ") || "";
      // Scrub the raw error off the address bar first so a reload / Back /
      // bfcache restore can't replay it.
      window.history.replaceState({}, "", window.location.pathname);

      if (urlErrorCode === "otp_expired") {
        // Expired email-verification link — unchanged behavior.
        setAuthError(
          "Your verification link has expired. Please sign up again to get a new one.",
        );
        setAuthErrorMode("verification");
      } else {
        // OAuth / login-handshake failure (incl. "OAuth state not found or
        // expired"). Decide off the REAL session, not the URL: a signed-in
        // user is fine — suppress entirely; otherwise show friendly recovery
        // copy and a fresh-Google-sign-in CTA — never GoTrue's raw message.
        const supabaseForAuthError = createClient();
        void supabaseForAuthError.auth
          .getUser()
          .then(({ data }) => {
            const signedIn = Boolean(data.user);
            console.log("[auth] homepage auth-error handling", {
              route: "/",
              callbackRoute: "/auth/callback",
              provider: "google",
              errorCode: urlErrorCode,
              sessionStatus: signedIn ? "active" : "none",
              stateValidationResult: "failed",
              failureReason: description || urlError,
            });
            if (signedIn) {
              // Returning, already-authenticated user — the reported bug.
              // Leave them on the homepage with no error banner at all.
              return;
            }
            setAuthError("Your session expired. Sign in again to continue.");
            setAuthErrorMode("session");
          })
          .catch((err) => {
            // Never surface GoTrue's raw text even if the session check
            // fails — fall back to the same friendly recovery copy.
            console.warn("[auth] auth-error session check failed", err);
            setAuthError("Your session expired. Sign in again to continue.");
            setAuthErrorMode("session");
          });
      }
    }

    // Capture ?ref= parameter for referral attribution
    const refParam = urlParams.get("ref");
    if (refParam && !safeGetItem("panda_ref")) {
      safeSetItem("panda_ref", refParam);
      document.cookie = `panda_ref=${refParam}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax`;
    }

    // Auto-open signup modal when navigated here with ?signup=1
    if (urlParams.get("signup") === "1") {
      const nextParam = urlParams.get("next");
      setSignupNext(nextParam && nextParam.startsWith("/") ? nextParam : "/");
      setSignupOpen(true);
      urlParams.delete("signup");
      urlParams.delete("next");
      const remaining = urlParams.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (remaining ? `?${remaining}` : ""),
      );
    }

    // Auto-open login modal when navigated here with ?login=1 (e.g. from
    // the redesigned /forgot-password "Continue to log in" link).
    if (urlParams.get("login") === "1") {
      const nextParam = urlParams.get("next");
      setLoginNext(nextParam && nextParam.startsWith("/") ? nextParam : "/");
      setLoginOpen(true);
      urlParams.delete("login");
      urlParams.delete("next");
      const remaining = urlParams.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (remaining ? `?${remaining}` : ""),
      );
    }

    // A failed OAuth round-trip (Google / Facebook / LinkedIn) comes back from
    // /auth/callback as ?error=oauth (the provider returned an error) or
    // ?error=auth_callback (the PKCE code exchange failed). Neither carries an
    // error_code, so the GoTrue-state handler above skips them — without this
    // the login modal just reopens silently and the user never learns their
    // sign-in failed. Scrub the param, then (for a genuinely signed-out user)
    // reopen login with a friendly, provider-agnostic message — never the
    // provider's raw error text.
    const oauthFailure = urlParams.get("error");
    if (oauthFailure === "oauth" || oauthFailure === "auth_callback") {
      urlParams.delete("error");
      urlParams.delete("oauth_detail");
      const remaining = urlParams.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (remaining ? `?${remaining}` : ""),
      );
      const supabaseForOauthError = createClient();
      void supabaseForOauthError.auth
        .getUser()
        .then(({ data }) => {
          // Already signed in (e.g. a stale handshake replayed in another
          // tab) — stay quiet rather than flash a misleading error.
          if (data.user) return;
          setLoginInitialError(
            "We couldn't complete your sign-in. Please try again.",
          );
          setLoginOpen(true);
        })
        .catch(() => {
          setLoginInitialError(
            "We couldn't complete your sign-in. Please try again.",
          );
          setLoginOpen(true);
        });
    }

    // Routed here with ?focus=prompt (e.g. the "Get started for free" CTA
    // on /pricing): drop the visitor straight into the prompt box so they
    // can start building. Strip the param first so a remount / back nav
    // doesn't refocus and yank the page back to the top.
    if (urlParams.get("focus") === "prompt") {
      urlParams.delete("focus");
      const remaining = urlParams.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (remaining ? `?${remaining}` : ""),
      );
      // Defer a beat so the prompt input is laid out before we scroll/focus.
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        focusHeroPrompt();
      }, 100);
    }

    const supabase = createClient();
    supabase.auth.getUser().then(({ data: userData }) => {
      if (userData.user) {
        setUser({
          email: userData.user.email,
          userMetadata: userData.user.user_metadata ?? {},
        });
        identifyUser(userData.user.id, buildPostHogPersonProperties(userData.user));
        setAuthUserId(userData.user.id);
        if (userData.user.created_at) {
          setSignupAtMs(new Date(userData.user.created_at).getTime());
        }

        fetch("/api/student/verify")
          .then((r) => r.json())
          .then((data) => {
            if (data?.isStudent) {
              setIsStudent(true);
              setUseStudentDiscount(true);
            }
          })
          .catch(() => {});
      }

      const params = new URLSearchParams(window.location.search);
      const urlPrompt = params.get("prompt") || "";
      // Durable pending project id carried through the auth chain (see
      // resolvePendingProjectId). Reused by the resume branches below so a
      // signup/verify/magic-link handoff resolves to ONE project.
      const urlPid = params.get("pid");
      const rawTemplate = params.get("templateId");
      const urlTemplate =
        rawTemplate && isTemplateId(rawTemplate) ? rawTemplate : "base-nextjs";
      if (isTemplateId(urlTemplate)) {
        setSelectedTemplate(urlTemplate);
        setTemplateTouched(true);
      }

      fetch("/api/projects")
        .then((r) => r.json())
        .then((data) => {
          if (Array.isArray(data)) setProjects(data.slice(0, 6));
        })
        .catch(() => {})
        .finally(() => setProjectsLoaded(true));

      // Fetch the user's current plan + credit state. Plan is used for
      // the pricing section's "Current plan" indicator (runs everywhere).
      // Lifetime-counter enforcement stays gated to non-localhost so dev
      // builds don't get blocked by the limit modal.
      if (userData.user) {
        // NOTE: we deliberately do NOT pre-flag the project limit here.
        // Aaron's product rule — never block before the user tries to use
        // more. We let them type their idea and submit; the create request
        // is the gate, and the upgrade wall only appears AFTER the server
        // returns project_limit_reached (see handleGenerate below).
        void refreshPlan();

        // Active Growth plan for the pricing section's Grow-mode
        // "Current plan" lock.
        fetch("/api/growth/credits")
          .then((r) => (r.ok ? r.json() : null))
          .then((snap) => {
            if (snap && !snap.error && snap.plan) setGrowthPlan(String(snap.plan));
          })
          .catch(() => {});
      }

      // Fetch recent prompts for logged in users
      if (userData.user) {
        fetch("/api/prompts/recent")
          .then((r) => r.json())
          .then((data) => {
            if (Array.isArray(data)) setRecentPrompts(data);
          })
          .catch(() => {});
      }

      // Only the legacy community section still needs this. The v15 "Built with
      // Panda" cards now arrive as props from the server, so on the live
      // homepage this request — and the auth round-trip it was chained behind —
      // is gone entirely.
      if (SHOW_LEGACY_HOME_SECTIONS) {
        setIsLoadingCommunity(true);
        fetch("/api/community-projects?limit=6")
          .then((r) => r.json())
          .then((data) => {
            if (data.projects && Array.isArray(data.projects)) {
              setCommunityProjects(data.projects);
            }
          })
          .catch(() => {})
          .finally(() => setIsLoadingCommunity(false));
      }

      // Fast: fetch screenshot URLs first so cards show real images instantly
      fetch("/api/templates/screenshots")
        .then((r) => r.json())
        .then((data) => {
          if (data.screenshots?.length > 0) {
            const map: Record<string, string> = {};
            for (const s of data.screenshots) {
              if (s.slug && s.screenshot_url) map[s.slug] = s.screenshot_url;
            }
            setScreenshotMap(map);
          }
        })
        .catch(() => {})
        .finally(() => setIsLoadingTemplates(false));

      // Fetch DB templates (falls back to hardcoded if empty)
      fetch("/api/templates")
        .then((r) => r.json())
        .then((data) => {
          if (data.templates?.length > 0) setDbTemplates(data.templates);
        })
        .catch(() => {});

      // Resume a clone the visitor asked for before signing in — or before the
      // email-verification gate interrupted them. Covers the full-page OAuth
      // redirect and the gate modal's post-verification reload, where this
      // mount is a brand-new document and the SIGNED_IN listener above never
      // sees the transition. Checked before the prompt paths: a clone target is
      // the more specific intent, and both share homeResumeFiredRef so only one
      // of them can ever fire.
      if (userData.user && consumeCloneIntentOnAuth()) return;

      if (urlPrompt) {
        // The OAuth-return path (Google/Facebook/LinkedIn) lands here with
        // `?prompt=...` once the callback has set session cookies. The
        // in-memory pending-attachments holder is gone — full-page redirect
        // wiped it — so pull the files back from IDB before the push so
        // /project/[id] can upload them. Only for authed users; an
        // unauthed direct visit to /?prompt=foo shouldn't pick up an
        // unrelated user's old IDB entry.
        if (homeResumeFiredRef.current) return;
        homeResumeFiredRef.current = true;
        // One-shot replay: consume the prompt now — drop the stash AND strip
        // ?prompt= from this URL — so a remount (back button, bfcache
        // restore, or a second auth event during sign-up) can't replay the
        // same prompt into a second project and deduct a second credit. The
        // build is driven by the project-page URL we push below, not by this
        // stash/param, so removing them here is safe.
        //
        // Read the stashed pending id BEFORE clearing: it's the fallback for the
        // stable project id when the URL `pid` is missing (e.g. a prompt with a
        // raw `&` truncated the passthrough). Same-browser tabs share this
        // localStorage, so the magic-link tab still converges on ONE project.
        const stashedForResume = readHomePromptStash();
        clearHomePromptStash();
        try {
          const cleaned = new URL(window.location.href);
          cleaned.searchParams.delete("prompt");
          cleaned.searchParams.delete("templateId");
          cleaned.searchParams.delete("pid");
          window.history.replaceState({}, "", cleaned.toString());
        } catch {
          // ignore — stripping the URL is a best-effort dedupe guard
        }
        void (async () => {
          if (userData.user) {
            const idbFiles = await restorePendingAttachments();
            if (idbFiles.length > 0) setPendingAttachments(idbFiles);
          }
          // Reuse the pending id from the auth chain so a second tab/callback
          // resolves to THIS project instead of minting a duplicate. URL `pid`
          // first, then the stashed id, then a fresh uuid for legacy links.
          const projectId = resolvePendingProjectId(
            urlPid || stashedForResume?.projectId,
          );
          const encoded = encodeURIComponent(urlPrompt);
          const nextTemplate = encodeURIComponent(urlTemplate);
          // Best-effort: the template also travels in the URL below, so a full
          // store costs nothing here. It must never stop the redirect.
          safeSetItem(`panda:project-template:${projectId}`, urlTemplate);
          // Resume is always the home "idea" path (stash/urlPrompt are only set
          // by handleGenerate), so the build-funnel source is home_idea.
          trackPreSignupIntentRestored("prompt");
          trackAuthReturnedToProjectAction("build");
          router.push(
            `/project/${projectId}?prompt=${encoded}&templateId=${nextTemplate}&source=home_idea`,
          );
        })();
        return;
      }

      // Recover from a previous handleGenerate that didn't make it to the
      // project page (modal dismissed, session bounce, etc). If the user
      // is logged in now, drive a fresh redirect with the stashed prompt;
      // otherwise pre-fill the input so they can resubmit by hand. The
      // project page clears the stash once a build actually starts.
      const stashed = readHomePromptStash();
      if (stashed) {
        if (userData.user) {
          // Same restore as the urlPrompt branch above — covers the
          // stash-only path (modal dismissed → user signs in later via
          // any route → home-mount stash recovery kicks in).
          if (homeResumeFiredRef.current) return;
          homeResumeFiredRef.current = true;
          // One-shot replay — consume the stash now so a remount can't fire a
          // second build for the same prompt (the double-credit bug). The
          // logged-out "pre-fill the box" branch below keeps the stash on
          // purpose; only this build-starting branch clears it.
          clearHomePromptStash();
          void (async () => {
            const idbFiles = await restorePendingAttachments();
            if (idbFiles.length > 0) setPendingAttachments(idbFiles);
            // Reuse the pending id stashed at submit so this stash-only resume
            // and any URL/OAuth resume (other tab) land on the SAME project.
            const projectId = resolvePendingProjectId(stashed.projectId);
            const encoded = encodeURIComponent(stashed.prompt);
            const tpl =
              stashed.templateId && isTemplateId(stashed.templateId)
                ? stashed.templateId
                : "base-nextjs";
            const nextTemplate = encodeURIComponent(tpl);
            safeSetItem(`panda:project-template:${projectId}`, tpl);
            // Home idea-path resume — see source note above.
            router.push(
              `/project/${projectId}?prompt=${encoded}&templateId=${nextTemplate}&source=home_idea`,
            );
          })();
          return;
        }
        // Only into an empty box. This effect resolves asynchronously (after
        // auth.getUser()), by which point the draft restore may already have
        // put back text the user typed *after* this stash was written — e.g.
        // they submitted, dismissed the signup modal, kept editing, refreshed.
        // The draft is the newer of the two, so it wins.
        setPrompt((current) => (current.trim() ? current : stashed.prompt));
        if (stashed.templateId && isTemplateId(stashed.templateId)) {
          setSelectedTemplate(stashed.templateId);
          setTemplateTouched(true);
        }
      }
    });
  }, [router]);

  // Other pages (protected routes, project editor, etc.) redirect here with
  // ?login=1 or ?signup=1 when an unauthenticated user hits something that
  // requires auth. Auto-open the matching modal so the user never sees the
  // legacy /login page.
  useEffect(() => {
    const url = new URL(window.location.href);
    const wantsLogin = url.searchParams.get("login");
    const wantsSignup = url.searchParams.get("signup");
    if (!wantsLogin && !wantsSignup) return;
    const rawNext = url.searchParams.get("next") || "/";
    const nextPath =
      rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/";
    if (wantsSignup) {
      setSignupNext(nextPath);
      setSignupOpen(true);
    } else {
      setLoginNext(nextPath);
      setLoginOpen(true);
    }
    url.searchParams.delete("login");
    url.searchParams.delete("signup");
    url.searchParams.delete("next");
    window.history.replaceState({}, "", url.toString());
  }, []);

  // Reopen the auth modal on return from a 3rd-party OAuth provider
  // (Google/Facebook). Handles both real loads and bfcache restores.
  useAuthModalSync(loginOpen, signupOpen, setLoginOpen, setSignupOpen);

  // PANDA-1523: post-signup personalization wizard (theme / role / company
  // size) for a freshly authenticated user who has NOTHING to resume — no
  // stashed prompt, no clone intent, no ?prompt= in the URL, no resume already
  // fired.
  //
  // This is now the ONLY place the wizard opens. It used to have a sibling on
  // /project/[id] that gated the first build, which is what QA reported as
  // "Onboarding quick setup modal appears during active build and blocks
  // workspace" — the builder is never an entry point any more (see
  // lib/onboarding/entry-point.ts). A user who signs up WITH a prompt therefore
  // goes straight into their build and is offered the wizard on their next idle
  // home load instead, which is the ticket's "defer it until the user is idle
  // and not inside an active build workspace". Nothing is persisted in the
  // meantime, so the offer survives.
  // Deliberately its OWN effect, independent of the resume mount-effect
  // above: it must never interfere with the one-shot resume guards that
  // stop duplicate builds / double credit charges.
  const [showPersonalization, setShowPersonalization] = useState(false);
  const [personalizationSubmitting, setPersonalizationSubmitting] =
    useState(false);
  const { setTheme: setAppTheme } = useTheme();

  // ── One-time-ness (QA: "onboarding questions reappear after dismiss/skip/
  //    refresh") ────────────────────────────────────────────────────────────
  //
  // What makes the wizard one-time is recording that it was shown the moment it
  // goes on screen — not when the user finishes it. Until migration 166 the only
  // write was the submit POST, so every exit that isn't a button press (refresh,
  // tab close, click-away, a submit whose POST failed) persisted nothing and the
  // wizard came back on the next load, forever.
  //
  // Migration 182 is why it is one-time NOW. 166 put the write at display time,
  // which was right, but the client still DECIDED in one request (GET says
  // `shown: false`) and RECORDED in another (PATCH). Between them sat the entry
  // checks and a whole verification-status round trip, and when the PATCH did
  // not land the only surviving record was a localStorage marker — which is
  // device-local, so the next browser asked again. Every layer added after 166
  // (marker, retries, next-load re-send, unload beacon) was covering that gap
  // and none of them could make the suppression cross-device.
  //
  // claimOnboardingOffer collapses the two into one atomic server call: the
  // wizard is only ever opened by a caller the server has ALREADY recorded as
  // having been shown it. See lib/onboarding/shown-marker.ts and migration 182.
  //
  // The user id comes from the status GET rather than a second auth round trip;
  // it scopes the local marker so a shared browser can't suppress the wizard for
  // whoever signs in next.
  const personalizationUserIdRef = useRef<string | null>(null);

  // Read inside `openPersonalization`, which is a `useCallback` that must not
  // re-create itself every time the user object changes — a new identity there
  // would re-run nothing today, but it is the kind of dependency that quietly
  // turns into a second offer later. A ref keeps the callback stable.
  const userEmailRef = useRef<string | null>(null);
  useEffect(() => {
    userEmailRef.current = user?.email ?? null;
  }, [user?.email]);

  // Once per page life. Both the mount effect and the SIGNED_IN handler call
  // maybeShowPersonalizationWizard, and each issues its own GET. The claim would
  // answer `owed: false` to the second one regardless — that is what makes this
  // safe rather than merely tidy — but there is no reason to ask twice.
  const personalizationOfferedRef = useRef(false);

  // Claiming the offer is irreversible from the client, so it must never happen
  // for a wizard that will not actually be painted. Navigating away while the
  // onboarding GET is still in flight would otherwise claim the user out of
  // onboarding permanently while setShowPersonalization no-ops on a dead tree.
  // Checked twice in openPersonalization — once before the claim and once after,
  // since the claim itself is an await.
  const personalizationMountedRef = useRef(true);
  useEffect(() => {
    personalizationMountedRef.current = true;
    return () => {
      personalizationMountedRef.current = false;
    };
  }, []);

  // ── This surface is the ONLY onboarding entry point ─────────────────────
  //
  // QA: "Onboarding quick setup modal appears during active build and blocks
  // workspace". The builder no longer opens the wizard at all (see
  // lib/onboarding/entry-point.ts and launchBuildFlow in
  // app/project/[id]/page.tsx), so what is left is making sure THIS surface
  // does not open it on a load that only exists to hand off into a build.
  //
  // Three ways that happens, and all three end in the builder a moment later:
  //   • `?prompt=` in the URL — the home prompt survived signup in the query.
  //   • a stashed home prompt — it survived signup in storage instead.
  //   • a parked clone intent — same, for "clone this template".
  // Plus `homeResumeFiredRef`, which is the one the stash checks alone MISS:
  // the resume effect CONSUMES the stash on mount, and this function only runs
  // after `supabase.auth.getUser()` resolves. So a stash read here can come back
  // empty while a project is already being created and navigated to, and the
  // wizard would flash over that — and stamp itself shown on the way out.
  const homeOnboardingEntryAllowed = useCallback(() => {
    if (typeof window === "undefined") return false;
    return canOpenOnboarding(
      homeOnboardingEntryContext({
        urlPrompt: Boolean(
          new URL(window.location.href).searchParams.get("prompt"),
        ),
        stashedPrompt: Boolean(readHomePromptStash()),
        cloneIntent: Boolean(readCloneIntent()),
        resumeFired: homeResumeFiredRef.current,
      }),
    );
  }, []);

  const openPersonalization = useCallback(async () => {
    if (personalizationOfferedRef.current) return;
    if (!personalizationMountedRef.current) return;
    // Re-checked at the last instant, not only before the status GET: the
    // network round trip is exactly the window in which a resume fires. Cheap
    // (two storage reads) and the failure it prevents is permanent — claiming
    // the offer is irreversible from the client.
    if (!homeOnboardingEntryAllowed()) return;
    // Latch BEFORE awaiting the claim, so a second caller (the SIGNED_IN handler
    // racing the mount effect) cannot slip into the same window and issue a
    // second claim. The server would answer `owed: false` to the loser anyway —
    // that is the guarantee 182 buys — but there is no reason to ask twice.
    personalizationOfferedRef.current = true;

    // Decide and record in one call. Unlike the old GET-then-PATCH, there is no
    // interval here in which the wizard is on screen but the server does not
    // know: `owed` is only ever true for the caller whose request wrote the
    // stamp. So a failed request costs this load's offer, never the record of it.
    const claim = await claimOnboardingOffer(personalizationUserIdRef.current);
    if (!claim.owed) return;
    // The claim is an await, so re-check both guards on the other side of it.
    // The offer is spent either way — that is the one-way door — so this is not
    // protecting the claim, it is protecting the SCREEN: a resume that fired
    // during the round trip means a build workspace is a moment away, and
    // painting a modal over it is the separate QA report this wizard already
    // has ("appears during active build and blocks workspace"). Losing one
    // optional survey in that race is the cheaper of the two failures.
    if (!personalizationMountedRef.current) return;
    if (!homeOnboardingEntryAllowed()) return;

    trackOnboardingShown({ surface: "home", email: userEmailRef.current });
    setShowPersonalization(true);
  }, [homeOnboardingEntryAllowed]);

  async function maybeShowPersonalizationWizard() {
    // Bail before the network: a load that is on its way into a build should
    // not even ask the server whether onboarding is owed.
    if (!homeOnboardingEntryAllowed()) return;
    try {
      // no-store: a cached `shown: false` would re-open a wizard the user has
      // already dismissed, which is the whole bug. Matches the sibling
      // verification-status call below.
      const r = await fetch("/api/user/onboarding", { cache: "no-store" });
      if (!r.ok) return;
      const data = await r.json();
      personalizationUserIdRef.current =
        typeof data.userId === "string" ? data.userId : null;
      // If this browser showed the wizard but never got the server to agree
      // (offline, a 5xx, the tab closed mid-retry), tell it now. This is what
      // turns "suppressed on this device" into the cross-device, cross-session
      // suppression the ticket asks for. No-ops unless a stamp is outstanding.
      void flushPendingOnboardingShown(
        personalizationUserIdRef.current,
        Boolean(data.shown),
      );
      // Re-apply the stored theme. Without this the answer only ever reached
      // next-themes' localStorage at submit time, so a new device, a cleared
      // cache or a private window silently ignored a preference the user had
      // explicitly given — the stored column was write-only.
      if (data.theme === "light" || data.theme === "dark") {
        setAppTheme(data.theme);
      }
      // Segment the PERSON from the stored answers, not just at wizard
      // completion — otherwise everyone who onboarded before segmentation
      // shipped stays unsegmented forever and the cohort comparison the ticket
      // asks for only covers new signups. Idempotent.
      identifyOnboardingSegment({
        theme: data.theme ?? null,
        role: data.role ?? null,
        companySize: data.companySize ?? null,
      });
      // A cheap early-out, NOT the decision. The two server stamps plus this
      // browser's local marker, via the shared rule — see onboardingWizardIsOwed.
      // Saves a claim round trip on the overwhelming majority of loads, where
      // the user was asked long ago. It may only ever say "don't"; saying "do"
      // just means we go on to ask the server properly below.
      if (!onboardingWizardIsOwed(data, personalizationUserIdRef.current)) return;
      // …but not underneath the email-verification gate, which is a
      // non-dismissible z-[9999] body portal. Claiming there would burn the
      // one offer on a wizard the user cannot see, and the reload that follows
      // verification would then suppress it forever. Defer to that reload.
      //
      // This MUST stay above the claim. Claiming is irreversible.
      if (await isEmailVerificationGatePending()) return;
      await openPersonalization();
    } catch {
      // best-effort — a failed check just skips the wizard this load
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const supabase = createClient();
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();
      if (cancelled || !authUser) return;
      await maybeShowPersonalizationWizard();
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // PANDA-1618: offer to pick a verification-interrupted clone back up.
  //
  // The happy path is automatic — the gate modal reloads the page the moment
  // it sees the account verify, and consumeCloneIntentOnAuth replays the
  // stashed target on that reload. This covers the leftovers: the user
  // verified on their phone, closed the desktop tab, and came back later. The
  // intent is still stashed but no `just-verified` signal exists, so we ask
  // instead of silently spending a build credit.
  //
  // Deliberately its OWN effect, like the personalization wizard above, so it
  // can never interfere with the one-shot resume guards that stop duplicate
  // builds. It only touches the network when such an intent actually exists.
  const verificationResumePromptedRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (verificationResumePromptedRef.current) return;
      const parked = readCloneIntent();
      if (!parked?.pendingVerification) return;
      // Peek, don't consume: the auto-resume path owns the just-verified
      // reload and needs the flag intact.
      if (readJustVerifiedFlag(false)) return;

      let verified = false;
      try {
        const res = await fetch("/api/auth/verification-status", {
          cache: "no-store",
        });
        if (!res.ok) return;
        const body = await res.json().catch(() => null);
        verified = Boolean(body?.verified);
      } catch {
        // Transient — the intent stays stashed for the next load.
        return;
      }
      // Still unverified: the gate modal is up (or about to be). Leave the
      // intent alone; verifying triggers the automatic path.
      if (!verified || cancelled) return;

      // Re-read: the auto path may have consumed the intent while we were
      // waiting on the network.
      const intent = readCloneIntent();
      if (!intent?.pendingVerification || homeResumeFiredRef.current) return;
      verificationResumePromptedRef.current = true;

      customToast.success("Email verified — your clone is ready to pick up.", {
        action: {
          label: "Resume",
          onClick: () => {
            if (homeResumeFiredRef.current) return;
            const current = readCloneIntent();
            if (!current) return;
            homeResumeFiredRef.current = true;
            // One-shot: consume before cloning so a double-tap can't fork
            // twice and charge two credits.
            clearCloneIntent();
            markVerificationResumeFired();
            trackHomepageCloneVerificationResumed(
              cloneIntentTarget(current),
              "manual",
            );
            runCloneIntent(current);
          },
        },
      });
    })();
    return () => {
      cancelled = true;
    };
    // Mount-once by design: re-running would re-offer a clone the user has
    // already been asked about. runCloneIntent reads its target from storage
    // at click time, so the stale closure carries no stale state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function submitPersonalization(answers: PersonalizationAnswers | null) {
    setPersonalizationSubmitting(true);

    // Close the wizard immediately rather than when the network says so. The
    // offer is already claimed and durable, so nothing about whether it comes
    // back depends on this request — and holding a spinner over a slow POST
    // only makes the user wait to be told something we already know.
    setShowPersonalization(false);

    // The ANSWERS still depend on it, though, and this used to be one
    // unverified request whose failure was swallowed while the modal closed
    // anyway: the three questions the user just answered were simply gone. Same
    // bounded ladder the stamp uses — one implementation, in shown-marker.
    //
    // keepalive so a navigation right after Continue/Skip cannot cancel it.
    void sendWithRetries(async () => {
      try {
        const res = await fetch("/api/user/onboarding", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(answers ?? {}),
          keepalive: true,
        });
        if (!res.ok) console.warn("[onboarding] save failed:", res.status);
        return res.ok;
      } catch {
        return false;
      }
    }).finally(() => {
      setPersonalizationSubmitting(false);
    });

    if (answers?.theme) setAppTheme(answers.theme);
  }

  // Keep the page's user-dependent state in sync with later auth changes
  // (e.g. signing in via the LoginModal without a navigation, or signing
  // out from a sub-component). The mount effect above seeds initial state;
  // this listener picks up SIGNED_IN / SIGNED_OUT events afterwards.
  useEffect(() => {
    const supabase = createClient();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        clearPendingAuth();
        const u = session.user;
        setUser({
          email: u.email,
          userMetadata: u.user_metadata ?? {},
        });
        identifyUser(u.id, buildPostHogPersonProperties(u));

        // PANDA-1523: covers signing in via the LoginModal without a full
        // navigation (the mount effect above only runs once, on first
        // load). Bails out on its own if a resume is pending.
        void maybeShowPersonalizationWizard();

        fetch("/api/student/verify")
          .then((r) => r.json())
          .then((data) => {
            if (data?.isStudent) {
              setIsStudent(true);
              setUseStudentDiscount(true);
            }
          })
          .catch(() => {});

        void refreshPlan();

        fetch("/api/growth/credits")
          .then((r) => (r.ok ? r.json() : null))
          .then((snap) => {
            if (snap && !snap.error && snap.plan) setGrowthPlan(String(snap.plan));
          })
          .catch(() => {});

        fetch("/api/projects")
          .then((r) => r.json())
          .then((data) => {
            if (Array.isArray(data)) setProjects(data.slice(0, 6));
          })
          .catch(() => {});

        fetch("/api/prompts/recent")
          .then((r) => r.json())
          .then((data) => {
            if (Array.isArray(data)) setRecentPrompts(data);
          })
          .catch(() => {});

        // If a prompt is stashed from a prior handleGenerate that didn't
        // make it through (modal dismissed, session bounce), drive the
        // redirect now that the user is signed in. Defer one tick so the
        // LoginModal's own router.push(next) wins when there's an
        // explicit next path — we only want to fire when nothing else is
        // already navigating.
        setTimeout(() => {
          if (typeof window === "undefined") return;
          const url = new URL(window.location.href);
          if (url.pathname !== "/") {
            // A stash that never gets replayed is a silently dropped prompt.
            // Naming the reason is what makes "did intent restoration or
            // project routing fail?" answerable at all.
            if (readHomePromptStash()) {
              trackPreSignupIntentLost({
                reason: "wrong_path",
                stage: "routing",
              });
            }
            return;
          }
          // Skip `login`/`signup`/`next` — those mean a modal is still
          // owning the navigation. Do NOT skip on `prompt`: that's the
          // exact case we want to handle (email modal just did
          // router.replace("/?prompt=…") and we need to drive the build).
          if (
            url.searchParams.has("login") ||
            url.searchParams.has("signup") ||
            url.searchParams.has("next")
          ) {
            if (readHomePromptStash()) {
              trackPreSignupIntentLost({
                reason: "modal_owns_navigation",
                stage: "routing",
              });
            }
            return;
          }
          if (homeResumeFiredRef.current) return;

          // A stashed clone target wins over a stashed prompt: the visitor
          // asked for a specific build, not for a prompt to be written from
          // scratch. Shares homeResumeFiredRef with the prompt path so exactly
          // one navigation (and one credit) can ever fire.
          if (consumeCloneIntentOnAuth()) return;

          const stash = readHomePromptStash();
          if (!stash) {
            // A `prompt` on the URL with nothing in storage is the iOS Private
            // Browsing / TTL-expiry case: the user's typed intent is gone and
            // nothing downstream will ever notice.
            if (url.searchParams.has("prompt")) {
              trackPreSignupIntentLost({
                reason: "no_stash",
                stage: "restore",
              });
            }
            return;
          }
          homeResumeFiredRef.current = true;
          // One-shot replay — consume the stash now so a remount can't fire a
          // second build for the same prompt (the double-credit bug).
          clearHomePromptStash();
          void (async () => {
            // Same OAuth-survival restore as the mount effect — the
            // module-level holder may still be populated from the same
            // SPA session, but if the SignInModal triggered a hard
            // navigation somewhere in the chain (or the user's session
            // came back via bfcache) the holder is empty and IDB is the
            // only source.
            const idbFiles = await restorePendingAttachments();
            if (idbFiles.length > 0) setPendingAttachments(idbFiles);
            // Reuse the pending id (URL `pid` first, else the stashed one) so
            // the original signup tab and the magic-link tab converge on ONE
            // project rather than each starting its own build.
            const projectId = resolvePendingProjectId(
              url.searchParams.get("pid") || stash.projectId,
            );
            const encoded = encodeURIComponent(stash.prompt);
            const tpl =
              stash.templateId && isTemplateId(stash.templateId)
                ? stash.templateId
                : "base-nextjs";
            const nextTemplate = encodeURIComponent(tpl);
            try {
              safeSetItem(`panda:project-template:${projectId}`, tpl);
            } catch {
              // ignore — non-blocking optimisation
            }
            // The email/password signup resumes through THIS listener, not the
            // mount effect, so without these two the common continuation path
            // emitted nothing at all and a stalled hand-off had to be inferred
            // from missing events.
            trackPreSignupIntentRestored("prompt");
            trackAuthReturnedToProjectAction("build");
            // Home idea-path resume — see source note above.
            router.push(
              `/project/${projectId}?prompt=${encoded}&templateId=${nextTemplate}&source=home_idea`,
            );
          })();
        }, 200);
      } else if (event === "SIGNED_OUT") {
        setUser(null);
        setIsStudent(false);
        setUseStudentDiscount(false);
        setUserPlan("free");
        setGrowthPlan("trial");
        setProjects([]);
        setRecentPrompts([]);
        clearHomePromptStash();
        clearCloneIntent();
        // Don't leave the previous session's typing on a shared machine.
        clearHomePromptDraft();
        setPrompt("");
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Normalises whatever the user typed into the URL field into a real
   * absolute URL. Accepts "example.com", "www.example.com",
   * "example.com/about", and any "http(s)://…" form. Returns null if
   * the input cannot be coerced into a valid http(s) URL — the caller
   * surfaces the inline error in that case.
   */
  /**
   * Validates a user-typed website URL for the growth scraper. The
   * scraper hits the URL from our backend, so the URL has to be
   * publicly resolvable — localhost / private IPs / link-local
   * addresses can't be scraped and must be rejected up front.
   *
   * Rules:
   *  - empty input             → { ok: false, error: null } (UI treats
   *                               this as "no URL yet" rather than an
   *                               error message — only the submit
   *                               handler converts a missing URL into
   *                               a "required" error when needed).
   *  - http://example.com      → upgraded to https.
   *  - https?://localhost      → rejected.
   *  - https?://10.0.0.1 (etc) → rejected.
   *  - missing TLD             → rejected.
   *  - parseable + public host → { ok: true, url: "<normalised>" }.
   */
  function normaliseWebsiteUrl(
    raw: string,
  ): { ok: true; url: string } | { ok: false; error: string | null } {
    const trimmed = raw.trim();
    if (!trimmed) return { ok: false, error: null };

    // Strip a leading protocol so we can re-add a forced https below.
    // Anything other than http(s) at this point is rejected.
    let body = trimmed;
    const protoMatch = body.match(/^([a-z][a-z0-9+\-.]*):\/\//i);
    if (protoMatch) {
      const proto = protoMatch[1].toLowerCase();
      if (proto !== "http" && proto !== "https") {
        return {
          ok: false,
          error: "URL must start with https:// (or just paste the domain).",
        };
      }
      body = body.slice(protoMatch[0].length);
    }

    if (!body) {
      return { ok: false, error: "Enter a website like panda.new." };
    }

    let parsed: URL;
    try {
      parsed = new URL(`https://${body}`);
    } catch {
      return {
        ok: false,
        error: "That doesn't look like a valid website URL.",
      };
    }

    const host = parsed.hostname.toLowerCase();

    // Reject hosts our scraper can't reach from the public internet.
    if (
      host === "localhost" ||
      host.endsWith(".localhost") ||
      host === "127.0.0.1" ||
      host === "0.0.0.0" ||
      host === "::1"
    ) {
      return {
        ok: false,
        error: "Localhost URLs can't be scanned. Use a live, public URL.",
      };
    }

    // Reject RFC1918 / link-local / loopback ranges. We're not running
    // a full IP-CIDR validator here — just the common cases users
    // accidentally paste.
    if (
      /^10\./.test(host) ||
      /^192\.168\./.test(host) ||
      /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(host) ||
      /^169\.254\./.test(host)
    ) {
      return {
        ok: false,
        error: "Private network addresses can't be scanned. Use a live URL.",
      };
    }

    // Need a real TLD. `example` alone is no good.
    const labels = host.split(".");
    if (labels.length < 2 || labels.some((label) => label.length === 0)) {
      return {
        ok: false,
        error: "Include a full domain — e.g. panda.new",
      };
    }
    const tld = labels[labels.length - 1];
    if (tld.length < 2 || !/^[a-z]{2,}$/.test(tld)) {
      return {
        ok: false,
        error: "That domain doesn't look right — check the spelling.",
      };
    }

    // Force https. The original input may have been http://; we always
    // hand the scraper an https URL so we don't issue a non-TLS request
    // from the backend (sites that 301 to https handle themselves).
    parsed.protocol = "https:";

    return { ok: true, url: parsed.toString() };
  }

  /**
   * "I need more revenue" path. Creates a lightweight project record
   * (no build, no template — just a row to anchor the growth canvas
   * to) and lands the user on /project/<id>?mode=growth&external_url=…
   * The project page reads `external_url`, runs the same has-card gate
   * the in-canvas toggle uses, and — when the user has a card on file
   * — auto-fires `/api/growth/onboarding/derive` with
   * entryPath: "external_url" so ICP / Offer kick off without any
   * extra clicks. Without a card, the trial-card popup fires instead.
   */
  async function handleGenerateGrowth() {
    setBusinessUrlError(null);
    setBusinessUrlLimitReached(false);
    const result = normaliseWebsiteUrl(businessUrl);
    if (!result.ok) {
      setBusinessUrlError(
        result.error ?? "Enter a website URL — e.g. panda.new.",
      );
      return;
    }
    const normalised = result.url;
    // Persist the submitted URL to the /admin/prompts feed before any
    // validation/signup gates, so even abandoned-funnel attempts are
    // captured for user-testing review.
    console.log("[prompts-log] firing home_url", { url: normalised });
    fetch("/api/prompts/log", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt: normalised, source: "home_url" }),
    })
      .then(async (r) =>
        console.log("[prompts-log] home_url response", r.status, await r.text().catch(() => "")),
      )
      .catch((e) => console.warn("[prompts-log] home_url fetch failed", e));

    // PostHog onboarding funnel: business URL submitted ("existing_business"
    // path). Fires before the signup gate so abandoned attempts count. Domain
    // is parsed defensively — `normalised` is already a validated URL.
    let submittedDomain = "unknown";
    try {
      submittedDomain = new URL(normalised).hostname.replace(/^www\./, "");
    } catch {}
    trackHomepageBusinessUrlSubmitted(submittedDomain);

    if (!user) {
      openSignup(
        `/?mode=business&url=${encodeURIComponent(normalised)}`,
        "",
        "home_business_url",
      );
      return;
    }
    if (generatingGrowth) return;
    setGeneratingGrowth(true);
    // Pre-flight the URL before any DB writes — saves the user from
    // landing on a dead-end canvas, and saves us a project row + a
    // wasted derive scrape when the URL is a typo or the domain is
    // unreachable. `blocked` / `timeout` / `not_html` still proceed —
    // those mean the site exists but won't let us scrape it cleanly,
    // and the canvas falls through to URL-only context per Aaron's
    // "ignore and scrape anyway" call. Only `unreachable` and
    // `invalid_url` short-circuit here.
    try {
      const validateRes = await fetch("/api/growth/onboarding/validate-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: normalised }),
      });
      const validateBody = await validateRes
        .json()
        .catch(() => ({ ok: false, reason: "unreachable" as const }));
      if (
        !validateBody?.ok &&
        (validateBody?.reason === "unreachable" ||
          validateBody?.reason === "invalid_url")
      ) {
        setBusinessUrlError(
          "We couldn't reach that URL — the site either doesn't exist or it blocked us from scraping it. Double-check the address and try again.",
        );
        setGeneratingGrowth(false);
        return;
      }
    } catch {
      // Network blip on validation — surface the same generic error
      // we use for project creation failures rather than silently
      // proceeding to a likely-broken canvas.
      setBusinessUrlError("Network blip. Try again.");
      setGeneratingGrowth(false);
      return;
    }
    try {
      const projectId = uuidv4();
      let host = "site";
      try {
        // `hostname` already drops the protocol; the replace strips a
        // leading `www.` so the project reads "Growth for example.com".
        host = new URL(normalised).hostname.replace(/^www\./, "");
      } catch {}
      const growthTitle = `Growth for ${host}`;
      const res = await fetch("/api/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: projectId,
          title: growthTitle,
          // `prompt` powers later category detection / search; keep a
          // human-readable summary even though there's no build chain.
          prompt: `Grow revenue for ${normalised}`,
          // Stash the external URL + entry path inside the JSONB plan
          // so the project page can resume the growth flow on reload
          // even without the query param (e.g. user hits refresh). The
          // growth canvas (KpiHero) renders `plan.title`, so the name
          // has to live here too — the `title` column alone won't show.
          plan: {
            title: growthTitle,
            externalUrl: normalised,
            entryPath: "external_url",
          },
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 403 && body?.error === "project_limit_reached") {
          // Build the prefix client-side so we can title-case the plan
          // name and pull `limit` straight from the API payload — the
          // rendering layer tacks on "To create more, Upgrade" with an
          // inline link that opens the pricing modal.
          const planSlug =
            typeof body.plan === "string" && body.plan.length > 0
              ? body.plan
              : "current";
          const planName = planSlug.charAt(0).toUpperCase() + planSlug.slice(1);
          const prefix =
            typeof body.limit === "number"
              ? `Your ${planName} plan allows up to ${body.limit} ${body.limit === 1 ? "project" : "projects"}.`
              : `You've reached your ${planName} plan project limit.`;
          setBusinessUrlError(prefix);
          setBusinessUrlLimitReached(true);
        } else {
          setBusinessUrlError("Couldn't start your growth project. Try again.");
          setBusinessUrlLimitReached(false);
        }
        setGeneratingGrowth(false);
        return;
      }
      const params = new URLSearchParams({
        mode: "growth",
        external_url: normalised,
        source: "home_url",
      });
      // Hard nav: / ships COEP: unsafe-none (so the community-preview iframe
      // works), which makes this document non-cross-origin-isolated. A soft
      // router.push into /project/* would carry that false COI status across,
      // and the editor's WebContainer.boot() requires crossOriginIsolated=true.
      // assign() forces a fresh document load so /project/*'s COEP applies.
      window.location.assign(`/project/${projectId}?${params.toString()}`);
    } catch {
      setBusinessUrlError("Network blip. Try again.");
      setGeneratingGrowth(false);
    }
  }

  // Builds the "idea" flow. Accepts an explicit prompt so the visible
  // "Enhance prompt" flow can build the accepted text immediately without
  // waiting for the async `setPrompt` to flush. Defaults to the live state.
  async function handleGenerate(promptText: string = prompt) {
    if (isGenerating) return;
    const trimmedPrompt = promptText.trim();
    setIsGenerating(true);
    // navigated: only reset the spinner on early-return paths. On the
    // hard-nav path the page is about to unload, so we deliberately keep
    // the spinner until the new document takes over (prevents a brief
    // enabled-again flicker between assign() and unload).
    let navigated = false;
    try {
      // PostHog onboarding funnel: AI-builder prompt submitted ("idea" path).
      // Fires before ALL gates (project-limit, signup, redirect) so every
      // non-empty submission is captured — including users who have hit their
      // project limit. Mirrors the business-URL path, which tracks before its
      // gates. Guard on non-empty so blank submits aren't counted.
      if (trimmedPrompt) {
        trackHomepagePromptSubmitted(trimmedPrompt.length);
      }
      // No pre-emptive project-limit gate here. Every submission attempts a
      // real create below; if the user is over their plan's project cap, the
      // POST returns 403 project_limit_reached and we surface the upgrade wall
      // THEN — after capturing their intent, never before.
      if (!trimmedPrompt) return;
      const decidedTemplate = templateTouched
        ? selectedTemplate
        : selectTemplateForPrompt(trimmedPrompt);
      if (!templateTouched) {
        setSelectedTemplate(decidedTemplate);
      }
      const nextTemplate = encodeURIComponent(decidedTemplate);

      // Meta CAPI: PromptSubmitted for Panda's own ads. Fire-and-forget —
      // server endpoint reads session cookie (if any) + IP/UA/_fbp/_fbc from
      // request headers. Fires for both logged-in and anonymous visitors so
      // marketing captures the top-of-funnel intent signal regardless of
      // whether the visitor goes on to sign up.
      fetch("/api/meta/track-homepage-prompt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ templateId: decidedTemplate }),
        // keepalive: hard nav (window.location.assign below) triggers an unload
        // that would otherwise cancel this in-flight fetch and drop the CAPI
        // event. Same trick as lib/meta-pixel.ts:131's sendBeacon path.
        keepalive: true,
      }).catch(() => {});

      // Persist the raw prompt to /admin/prompts so the team can review
    // user-testing intent. Anon submissions are captured too.
    console.log("[prompts-log] firing home_idea", {
      promptLen: trimmedPrompt.length,
      template: decidedTemplate,
    });
    fetch("/api/prompts/log", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        prompt: trimmedPrompt,
        source: "home_idea",
        templateId: decidedTemplate,
      }),
    })
      .then(async (r) =>
        console.log("[prompts-log] home_idea response", r.status, await r.text().catch(() => "")),
      )
      .catch((e) => console.warn("[prompts-log] home_idea fetch failed", e));

    // Stash before *any* redirect — covers the URL-passthrough being
    // dropped (modal dismissed, user hits back, session expires between
    // home and project page). The project page clears the stash once the
    // build actually starts.
    stashHomePrompt(trimmedPrompt, decidedTemplate);
    // The draft has served its purpose — the resume stash above now owns this
    // text and carries it through auth. Leaving both would mean a dismissed
    // signup modal restores the prompt from the stash while a stale draft of an
    // earlier keystroke sits behind it.
    clearHomePromptDraft();
    // Submitting a prompt supersedes any clone the visitor clicked earlier and
    // abandoned at the login modal. Without this, the resume paths (which let a
    // clone win over a prompt) would fork that old card on sign-in, spend the
    // credit on it, and silently drop the prompt just typed.
    clearCloneIntent();

    // Hand selected files to the in-memory holder. The project page
    // calls takePendingAttachments() and uploads after ensureProject.
    //
    // The IDB mirror runs on BOTH branches, not only the logged-out one. It
    // was written for the OAuth full-page redirect, but the signed-in path has
    // a full-page redirect of its own: a 403 project_limit_reached below hard-
    // navigates to the workspace, which kills the module-level holder just as
    // thoroughly. That silently dropped the files of the user at their cap —
    // the highest-intent moment there is. AWAIT, not fire-and-forget: a
    // window.location.assign() tears the page down and kills an in-flight IDB
    // transaction mid-commit. Cost is ~10 ms.
    if (attachFiles.length > 0) {
      setPendingAttachments(attachFiles);
      await persistPendingAttachments(attachFiles);
    }

    if (!user) {
      // Mint the pending project id ONCE here (logged-out submit) and thread it
      // through BOTH the stash and the `pid` URL passthrough. Every post-auth
      // resume path reuses this exact id, so signup + email verification +
      // opening the magic link in a new tab all converge on the SAME
      // /project/<id> instead of each minting a fresh uuid — which was creating
      // duplicate projects and double-charging credits (QA dup-build bug). The
      // re-stash overwrites the id-less one written above.
      const pendingProjectId = uuidv4();
      stashHomePrompt(trimmedPrompt, decidedTemplate, pendingProjectId);
      trackPreSignupIntentCaptured("prompt");
      openSignup(
        "/?prompt=" +
          trimmedPrompt +
          "&templateId=" +
          decidedTemplate +
          "&pid=" +
          pendingProjectId,
        "",
        "home_hero_submit",
      );
      return;
    }

    if (generatingIdea) return;
    setIdeaError(null);
    setGeneratingIdea(true);

    const projectId = uuidv4();
    const encoded = encodeURIComponent(trimmedPrompt);
    // Carry the "already improved" signal to the project's initial build so the
    // silent enhancer is skipped for a prompt the user already reviewed. Trim
    // both sides — the accepted text may carry whitespace added while editing.
    const improvedParam =
      improvedHomeTextRef.current != null &&
      improvedHomeTextRef.current.trim().length > 0 &&
      improvedHomeTextRef.current.trim() === trimmedPrompt
        ? "&improved=1"
        : "";

    // Pre-create the project row before navigating so we never land in the
    // /project/[id] workspace with a non-existent project. The POST is
    // idempotent (upsert + ignoreDuplicates), so the workspace's own
    // ensureProject() call will resolve cleanly on top of it.
    try {
      const res = await fetch("/api/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: projectId,
          prompt: trimmedPrompt,
          plan: { templateId: decidedTemplate },
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 401) {
          setGeneratingIdea(false);
          openSignup(
            "/?prompt=" + trimmedPrompt + "&templateId=" + decidedTemplate,
            "",
            "home_hero_submit_401",
          );
          return;
        }
        if (res.status === 403 && body?.error === "project_limit_reached") {
          // They tried to create past their cap — NOW we show the upgrade
          // wall. Carry the prompt + template into the workspace via
          // show_limit so the project page restores it into the input and
          // opens the modal; after upgrading they send and continue right
          // where they left off (no lost intent). Hard nav for COEP/COI,
          // matching the normal build path.
          stashHomePrompt(trimmedPrompt, decidedTemplate);
          const params = new URLSearchParams({ show_limit: "true" });
          params.set("prompt", trimmedPrompt);
          if (decidedTemplate) params.set("templateId", decidedTemplate);
          navigated = true;
          window.location.assign(
            `/project/${projectId}?${params.toString()}`,
          );
          return;
        }
        if (res.status === 429) {
          setIdeaError("You're going too fast. Try again in a moment.");
          setGeneratingIdea(false);
          return;
        }
        setIdeaError("Couldn't start your project. Try again.");
        setGeneratingIdea(false);
        return;
      }
    } catch {
      setIdeaError("Network blip. Try again.");
      setGeneratingIdea(false);
      return;
    }

    localStorage.setItem(
      `panda:project-template:${projectId}`,
      decidedTemplate,
    );
    router.push(
      `/project/${projectId}?prompt=${encoded}&templateId=${nextTemplate}&source=home_idea${improvedParam}`,
    );
    } finally {
      // Only reset the spinner on early-return paths. On the hard-nav
      // path the page is about to unload, so we keep the spinner until
      // the new document takes over (see the note at the top of this fn).
      if (!navigated) setIsGenerating(false);
    }
  }

  function handleRegenerate(project: UserProject) {
    setPrompt(project.prompt);
    // Focus on input without scrolling
    focusHeroPrompt();
  }

  async function handleLogout() {
    const supabase = createClient();
    // Current-device logout only — never revoke the user's other sessions
    // (signOutCurrentDevice handles scope + analytics + identity reset).
    await signOutCurrentDevice(supabase);
    setUser(null);
    setProjects([]);
    router.refresh();
  }

  /**
   * One-shot marker: a verification-parked clone has already been replayed in
   * this tab. The gate modal reloads the page whenever it sees the account
   * verify, and that reload is what auto-resumes the clone — so if a resumed
   * fork were blocked AGAIN and re-parked itself, the two would ping-pong.
   * Both sides read the same `user_credits.email_verified_at`, so a real
   * disagreement should be transient, but a reload loop on a paid landing page
   * is not a failure mode worth leaving open. sessionStorage on purpose: the
   * guard has to survive that reload and die with the tab.
   */
  const VERIFICATION_RESUME_ONCE_KEY = "panda:home-clone-verify-resumed";

  function markVerificationResumeFired() {
    try {
      sessionStorage.setItem(VERIFICATION_RESUME_ONCE_KEY, "1");
    } catch {
      // Unavailable — the worst case is the loop guard doesn't apply.
    }
  }

  function verificationResumeAlreadyFired(): boolean {
    try {
      return sessionStorage.getItem(VERIFICATION_RESUME_ONCE_KEY) === "1";
    } catch {
      return false;
    }
  }

  /**
   * Park a clone the email-verification gate just interrupted, so the reload
   * that gate performs after the magic link is clicked can replay the exact
   * same target (PANDA-1618). Returns whether the intent really made it into
   * storage — localStorage throws in private browsing / on quota, and a clone
   * that has already had its one post-verification replay is refused — so the
   * analytics event can report a lost resume instead of implying the user
   * simply walked away.
   */
  function persistVerificationIntent(
    target: CloneTarget,
    surface: HomepageCloneSurface,
  ): boolean {
    if (verificationResumeAlreadyFired()) return false;
    stashCloneIntent(target, { surface, pendingVerification: true });
    const saved = readCloneIntent();
    if (!saved?.pendingVerification) return false;
    return "projectId" in target
      ? saved.projectId === target.projectId
      : saved.templateSlug === target.templateSlug;
  }

  /**
   * Perform the fork. Deliberately free of any `user` dependency so the
   * post-login resume path can call it straight from the auth listener, whose
   * `[]`-dep closure still sees the signed-out `user` from first render.
   * Callers are responsible for the auth gate.
   *
   * A fork costs one build credit, so every rejection has to be visible:
   * silently doing nothing on a 402 is how the old prompt-prefill behaviour
   * hid the fact that nothing was actually cloned.
   */
  async function runClone(
    projectId: string,
    surface: HomepageCloneSurface = "built_with_panda",
  ) {
    setRemixingProjectId(projectId);
    let navigated = false;
    try {
      const forked = await forkProject(projectId);
      // Hard nav — same COEP/COI reasoning as the prompt path. Without this,
      // the editor lands with crossOriginIsolated=false and WebContainer.boot()
      // fails, so the remix preview never appears.
      navigated = true;
      // ?from=clone → "Cloning your template" loading copy on first open
      // instead of "Restoring your session" (see lib/preview/load-kind).
      window.location.assign(`/project/${forked.id}?from=clone`);
    } catch (error) {
      const code =
        error instanceof ApiError
          ? (error.code ?? error.message)
          : error instanceof Error
            ? error.message
            : "unknown";
      // The API puts its human-readable explanation in `message` and the code
      // in `error`; ApiError.message carries the code, so reach into the parsed
      // body first — otherwise the failure event's `error` would just repeat
      // `reason` and say nothing about what actually happened.
      const bodyMessage =
        error instanceof ApiError &&
        typeof (error.details as { message?: unknown })?.message === "string"
          ? ((error.details as { message: string }).message)
          : null;
      const detail =
        bodyMessage ?? (error instanceof Error ? error.message : String(error));
      const target = { projectId, surface };

      if (code === "email_verification_required") {
        // NOT a failure — the clone is paused, not lost. Persist the target
        // BEFORE verification starts so the gate modal's post-verification
        // reload resumes this exact build, and report the interruption under
        // its own event so `homepage_clone_failed` keeps meaning "the clone
        // actually broke". The global gate modal (app/layout.tsx) explains the
        // block, so no toast on top of it.
        const persisted = persistVerificationIntent({ projectId }, surface);
        trackHomepageCloneVerificationRequired(target, persisted);
        return;
      }

      trackHomepageCloneFailed(target, code || "unknown", detail);

      if (code === "project_limit_reached") {
        // The workspace shows the upgrade wall; hard nav for COEP/COI parity
        // with the normal build path.
        navigated = true;
        window.location.assign(`/project/${uuidv4()}?show_limit=true`);
        return;
      }
      if (code === "credit_limit_reached") {
        // Deliberate: the paywall fires at balance 0 rather than pre-blocking
        // the click. There's no workspace to host the modal here, so surface
        // it inline with the upgrade path one tap away.
        customToast.error("You're out of credits. Top up to keep cloning.", {
          action: {
            label: "Upgrade",
            onClick: () => router.push("/pricing"),
          },
        });
        return;
      }
      if (code === "source_not_clonable") {
        // The route sends the reason alongside the code — a source with no files
        // and a source the health gate refused are both "not clonable" but need
        // different copy, and only the server knows which one this is.
        // `bodyMessage`, not `detail`: detail falls back to ApiError.message,
        // which is the raw code — never show the user "source_not_clonable".
        customToast.error(
          bodyMessage ||
            "This build's files are no longer available, so it can't be cloned.",
        );
        return;
      }
      // Anything else. `code` is the route's machine code and `bodyMessage` its
      // own sentence; the shared map turns both into copy that is true of THIS
      // failure instead of the old one-size "try again", and only offers Retry
      // when retrying can actually succeed.
      const status = error instanceof ApiError ? error.status : undefined;
      customToast.error(
        cloneErrorMessage(
          code,
          bodyMessage ?? undefined,
          "Couldn't clone this build. Try again.",
        ),
        isRetryableCloneError(code, status)
          ? {
              action: {
                label: "Retry",
                onClick: () => void runClone(projectId, surface),
              },
            }
          : undefined,
      );
    } finally {
      // Only clear on early/error paths — the success path is about to
      // unload the page anyway and we don't want the spinner to flicker
      // back to the static remix icon before the new document takes over.
      if (!navigated) setRemixingProjectId(null);
    }
  }

  /**
   * "Clone this" — a real fork of the showcased build's files into a project
   * the user owns. Shared by the "Built with Panda" cards and the legacy
   * CommunityTemplateCard.
   */
  async function handleRemix(
    projectId: string,
    surface: HomepageCloneSurface = "built_with_panda",
  ) {
    if (remixingProjectId) return;
    if (!user) {
      // A clone target is a project id, not a route, so the login modal's
      // `next` can't carry it. Stash it and replay on SIGNED_IN.
      stashCloneIntent({ projectId }, { surface });
      openLogin(window.location.pathname);
      return;
    }
    await runClone(projectId, surface);
  }

  /**
   * "Clone this" for a homepage template card. These cards front a real
   * screenshot of a real source project, so — per the clone rule — clicking
   * actually clones that source via /api/templates/[slug]/use rather than
   * re-imagining it from a prompt. Mirrors the /templates gallery's
   * handleCloneTemplate so both surfaces behave identically.
   *
   * A clone spends one build credit, so every rejection is surfaced rather than
   * silently swallowed.
   */
  async function handleCloneTemplate(tpl: HomepageTemplate) {
    if (cloningTemplateSlug) return;
    trackTemplateUsed(tpl.slug, "homepage");
    if (!user) {
      // Hand off to the template's own page, which owns the signup modal and
      // the same clone button, so the chosen template survives auth without a
      // separate intent stash.
      router.push(`/templates/${tpl.slug}`);
      return;
    }
    await runTemplateClone(tpl.slug);
  }

  /**
   * The slug-only half of handleCloneTemplate. Split out because the resume
   * paths replay a stashed intent that carries a slug and nothing else — they
   * have no HomepageTemplate object to hand — and because both entry points
   * must report outcomes identically.
   */
  async function runTemplateClone(slug: string) {
    setCloningTemplateSlug(slug);
    const target = {
      templateSlug: slug,
      surface: "homepage_template" as const,
    };
    try {
      const res = await fetch(`/api/templates/${slug}/use`, {
        method: "POST",
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403 && data?.error === "email_verification_required") {
        // Same hand-off as runClone: park the intent, report the interruption
        // as an interruption. The global gate modal (app/layout.tsx) already
        // explains this; a second toast on top of it just contradicts it.
        const persisted = persistVerificationIntent(
          { templateSlug: slug },
          "homepage_template",
        );
        trackHomepageCloneVerificationRequired(target, persisted);
        setCloningTemplateSlug(null);
        return;
      }
      if (res.status === 403 && data?.error === "project_limit_reached") {
        trackHomepageCloneFailed(
          target,
          "project_limit_reached",
          data?.message ?? "Project limit reached",
        );
        router.push(`/project/${uuidv4()}?show_limit=true`);
        return;
      }
      if (res.status === 402) {
        trackHomepageCloneFailed(
          target,
          data?.error || "credit_limit_reached",
          data?.message ?? "Out of credits",
        );
        customToast.error("You're out of credits. Top up to keep building.", {
          action: { label: "Upgrade", onClick: () => router.push("/pricing") },
        });
        setCloningTemplateSlug(null);
        return;
      }
      if (res.status === 422) {
        trackHomepageCloneFailed(
          target,
          data?.error || "source_not_clonable",
          data?.message ?? "Template files unavailable",
        );
        customToast.error(
          data?.message ||
            "This template's files are no longer available, so it can't be cloned.",
        );
        setCloningTemplateSlug(null);
        return;
      }
      // A 503 (the template lookup itself failed) and a 500 are ours and
      // retryable; the old code threw both into the catch below, where they
      // became "Couldn't clone this template. Try again." with no way to tell
      // a transient blip from a template that is genuinely gone.
      if (!res.ok && res.status >= 500) {
        const code: string | undefined = data?.error;
        trackHomepageCloneFailed(
          target,
          code || "server_error",
          data?.message ?? `HTTP ${res.status}`,
        );
        customToast.error(
          cloneErrorMessage(
            code,
            data?.message,
            "Couldn't clone this template. Try again.",
          ),
          {
            action: {
              label: "Retry",
              onClick: () => void runTemplateClone(slug),
            },
          },
        );
        setCloningTemplateSlug(null);
        return;
      }
      if (res.status === 404) {
        const code: string | undefined = data?.error;
        trackHomepageCloneFailed(
          target,
          code || "template_unavailable",
          data?.message ?? "Template unavailable",
        );
        customToast.error(
          cloneErrorMessage(
            code,
            data?.message,
            "This template isn't available right now.",
          ),
        );
        setCloningTemplateSlug(null);
        return;
      }
      if (!res.ok || !data?.id) throw new Error(data?.error || "clone_failed");
      // ?from=template → template loading copy on first open (see load-kind).
      router.push(`/project/${data.id}?from=template`);
    } catch (err) {
      console.error("Clone homepage template error:", err);
      trackHomepageCloneFailed(
        target,
        err instanceof Error ? err.message || "clone_failed" : "clone_failed",
        err instanceof Error ? err.message : String(err),
      );
      customToast.error("Couldn't clone this template. Try again.");
      setCloningTemplateSlug(null);
    }
  }

  /** The analytics identity of a stashed intent. */
  function cloneIntentTarget(intent: CloneIntent) {
    return {
      projectId: intent.projectId,
      templateSlug: intent.templateSlug,
      surface: intent.surface,
    };
  }

  /** Replay a stashed intent down whichever clone path owns its target. */
  function runCloneIntent(intent: CloneIntent) {
    if (intent.templateSlug) {
      void runTemplateClone(intent.templateSlug);
      return;
    }
    if (intent.projectId) {
      // runClone (not handleRemix): the auth-listener closure was captured on
      // first render, where `user` is still null — handleRemix's auth gate
      // would bounce straight back to the login modal.
      void runClone(intent.projectId, intent.surface);
    }
  }

  /**
   * Read the flag the verification gate modal sets on the reload it performs
   * once the magic link is clicked. `consume` empties it so exactly one resume
   * path can act on a given verification.
   */
  function readJustVerifiedFlag(consume: boolean): boolean {
    if (typeof window === "undefined") return false;
    try {
      if (sessionStorage.getItem("panda:just-verified") !== "1") return false;
      if (consume) sessionStorage.removeItem("panda:just-verified");
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Act on a stashed clone intent now that the user is signed in. Returns true
   * when it has taken ownership of this load (fired a clone, or deliberately
   * did nothing because another resume already ran), so the caller stops
   * before the prompt-resume paths. The rule itself — and why a
   * verification-parked intent resumes differently from a login-parked one —
   * lives in decideCloneResume.
   */
  function consumeCloneIntentOnAuth(): boolean {
    const decision = decideCloneResume({
      intent: readCloneIntent(),
      // Peek: only a decision that actually resumes gets to consume it.
      justVerified: readJustVerifiedFlag(false),
      alreadyResumed: homeResumeFiredRef.current,
    });

    if (decision.kind === "none") return false;
    if (decision.kind === "already_resumed") return true;

    homeResumeFiredRef.current = true;
    if (decision.consumeJustVerified) readJustVerifiedFlag(true);
    // One-shot: consume before cloning so a bfcache restore or a second auth
    // event can't clone the same build twice (the double-credit bug).
    clearCloneIntent();

    if (decision.trigger === "verification") {
      markVerificationResumeFired();
      trackHomepageCloneVerificationResumed(
        cloneIntentTarget(decision.intent),
        "auto",
      );
    } else {
      trackPreSignupIntentRestored("clone");
      trackAuthReturnedToProjectAction("clone");
    }
    runCloneIntent(decision.intent);
    return true;
  }

  async function handleLikeChange(projectId: string, newLikeCount: number) {
    setCommunityProjects((prev) =>
      prev.map((project) =>
        project.id === projectId
          ? { ...project, likes: newLikeCount, hasLiked: !project.hasLiked }
          : project,
      ),
    );
  }

  function getDisplayNameFromUserId(userId: string) {
    return `User ${userId.slice(0, 6)}`;
  }

  function pseudoStat(seed: string, base: number, spread: number) {
    let h = 0;
    for (let i = 0; i < seed.length; i++)
      h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    return base + (h % spread);
  }

  function timeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffWeeks < 4) return `${diffWeeks}w ago`;
    if (diffMonths < 12) return `${diffMonths}mo ago`;
    return `${Math.floor(diffMonths / 12)}y ago`;
  }

  // "8,133" → "8.1K". Used only below `sm`, where the mono digits are the most
  // expensive part of the trust-indicator row. The fraction digit is dropped in
  // the 10K–999K band so the string never exceeds 4 characters as the count
  // grows (6.8K → 68K → 683K → 6.8M); the row's slack at 320px is single-digit
  // pixels, so a 5th character would wrap it.
  function compactCount(n: number): string {
    const wide = n >= 10_000 && n < 1_000_000;
    return new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: wide ? 0 : 1,
    }).format(n);
  }

  // ── v15 homepage (client rebuild) ──────────────────────────────────────────
  // Rendered when SHOW_HOME_V15 is on. Reuses the SAME state + handlers as the
  // legacy page (prompt, handleGenerate, auth modals, community/template data),
  // so the real build/submit flow is unchanged — only the layout differs. The
  // legacy return below stays intact so flipping the flag restores it exactly.
  if (SHOW_HOME_V15) {
    // Fill the hero box from anywhere (chip / template / clone), focus it, and
    // scroll up to it — the single conversion action the whole page funnels to.
    const fillHeroPrompt = (text: string) => {
      setPrompt(text);
      requestAnimationFrame(() => {
        document
          .getElementById("v15-build-input")
          ?.focus({ preventScroll: true });
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    };

    // "Built with Panda" — only real builds that have BOTH a screenshot and a
    // live URL, so every card is real and every "View live site" works. Capped
    // at 3. If none qualify, the whole section is hidden (no placeholders).
    // Resolved server-side in lib/community/homepage-builds.ts and delivered in
    // the initial HTML, so there is no loading state and nothing pops in.
    const v15Builds = initialBuilds;

    // NOTE: the Templates cards used to borrow a real DB template's
    // screenshot_url via a slug/name keyword match. That was a lie: the card's
    // prompt was a hardcoded string with no source_project_id, so the screenshot
    // showed one project while the click built something else from text — a
    // result the build could never reproduce. Do not reintroduce that match.
    //
    // A real screenshot may only front a card that clones its real source. So
    // the Templates section now renders real, clonable gallery templates
    // (initialTemplates: screenshot + source project) whose click actually
    // clones the source via handleCloneTemplate. Only when NONE exist does it
    // fall back to the hardcoded prompt cards, which carry their own generic
    // illustrative art and say "Use this prompt" — never an app screenshot.

    // The three trust indicators must never wrap to a second line. The row is
    // sized from the viewport instead of a fixed 12.5px: at 12.5px with 8px
    // separator margins the full string measures ~328px, which only fits a
    // ~424px-wide phone, so it wrapped on every common handset. Font + gap
    // scale with vw (floors chosen so the row still fits a 320px viewport),
    // and the count renders compact below `sm` where the digits cost the most.
    const reassurance = (
      <div className="mt-[18px] flex max-w-full items-baseline justify-center gap-x-[clamp(3px,1.05vw,8px)] whitespace-nowrap font-[family-name:var(--font-geist),sans-serif] text-[clamp(10px,3.05vw,12.5px)] text-muted-foreground/70 dark:text-white/85">
        {appsBuilt !== null && appsBuilt > 0 && (
          <>
            <span className="shrink-0 text-muted-foreground dark:text-white">
              <span className="font-[family-name:var(--font-geist-mono),monospace] tabular-nums">
                <span className="sm:hidden">{compactCount(appsBuilt)}</span>
                <span className="hidden sm:inline">
                  {appsBuilt.toLocaleString()}
                </span>
              </span>{" "}
              apps built
            </span>
            <span aria-hidden className="shrink-0 opacity-60">
              ·
            </span>
          </>
        )}
        <span className="shrink-0 text-muted-foreground dark:text-white">
          No credit card required
        </span>
        <span aria-hidden className="shrink-0 opacity-60">
          ·
        </span>
        <span className="shrink-0 text-muted-foreground dark:text-white">
          Free to start
        </span>
      </div>
    );

    const arrow = (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M5 12h13M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );

    return (
      <div className="relative min-h-screen overflow-x-clip bg-background font-[family-name:var(--font-geist),sans-serif] text-foreground">
        {/* Hero halo as a full-bleed layer spanning the WHOLE first viewport,
            including behind the sticky nav (z-0, under the z-50 nav). The nav is
            fully transparent, so the gradient reads straight through it and the
            header blends seamlessly into the hero — no white strip (Simon's
            feedback). The radial origin (50% 0%) now blooms from the true page
            top. The hero <header> below no longer paints its own copy. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[100lvh]"
          style={{ backgroundImage: "var(--v15-halo)" }}
        />
        {user && <PausedBanner />}
        {user && <PaymentIssueBanner surface="home" />}

        {/* Auth error banner (e.g. expired verification link) — same behavior
            as the legacy page. */}
        {authError && (
          <div className="fixed left-0 right-0 top-0 z-[60] flex items-center justify-center gap-3 bg-red-500 px-4 py-3 text-sm font-medium text-white shadow-lg">
            <span>{authError}</span>
            <button
              type="button"
              onClick={() =>
                authErrorMode === "session" ? openLogin("/") : openSignup("/", "", "auth_error_banner")
              }
              className="shrink-0 rounded-full bg-white px-4 py-1.5 text-xs font-semibold text-red-600 transition-opacity hover:opacity-90"
            >
              {authErrorMode === "session" ? "Sign in with Google" : "Sign up again"}
            </button>
            <button
              onClick={() => setAuthError(null)}
              className="ml-1 shrink-0 rounded-full p-1 text-white/80 transition-colors hover:bg-white/20 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* QUIET NAV — logo · Pricing · Log in · Start free (no theme toggle).
            Logged-in users keep Dashboard + account menu so their UX isn't
            broken. "Start free" focuses the hero box (does not navigate).
            On mobile the secondary links (Pricing, Log in / Dashboard) collapse
            into HomeMobileNavMenu, leaving the bar to the logo + primary CTA. */}
        {/* Header blends into the hero gradient (Simon's feedback — no white
            strip) while still reading as a bar. The hero halo layer above sits
            BEHIND this nav, so it stays translucent: a light frosted-glass tint +
            blur + a hairline bottom edge give it just enough definition to read
            as a navbar, with the gradient still showing straight through. On
            scroll it deepens to the fuller frosted bar for readability over the
            content beneath. Reuses headerScrolled. */}
        <nav
          className={cn(
            "sticky top-0 z-50 border-b transition-all duration-300 ease-out",
            headerScrolled
              ? "border-black/5 bg-white/60 shadow-sm backdrop-blur-xl backdrop-saturate-150 dark:border-white/10 dark:bg-black/50"
              : "border-black/10 bg-white/25 shadow-sm backdrop-blur-lg backdrop-saturate-150 dark:border-white/10 dark:bg-white/8",
          )}
        >
          {/* `app-header-row` is a hook for one rule in globals.css: when a
              top-of-page banner is on screen this row grows so the logo keeps
              its breathing room. Class only — the 58px here is unchanged and is
              still what every page without a banner renders. */}
          <div className="app-header-row mx-auto flex h-[58px] w-full max-w-[1080px] items-center gap-2 px-3 sm:gap-5 sm:px-6">
            <a href="#top" className="shrink-0 text-foreground" aria-label="Panda home">
              <PandaBrandLogo />
            </a>
            <div className="ml-auto flex items-center gap-2.5 sm:gap-5">
              {/* Secondary nav — desktop only; mobile reaches these through the
                  burger below (HomeMobileNavMenu → MobileNavLinks, which has
                  always read the shared list). These were hand-written links
                  until GT-04: this bar was the ONLY nav surface not sourced
                  from lib/nav-links.ts, which is precisely how Referrals ended
                  up reachable everywhere except the desktop homepage — the one
                  place logged-in users land. The bar stays deliberately short;
                  HOME_NAV_KEYS picks which entries appear, nav-links owns where
                  each one points. requiresAuth does the signed-in gating, so
                  Projects and Referrals still only render for a user. */}
              {navLinksByKeys(HOME_NAV_KEYS, !!user).map((link) => (
                <Link
                  key={link.key}
                  href={link.href}
                  className={HOME_NAV_LINK_CLS}
                >
                  {link.label}
                </Link>
              ))}
              {user ? (
                <>
                  {/* Quick dark/light switch — same next-themes state as the
                      Settings → Appearance card, so the two stay in sync and the
                      choice persists across reloads. Shown on desktop + mobile. */}
                  <FooterThemeToggle />
                  <HomeUserAccountMenu
                    email={user.email}
                    userMetadata={user.userMetadata}
                    onLogout={handleLogout}
                  />
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => openLogin("/")}
                    className="hidden whitespace-nowrap text-[13.5px] font-medium text-muted-foreground transition-colors hover:text-foreground sm:inline-flex"
                  >
                    Log in
                  </button>
                  <button
                    type="button"
                    onClick={() => startFree("home_nav")}
                    // Primary CTA inverts with the theme: black pill / white
                    // label in light mode, flipping to white / black in dark so
                    // it stays the highest-contrast thing in the bar either way.
                    className="whitespace-nowrap rounded-[9px] bg-zinc-900 px-3 py-[7px] text-[13.5px] font-semibold text-white shadow-sm ring-1 ring-black/10 transition-colors hover:bg-zinc-800 sm:px-3.5 dark:bg-white dark:text-zinc-900 dark:ring-white/15 dark:hover:bg-zinc-100"
                  >
                    Start free
                  </button>
                </>
              )}
              <HomeMobileNavMenu
                signedIn={!!user}
                onLogin={() => openLogin("/")}
              />
            </div>
          </div>
        </nav>

        {/* HERO — fills the first viewport (minus the 58px sticky nav) and
            vertically centers the prompt so it owns the first screen. This is
            what keeps the "Built with Panda" section that follows below the fold
            on desktop and mobile (PANDA-1506): the hero always reaches the fold,
            so the next section starts under it. 58px = the sticky nav's
            h-[58px] above.

            lvh (largest viewport) — NOT svh, and NOT dvh:
              • svh is the viewport with the browser chrome FULLY EXPANDED, i.e.
                the smallest it can ever be. Mobile Safari usually shows less
                chrome than that (collapsed/floating toolbar), so a svh-tall hero
                ended up SHORTER than the real screen and let "Built with Panda"
                peek through the strip at the bottom — the bug this replaces.
              • dvh tracks the chrome live, so the hero would grow mid-scroll and
                slide the vertically-centred prompt around under the user's
                finger, and would shrink when the mobile keyboard opens over the
                prompt input.
              • lvh is the viewport with the chrome hidden — the largest it can
                be — so the hero is >= the visible screen in EVERY chrome state
                and the next section can never reach the fold. It is also a
                static value, so it keeps the no-reflow property svh was
                originally chosen for.
            The trade-off is ~1 toolbar's worth of hero that sits just past the
            fold when the chrome IS expanded. Harmless here: the content is
            centred and only fills about half the box, so nothing is clipped —
            the page just scrolls a few dozen px more. Desktop is unaffected:
            with no collapsing chrome, svh/lvh/dvh all resolve to the same value,
            so this is a mobile-only behaviour change by construction.
            The halo layer above is h-[100lvh] to match — it spans nav + hero, so
            the two must stay on the same unit or the gradient stops short. */}
        <header
          id="top"
          className="relative flex min-h-[calc(100lvh-58px)] flex-col justify-center px-6 py-12 text-center sm:py-[78px]"
        >
          <div className="mx-auto flex w-full max-w-[1080px] flex-col items-center">
            {/* The headline is EDITABLE COPY, not a string in this file — it
                comes from the `site_content` row that Slack's /hero command
                writes, server-rendered in app/page.tsx so it is in the initial
                HTML for SEO. It was hard-coded here until this change, which
                meant /hero silently edited a hero nobody could see (the legacy
                homepage below the SHOW_HOME_V15 return) while the live page
                ignored it. Both heroes now read the same row.

                The two hand-placed responsive <br>s that used to sit here went
                with it: they were tuned to the exact words "Build your app. Get
                customers." and would have cut arbitrary editable copy in the
                wrong place. An editor who wants a specific break presses Enter
                in Slack; everything else is decided by the two mechanisms
                below, in this order.

                WHERE it may break comes from renderHighlighted, which plans
                the copy into phrases (lib/hero-wrapping.ts) and renders them as
                nested nowrap spans, each held from the narrowest width that can
                fit it, so no line ends on a connector — "Build your app. Get /
                customers." is the wrap that motivated it. text-balance then
                evens out whatever breaks are left, which is all it was ever
                able to do: balance optimises line WIDTH and has no notion of a
                phrase, so on its own it produced "Build apps and / get
                customers by / chatting with AI." — three tidy lines, two
                stranded connectors.

                break-words is the floor under both — it keeps a pasted URL or
                an unbroken 60-character word from overflowing the page on a
                360px phone (playwright/tests/responsive/homepage.spec.ts
                asserts exactly that, and also that no line ends on a stranded
                connector). Nothing the planner emits can defeat it: it never
                holds a lone word, so a long word always keeps its break
                opportunities. */}
            <h1 className="mt-2 max-w-[860px] text-balance break-words text-[clamp(40px,7vw,64px)] font-extrabold leading-[1.1] tracking-[-0.02em]">
              {renderHighlighted(heroContent.heading, V15_HERO_PAINT)}
            </h1>
            {/* Same treatment, one size down: phrases hold, then text-balance
                evens the remaining breaks so the closing word never orphans on
                a line of its own. The planner's budgets are sized for the
                headline's type scale, which is 2.5-4x this one, so every phrase
                it holds here has room to spare. */}
            <p className="mt-4 max-w-[540px] text-balance break-words text-[clamp(15px,2vw,16px)] leading-[1.6] text-muted-foreground dark:text-white">
              {renderHighlighted(heroContent.subheading, V15_SUBHEADING_PAINT)}
            </p>

            {user && creditsResetActions && (
              <div className="mx-auto mt-8 w-full max-w-[540px] text-left">
                <div className="rounded-2xl border border-border/70 bg-background/60 py-2.5 shadow-sm backdrop-blur-sm dark:bg-white/[0.03]">
                  <CreditsResetCard
                    actions={creditsResetActions}
                    onSelect={(a) => {
                      setCreditsResetActions(null);
                      setPrompt(a.prompt);
                      setLastSubmitOrigin("hero");
                      void handleGenerate(a.prompt);
                    }}
                  />
                </div>
              </div>
            )}

            <div className="mt-10 w-full">
              <BuildComposerV15
                idPrefix=""
                variant="hero"
                prompt={prompt}
                setPrompt={setPrompt}
                onSubmit={(text) => {
                  setLastSubmitOrigin("hero");
                  void handleGenerate(text ?? prompt);
                }}
                generating={isGenerating}
                onPromptTyped={handlePromptTyped}
                improve={improve}
                attachFiles={attachFiles}
                setAttachFiles={setAttachFiles}
                autoFocus
              />
            </div>

            {reassurance}
          </div>
        </header>

        {/* BUILT WITH PANDA — real community builds you can clone. Hidden when
            no real build qualifies (screenshot + live URL), never faked. */}
        {v15Builds.length > 0 && (
          <section className="px-6 pt-16">
            <div className="mx-auto w-full max-w-[1080px]">
              <div className="mb-7 text-center">
                {/* Green with the templates eyebrow below it. The two sections are
                    one journey — clone a real build, or clone a template — and
                    their eyebrows are a matched pair, so they move together. */}
                <span className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-400">
                  Built with Panda
                </span>
                <h2 className="mt-2 text-[clamp(22px,3.4vw,30px)] font-bold tracking-[-0.028em]">
                  Real apps and websites you can clone
                </h2>
                <p className="mx-auto mt-2 max-w-[48ch] text-[15.5px] text-muted-foreground">
                  See what founders built, then start from one and make it yours.
                </p>
              </div>
              <div className="mx-auto grid max-w-[980px] grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {v15Builds.map((b) => {
                  const author =
                    b.authorName && b.authorName !== "Unknown"
                      ? b.authorName
                      : null;
                  // Coalesce to 0: a build served from an older Data Cache entry
                  // (cached before these fields existed) has no likes/remixCount,
                  // and calling .toLocaleString() on undefined crashed the page.
                  const cloneCount = b.remixCount ?? 0;
                  const likeCount = b.likes ?? 0;
                  const desc =
                    b.prompt && b.prompt.length > 72
                      ? `${b.prompt.slice(0, 72).trim()}…`
                      : b.prompt;
                  // Captured to a const so the type narrows to `string` inside
                  // the onClick closure below (a bare property access widens).
                  // Normalized because a stored bare host ("app.panda.new")
                  // in an href resolves RELATIVE to this page and sends the
                  // visitor to our own 404 instead of the live site (GT-02).
                  const liveUrl = normalizeCommunityPreviewUrl(b.vercelUrl);
                  // `cloningThis` drives this card's spinner; `isCloning` locks
                  // every card while any fork is in flight.
                  const cloningThis = remixingProjectId === b.id;
                  const isCloning = remixingProjectId !== null;
                  return (
                    <div
                      key={b.id}
                      className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card text-left transition-all hover:-translate-y-[3px] hover:border-muted-foreground/40 hover:shadow-lg"
                    >
                      <div className="relative aspect-5/3">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={b.screenshot_url || undefined}
                          alt={b.title || "Community build"}
                          className="h-full w-full object-cover object-top"
                        />
                        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#18A957]" />
                          Live
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col p-4">
                        {/* Title + creator byline share one baseline-aligned row
                            so short attribution ("by Meera") sits inline instead
                            of dropping to its own line. The title takes the
                            remaining width and truncates; the byline is capped and
                            truncates too, so a long creator name can't crowd out
                            the title or overflow the card on mobile. */}
                        <div className="flex items-baseline gap-x-1.5">
                          <span className="min-w-0 truncate text-base font-semibold tracking-[-0.01em]">
                            {b.title || "Untitled build"}
                          </span>
                          {author && (
                            <span
                              className="max-w-[50%] shrink-0 truncate text-[12px] text-muted-foreground/60"
                              title={`by ${author}`}
                            >
                              by {author}
                            </span>
                          )}
                        </div>
                        {desc && (
                          <div className="mt-1.5 text-[13px] leading-snug text-muted-foreground/70">
                            {desc}
                          </div>
                        )}
                        {/* Social proof — clones + likes, always shown (including
                            0) so the signal is consistent and never disappears on
                            a fresh build. Clone/repost then like, matching the
                            community gallery card. */}
                        <div className="mt-2 flex items-center gap-4 text-[12px] text-muted-foreground/70">
                          <span
                            className="inline-flex items-center gap-1.5"
                            title={`${cloneCount.toLocaleString()} ${cloneCount === 1 ? "clone" : "clones"}`}
                          >
                            <Repeat2 className="h-4 w-4" strokeWidth={2.2} aria-hidden />
                            {cloneCount.toLocaleString()}
                          </span>
                          <span
                            className="inline-flex items-center gap-1.5"
                            title={`${likeCount.toLocaleString()} ${likeCount === 1 ? "like" : "likes"}`}
                          >
                            <ThumbsUp className="h-4 w-4" strokeWidth={2.2} aria-hidden />
                            {likeCount.toLocaleString()}
                          </span>
                        </div>
                        <div className="mt-auto flex flex-col gap-2 pt-3.5">
                          {/* Real fork of the source project's files — NOT a
                              prompt prefill. Every clone button is disabled
                              while any fork is in flight, so a double-click
                              (or a click on a second card) can't create two
                              projects and burn two credits. */}
                          <button
                            type="button"
                            disabled={isCloning}
                            aria-busy={cloningThis}
                            onClick={() => {
                              trackHomepageCloneClicked(
                                b.id,
                                b.title || "Untitled build",
                                "real_fork",
                              );
                              void handleRemix(b.id);
                            }}
                            className="flex h-[42px] w-full items-center justify-center gap-1.5 rounded-[11px] bg-panda-gradient-success text-sm font-semibold text-white shadow-[0_10px_22px_-12px_rgba(0,122,85,0.7)] transition-transform hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                          >
                            {cloningThis ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.2} />
                                Cloning…
                              </>
                            ) : (
                              <>
                                Clone this
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                                  <path d="M5 12h13M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </>
                            )}
                          </button>
                          {liveUrl && (
                            <a
                              href={liveUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() =>
                                trackHomepageViewLiveClicked(b.id, liveUrl)
                              }
                              className="rounded-md text-center text-[12.5px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5606] focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                            >
                              View live site
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* TEMPLATES — fallback for visitors who don't know what to type.

            Every ACTION in here is green (PANDA-2025's non-destructive
            palette, documented in components/ui/modal-accents.ts): the card's
            "Clone this →" / "Use this prompt →" label, the card's focus ring,
            and "Browse all templates". Cloning a template is the single
            highest-intent thing a logged-out visitor can do on this page, and
            it was painted in the same warm ramp the product confirms a
            deletion in — while the "Built with Panda" clone button ONE SECTION
            UP had already gone green. Same verb, same page, two colours.

            The section EYEBROW ("Templates") is green too, and so is "Built
            with Panda" above it — they are a matched pair and move together.
            Neither is a control, so the palette policy did not require this;
            it is a deliberate call to make the whole clone half of the page
            read as one green surface, and it puts the homepage in agreement
            with the /templates hero eyebrow. They are 12px, below the WCAG
            large-text cutoff, so they take the same emerald-700/400 as the
            card links: 5.5:1 on white, 10.3:1 on the dark base.

            That leaves NO #FF5A1F anywhere in this file, which the guard at
            tests/unit/components/template-clone-cta-green.test.ts now asserts
            outright. */}
        <section id="templates" className="px-6 pt-16">
          <div className="mx-auto w-full max-w-[1080px]">
            <div className="mb-7 text-center">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-400">
                Templates
              </span>
              <h2 className="mt-2 text-[clamp(22px,3.4vw,30px)] font-bold tracking-[-0.028em]">
                Not sure what to type? Start here.
              </h2>
              <p className="mx-auto mt-2 max-w-[48ch] text-[15.5px] text-muted-foreground">
                {initialTemplates.length > 0
                  ? "Pick one and Panda copies its code into a project you own."
                  : "Pick one and Panda will load the prompt for you."}
              </p>
            </div>
            {/* One card per row on phones — a half-width card is too small to
                judge a template by. 2-up returns at sm:, 4-up at lg:. */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* Real, clonable templates (screenshot + source) when the DB has
                  them: the card shows the source's real screenshot and clicking
                  CLONES that source, so the project opens exactly as shown. When
                  none exist (empty DB / local dev with no service role), fall
                  back to the hardcoded prompt-inspiration cards below — those
                  only prefill the hero and MUST NOT show an app screenshot. */}
              {initialTemplates.length > 0
                ? initialTemplates.map((tpl) => {
                    const isCloningThis = cloningTemplateSlug === tpl.slug;
                    // Coalesce to 0: a template served from an older Data Cache
                    // entry (cached before these fields existed) has no
                    // useCount/likes, and .toLocaleString() on undefined crashed.
                    const cloneCount = tpl.useCount ?? 0;
                    const likeCount = tpl.likes ?? 0;
                    return (
                      <button
                        key={tpl.slug}
                        type="button"
                        disabled={cloningTemplateSlug !== null}
                        aria-busy={isCloningThis}
                        onClick={() => void handleCloneTemplate(tpl)}
                        className="group flex w-full flex-col overflow-hidden rounded-2xl border border-border bg-card text-left transition-all hover:-translate-y-1 hover:border-muted-foreground/40 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:border-border disabled:hover:shadow-none"
                      >
                        {/* Taller on phones: the card is full-width there, and
                            100px of a full-width screenshot is an unreadable
                            band. Back to 100px from sm: up, where it's 2-up. */}
                        <div className="h-[184px] sm:h-[100px]">
                          {/* object-top: screenshots are ~4:3, so anchor the crop
                              to the top to match the /templates gallery cards. */}
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={tpl.screenshotUrl} alt="" aria-hidden className="h-full w-full object-cover object-top" />
                        </div>
                        <div className="p-3.5">
                          <h3 className="text-[14.5px] font-semibold tracking-[-0.01em]">
                            {tpl.name}
                          </h3>
                          <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground/70">
                            {tpl.description}
                          </p>
                          {/* Social proof — clones + likes, always shown
                              (including 0), on their OWN row above "Clone this →".
                              Same icon + number style + order as the community
                              cards. Likes come from the template's source project. */}
                          <div className="mt-2 flex items-center gap-4 text-[12px] text-muted-foreground/60">
                            <span
                              className="inline-flex items-center gap-1.5"
                              title={`${cloneCount.toLocaleString()} ${cloneCount === 1 ? "clone" : "clones"}`}
                            >
                              <Repeat2 className="h-4 w-4" strokeWidth={2.2} aria-hidden />
                              {cloneCount.toLocaleString()}
                            </span>
                            <span
                              className="inline-flex items-center gap-1.5"
                              title={`${likeCount.toLocaleString()} ${likeCount === 1 ? "like" : "likes"}`}
                            >
                              <ThumbsUp className="h-4 w-4" strokeWidth={2.2} aria-hidden />
                              {likeCount.toLocaleString()}
                            </span>
                          </div>
                          <span className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 transition-all group-hover:gap-2 dark:text-emerald-400">
                            {isCloningThis ? "Cloning…" : "Clone this →"}
                          </span>
                        </div>
                      </button>
                    );
                  })
                : V15_TEMPLATE_CARDS.map((card, i) => {
                    const shot = V15_TEMPLATE_IMAGES[i];
                    return (
                      <button
                        key={card.name}
                        type="button"
                        onClick={() => {
                          trackTemplateUsed(card.name, "homepage");
                          fillHeroPrompt(card.fill);
                        }}
                        className="group flex w-full flex-col overflow-hidden rounded-2xl border border-border bg-card text-left transition-all hover:-translate-y-1 hover:border-muted-foreground/40 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                      >
                        <div className="h-[184px] sm:h-[100px]">
                          {/* object-top: the art is ~4:3, so anchor the crop to
                              the top to match the /templates gallery cards. */}
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={shot} alt="" aria-hidden className="h-full w-full object-cover object-top" />
                        </div>
                        <div className="p-3.5">
                          <h3 className="text-[14.5px] font-semibold tracking-[-0.01em]">
                            {card.name}
                          </h3>
                          <p className="mt-1 text-xs leading-snug text-muted-foreground/70">
                            {card.desc}
                          </p>
                          <span className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 transition-all group-hover:gap-2 dark:text-emerald-400">
                            Use this prompt →
                          </span>
                        </div>
                      </button>
                    );
                  })}
            </div>
            <div className="mt-6 text-center">
              <Link
                href="/templates"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 transition-all hover:gap-2.5 dark:text-emerald-400"
              >
                Browse all templates {arrow}
              </Link>
            </div>
          </div>
        </section>

        {/* FINAL CTA — repeat the prompt box at the end. Mobile drops to a 16px
            gutter on both the section and the card so the trust-indicator row
            below the prompt has room to stay on one line. */}
        <section className="px-4 pb-5 pt-16 sm:px-6">
          <div className="mx-auto w-full max-w-[1080px]">
            <div
              className="flex flex-col items-center rounded-[28px] px-4 py-14 text-center sm:px-6"
              style={{ backgroundImage: "var(--v15-halo)" }}
            >
              <h2 className="text-balance text-[clamp(24px,3.6vw,34px)] font-bold tracking-[-0.03em]">
                Type your idea. Watch it build.
              </h2>
              <div className="mt-[27px] w-full">
                <BuildComposerV15
                  idPrefix="final-"
                  variant="compact"
                  prompt={prompt}
                  setPrompt={setPrompt}
                  onPromptTyped={handlePromptTyped}
                  onSubmit={(text) => {
                    setLastSubmitOrigin("hero");
                    void handleGenerate(text ?? prompt);
                  }}
                  generating={isGenerating}
                  improve={improve}
                  attachFiles={attachFiles}
                  setAttachFiles={setAttachFiles}
                />
              </div>
              {reassurance}
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="mt-4 border-t border-border px-6 py-9">
          <div className="mx-auto flex w-full max-w-[1080px] flex-col gap-6 text-[13.5px] text-muted-foreground sm:flex-row sm:items-start">
            <div>
              <a href="#top" className="inline-flex text-foreground" aria-label="Panda home">
                <PandaBrandLogo />
              </a>
              <div className="mt-2.5 text-muted-foreground/60">© {currentYear} Panda</div>
            </div>
            <div className="flex flex-wrap gap-x-7 gap-y-6 sm:ml-auto">
              <div className="flex flex-col gap-2.5">
                <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground/50">
                  Build
                </span>
                <Link href="/templates" className="transition-colors hover:text-foreground">Templates</Link>
                <Link href="/pricing" className="transition-colors hover:text-foreground">Pricing</Link>
              </div>
              <div className="flex flex-col gap-2.5">
                <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground/50">
                  Account
                </span>
                {!user && (
                  <button
                    type="button"
                    onClick={() => openLogin("/")}
                    className="text-left transition-colors hover:text-foreground"
                  >
                    Log in
                  </button>
                )}
                {/* Signed-in visitors got a "Start free" that scrolled them
                    back to the public hero — a dead end for someone who
                    already has an account. They now get the dashboard link
                    instead, and signed-out visitors get real signup. */}
                {user ? (
                  <Link
                    href="/projects"
                    className="text-left transition-colors hover:text-foreground"
                  >
                    Projects
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => startFree("home_footer")}
                    className="text-left transition-colors hover:text-foreground"
                  >
                    Start free
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-2.5">
                <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground/50">
                  Legal
                </span>
                <Link href="/terms" className="transition-colors hover:text-foreground">Terms</Link>
                <Link href="/privacy" className="transition-colors hover:text-foreground">Privacy</Link>
                {/* Both required by Paddle's domain review: a Refund Policy in
                    site navigation, and a contact address reachable from the
                    homepage. See app/(auth)/refund/page.tsx. */}
                <Link href="/refund" className="transition-colors hover:text-foreground">Refunds</Link>
                <a href="mailto:help@panda.new" className="transition-colors hover:text-foreground">Contact</a>
              </div>
            </div>
          </div>
        </footer>

        {/* Shared modals — identical to the legacy page so auth/upgrade flows
            are unchanged. */}
        <SignupModal
          open={signupOpen}
          onOpenChange={(o) => {
            setSignupOpen(o);
            if (!o) setSignupInitialEmail("");
          }}
          next={signupNext}
          initialEmail={signupInitialEmail}
          source={signupSource}
        />
        <LoginModal
          open={loginOpen}
          onOpenChange={(o) => {
            setLoginOpen(o);
            if (!o) setLoginInitialError(null);
          }}
          next={loginNext}
          initialError={loginInitialError}
          onSwitchToSignup={(email) => {
            setLoginOpen(false);
            openSignup(loginNext, email, "login_modal_switch");
          }}
        />

        {/* PANDA-1523: post-signup personalization wizard — direct-signup,
            no-prompt first-run onboarding, and the only surface that renders it.
            The prompt-carrying case is deferred to the user's next idle home
            load rather than shown over the builder (see the entry guard above).
            No close affordance on purpose: "Skip for now" inside the wizard
            is the only way out. Note it is NOT what stops the wizard coming
            back — this only renders because the server granted the claim that
            recorded the impression (claimOnboardingOffer), so refresh, click-
            away, a closed tab and a failed submit are all already covered by a
            write that happened before the first pixel. */}
        {showPersonalization ? (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
            <div className="flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col overflow-y-auto rounded-3xl bg-white shadow-2xl dark:bg-[#0b0f19]">
              <PersonalizationWizardInline
                onComplete={(answers) => submitPersonalization(answers)}
                onSkip={() => submitPersonalization(null)}
                submitting={personalizationSubmitting}
                surface="home"
                email={user?.email}
              />
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    // overflow-x-clip (not overflow-hidden) still tames decorative elements
    // that would cause sideways scroll, but unlike overflow:hidden it does
    // NOT create a scroll container — so position:sticky descendants (e.g.
    // the pricing plan-filter bar) keep pinning instead of scrolling away.
    <div className="relative min-h-screen overflow-x-clip bg-background text-foreground">
      {/* Page-level dark-mode atmosphere — layered indigo/magenta radial
          glows that start at the top of the page and bleed past the hero
          into the next sections, then fade to the page background. The
          overlay is `position: absolute` so it scrolls with the page (not
          fixed), `pointer-events-none`, and only renders in dark mode. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-0 hidden h-[180vh] dark:block"
        style={{
          backgroundImage: [
            "radial-gradient(ellipse 70% 35% at 18% 16%, rgba(76, 40, 180, 0.55), transparent 65%)",
            "radial-gradient(ellipse 55% 40% at 88% 24%, rgba(170, 48, 130, 0.45), transparent 60%)",
            "radial-gradient(ellipse 60% 30% at 50% 46%, rgba(60, 28, 120, 0.32), transparent 70%)",
            "linear-gradient(180deg, rgba(8,11,18,0) 0%, rgba(8,11,18,0) 60%, rgba(8,11,18,1) 100%)",
          ].join(", "),
        }}
      />

      {/* Paused subscription banner */}
      {user && <PausedBanner />}
        {user && <PaymentIssueBanner surface="home" />}

      {/* Auth error banner (e.g. expired verification link) */}
      {authError && (
        <div className="fixed top-0 left-0 right-0 z-[60] flex items-center justify-center gap-3 bg-red-500 px-4 py-3 text-sm font-medium text-white shadow-lg">
          <span>{authError}</span>
          <button
            type="button"
            onClick={() =>
              authErrorMode === "session" ? openLogin("/") : openSignup("/", "", "auth_error_banner")
            }
            className="shrink-0 rounded-full bg-white px-4 py-1.5 text-xs font-semibold text-red-600 transition-opacity hover:opacity-90"
          >
            {authErrorMode === "session"
              ? "Sign in with Google"
              : "Sign up again"}
          </button>
          <button
            onClick={() => setAuthError(null)}
            className="ml-1 shrink-0 rounded-full p-1 text-white/80 transition-colors hover:bg-white/20 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Home-page header — logo left, nav centered, auth right */}
      <nav
        className={cn(
          "fixed top-0 left-0 right-0 z-50 px-3 transition-all duration-300 ease-out sm:px-8 lg:px-12",
          headerScrolled
            ? "border-b border-black/5 bg-white/60 py-2 shadow-sm backdrop-blur-xl backdrop-saturate-150 dark:border-white/10 dark:bg-black/50 sm:py-3"
            : "border-b border-transparent bg-transparent py-2 sm:py-6",
        )}
      >
        <div className="mx-auto grid w-full max-w-[1920px] grid-cols-[auto_1fr_auto] items-center gap-4">
          {/* Left: logo */}
          <a
            href="/"
            className="shrink-0 text-foreground transition-opacity hover:opacity-90"
            aria-label="Panda home"
          >
            <PandaBrandLogo />
          </a>
          {/* Center: nav links — Templates, Pricing. (Community is flag-gated
              off in the conversion redesign via SHOW_LEGACY_HOME_SECTIONS; a
              "Features" link is omitted until a Features section exists.) */}
          <div className="hidden items-center justify-center gap-2 md:flex lg:gap-4">
            <a
              href="#templates"
              className="rounded-md px-3 py-2 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-foreground/85 transition-colors hover:text-foreground"
            >
              Templates
            </a>
            {SHOW_LEGACY_HOME_SECTIONS && (
              <Link
                href="/community"
                className="rounded-md px-3 py-2 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-foreground/85 transition-colors hover:text-foreground"
              >
                Community
              </Link>
            )}
            <Link
              href="/pricing"
              className="rounded-md px-3 py-2 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-foreground/85 transition-colors hover:text-foreground"
            >
              Pricing
            </Link>
            {user && (
              <a
                href="/projects"
                className="rounded-md px-3 py-2 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-foreground/85 transition-colors hover:text-foreground"
              >
                Projects
              </a>
            )}
          </div>

          
          {/* Right: auth — mobile Figma shows logo + hamburger only, so no
              dashboard shortcut here; logged-in users reach Dashboard via
              the hamburger menu. */}
          <div className="flex items-center justify-end gap-3 sm:gap-5">
            
            {user ? (
              <>
              <div className="pr-2 md:hidden">
              <Link href="/projects"><LayoutDashboardIcon /></Link>
            </div>
                <FooterThemeToggle className="hidden sm:inline-flex" />
                <HomeUserAccountMenu
                  email={user.email}
                  userMetadata={user.userMetadata}
                  onLogout={handleLogout}
                />
              </>
            ) : (
              <>
                <FooterThemeToggle className="hidden sm:inline-flex" />
                <button
                  type="button"
                  onClick={() => openLogin("/")}
                  className="hidden sm:inline-flex font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-foreground/85 transition-colors hover:text-foreground"
                >
                  Log in
                </button>
                <button
                  type="button"
                  onClick={() => openSignup("/", "", "home_nav_legacy")}
                  className="hidden sm:inline-flex items-center justify-center rounded-full bg-linear-to-r from-[#FF5606] via-[#D52A26] to-[#AA0346] px-6 py-2.5 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-white shadow-md shadow-orange-500/30 transition-all hover:shadow-lg hover:shadow-orange-500/40 hover:brightness-110"
                >
                  Build free →
                </button>
              </>
            )}
            {/* Hamburger — mobile only */}
            <button
              type="button"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="flex md:hidden h-10 w-10 -mr-1 shrink-0 items-center justify-center text-foreground transition-opacity hover:opacity-70"
            >
              {mobileMenuOpen ? (
                <X size={18} />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="9"
                  viewBox="0 0 18 9"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M16.75 7C17.1642 7 17.5 7.33579 17.5 7.75C17.5 8.16421 17.1642 8.5 16.75 8.5H0.75C0.335786 8.5 -3.21882e-08 8.16421 0 7.75C3.21882e-08 7.33579 0.335786 7 0.75 7H16.75ZM16.75 0C17.1642 4.07381e-08 17.5 0.335787 17.5 0.75C17.5 1.16421 17.1642 1.5 16.75 1.5H0.75C0.335786 1.5 -3.21882e-08 1.16421 0 0.75C3.21882e-08 0.335786 0.335786 -4.07382e-08 0.75 0H16.75Z"
                    fill="currentColor"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

      </nav>

      {/* Mobile menu — full-screen overlay (Figma node 2324:40495).
          Renders only on mobile (md:hidden). Locks body scroll while open
          via the effect below. Nav links mirror the desktop header so
          they stay in sync. */}
      {mobileMenuOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Main menu"
          className="fixed inset-0 z-[70] flex flex-col bg-background md:hidden"
        >
          {/* Top bar: theme toggle (left) + close (right) */}
          <div className="flex h-14 items-center justify-between px-4">
            <FooterThemeToggle />
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setMobileMenuOpen(false)}
              className="flex h-6 w-6 items-center justify-center text-[#1A1A1B] transition-opacity hover:opacity-70 dark:text-foreground"
            >
              <X size={24} strokeWidth={2} />
            </button>
          </div>

          {/* Nav links — 40px-tall rows with 8px gap, centered text */}
          <nav className="mt-2 flex flex-col gap-2 px-4">
            <a
              href="#templates"
              onClick={() => setMobileMenuOpen(false)}
              className="flex h-10 items-center justify-center font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-[#1A1A1B] transition-opacity hover:opacity-80 dark:text-foreground"
            >
              Templates
            </a>
            {SHOW_LEGACY_HOME_SECTIONS && (
              <Link
                href="/community"
                onClick={() => setMobileMenuOpen(false)}
                className="flex h-10 items-center justify-center font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-[#1A1A1B] transition-opacity hover:opacity-80 dark:text-foreground"
              >
                Community
              </Link>
            )}
            <Link
              href="/pricing"
              onClick={() => setMobileMenuOpen(false)}
              className="flex h-10 items-center justify-center font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-[#1A1A1B] transition-opacity hover:opacity-80 dark:text-foreground"
            >
              Pricing
            </Link>
            {user && (
              <a
                href="/projects"
                onClick={() => setMobileMenuOpen(false)}
                className="flex h-10 items-center justify-center font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-[#1A1A1B] transition-opacity hover:opacity-80 dark:text-foreground"
              >
                Projects
              </a>
            )}
          </nav>

          {/* Bottom action area — Sign up (gradient) + Log in (text).
              Only shown when signed out, per current header logic. */}
          {!user && (
            <div className="mt-auto flex flex-col gap-3 px-4 pb-6">
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  openSignup("/", "", "home_mobile_menu_legacy");
                }}
                className="flex h-12 items-center justify-center rounded-full bg-linear-to-r from-[#FF5606] via-[#D52A26] to-[#AA0346] font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-white shadow-md shadow-orange-500/30 transition-all hover:shadow-lg hover:shadow-orange-500/40 hover:brightness-110"
              >
                Build free →
              </button>
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  openLogin("/");
                }}
                className="flex h-12 items-center justify-center rounded-full font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-[#1A1A1B] transition-opacity hover:opacity-70 dark:text-foreground"
              >
                Log in
              </button>
            </div>
          )}
        </div>
      )}

      {/* Annual upgrade prompt for logged-in monthly users */}
      {user && (
        <div className="fixed top-18 left-0 right-0 z-40 mx-auto max-w-7xl px-4 sm:px-8">
          <AnnualUpgradePrompt />
        </div>
      )}

      {/* Hero — Figma background image */}
      <section className="relative z-10 min-h-screen overflow-hidden">
        {/* Background image from Figma */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat dark:hidden"
          style={{ backgroundImage: "url('/home_bg.svg')" }}
          aria-hidden
        />
        {/* Dark mode: gradient lives on the page root so it can bleed past
            the hero section. Section stays transparent in dark mode. */}
        {/* Soft white center light to fade chat card into bg */}
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_60%_55%_at_50%_75%,rgba(255,255,255,0.55),transparent_70%)] dark:bg-[radial-gradient(ellipse_60%_55%_at_50%_75%,rgba(255,255,255,0.04),transparent_70%)]"
          aria-hidden
        />

        <div className="relative mx-auto flex min-h-screen w-full max-w-7xl flex-col justify-center px-4 pb-16 pt-20 text-center sm:px-8 sm:pb-28 sm:pt-24 lg:max-w-[90rem] lg:px-12 lg:pt-28">
          {/* Eyebrow — the mobile Figma drops it, so it only shows from sm up */}
          <span className="mx-auto hidden items-center gap-1.5 rounded-full border border-orange-200/70 bg-orange-50/90 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-orange-700 shadow-sm backdrop-blur-md dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-300 sm:inline-flex sm:px-3.5 sm:py-1.5 sm:text-xs">
            <span aria-hidden>🐼</span> The AI Business Builder
          </span>
          <h1 className="mx-auto mt-5 max-w-5xl text-balance text-center font-[family-name:var(--font-rubik),sans-serif] text-[36px] font-medium leading-11 tracking-normal text-foreground sm:mt-6 sm:text-[44px] sm:leading-none lg:text-[56px]">
            {renderHighlighted(heroContent.heading, LEGACY_HERO_PAINT)}
          </h1>
          {/* Through the same renderer as the heading, so a line break or a
              __bold__ run typed into the Slack box behaves identically in both
              fields. It used to render raw, which meant an editor pressing
              Enter here got a space and no warning. */}
          <p className="mx-auto mt-4 max-w-2xl text-pretty text-center font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 tracking-normal text-[#475569] dark:text-slate-400 sm:mt-6 sm:text-base">
            {renderHighlighted(heroContent.subheading, LEGACY_SUBHEADING_PAINT)}
          </p>

          {/* Mode tabs */}
          <PromptComposer
            idPrefix=""
            cardRef={heroCardRef}
            heroMode={heroMode}
            setHeroMode={setHeroMode}
            prompt={prompt}
            setPrompt={setPrompt}
            focused={focused}
            setFocused={setFocused}
            ideaError={ideaError}
            setIdeaError={setIdeaError}
            generatingIdea={generatingIdea}
            onSubmitIdea={() => {
              setLastSubmitOrigin("hero");
              void handleGenerate();
            }}
            attachFiles={attachFiles}
            setAttachFiles={setAttachFiles}
            businessUrl={businessUrl}
            setBusinessUrl={setBusinessUrl}
            businessUrlError={businessUrlError}
            setBusinessUrlError={setBusinessUrlError}
            businessUrlLimitReached={businessUrlLimitReached}
            setBusinessUrlLimitReached={setBusinessUrlLimitReached}
            generatingGrowth={generatingGrowth}
            onSubmitGrowth={() => {
              setLastSubmitOrigin("hero");
              void handleGenerateGrowth();
            }}
            normaliseWebsiteUrl={normaliseWebsiteUrl}
            onUpgrade={() => router.push("/settings/billing")}
            canUpgradePlan={hasHigherPlan(userPlan)}
          />

          {/* Live "apps built this week" pill — sits directly under the prompt
              card (redesign choice). Mode-agnostic social proof; the animated
              count-up + pulsing live dot live inside LiveAppsCounter. mostBuilt
              is the top real templates by use_count, fail-closed when no real
              data. */}
          <LiveAppsCounter
            count={appsBuilt}
            mostBuilt={[...dbTemplates]
              .filter((t) => (t.use_count ?? 0) > 0)
              .sort((a, b) => (b.use_count ?? 0) - (a.use_count ?? 0))
              .slice(0, 3)
              .map((t) => t.name)}
          />

          {/* Try chips — idea mode only: in business mode the card takes a
              URL, so prompt suggestions would write into a hidden input. */}
          {heroMode === "idea" && (
            <div className="mx-auto mt-6 flex flex-wrap items-center justify-center gap-3 px-2">
              <span className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 tracking-normal text-[#1A1A1B]/70 dark:text-muted-foreground">
                Try:
              </span>
              {HERO_IDEA_CHIPS.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => setPrompt(chip)}
                  className="rounded-full border border-white/70 bg-white/85 px-3.5 py-1.5 text-center font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 tracking-normal text-[#1A1A1B] shadow-sm backdrop-blur-md transition-all hover:bg-white hover:text-foreground hover:shadow-md dark:border-white/10 dark:bg-white/[0.06] dark:text-foreground/80 dark:hover:bg-white/10 dark:hover:text-foreground"
                >
                  {chip}
                </button>
              ))}
            </div>
          )}

          {/* Supporting trust line */}
          <p className="mx-auto mt-8 text-pretty text-sm text-muted-foreground/80">
            No credit card required · Free to start · Built by founders who
            scaled $0 → 7-figures
          </p>
        </div>
      </section>

      {/* Made by the Panda Community — flag-gated off in the conversion
          redesign (SHOW_LEGACY_HOME_SECTIONS); kept so it can be revisited. */}
      {SHOW_LEGACY_HOME_SECTIONS && (
      <section className="relative z-10 border-t border-border bg-background py-12 dark:border-transparent sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:max-w-[90rem] lg:px-12">
          <div className="flex flex-col items-center text-center">
            <span className="inline-flex h-7 items-center gap-1.5 rounded-full bg-[#FEF3F2] px-3 font-[family-name:var(--font-rubik),sans-serif] text-xs font-medium leading-4 tracking-normal uppercase text-[#B90C3B] dark:bg-[#3b0a0a] dark:text-[#fe5b4f]">
              <PandaLogo size="xs" className="h-5 w-5" />
              Made by the Panda Community
            </span>
            <h2 className="mt-4 max-w-3xl text-balance font-[family-name:var(--font-rubik),sans-serif] text-[32px] font-medium leading-10 tracking-normal text-foreground sm:mt-5 sm:text-[44px] sm:leading-none lg:text-[56px]">
              Real Products built with Panda
            </h2>
            <p className="mx-auto mt-2 max-w-2xl text-center font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 tracking-normal text-[#77869D] dark:text-muted-foreground sm:mt-4">
              Apps, SaaS products, marketplaces, and tools launched with Panda.
            </p>
          </div>

          <div className="mt-8 grid gap-x-6 gap-y-8 sm:mt-12 sm:gap-y-10 md:grid-cols-2 lg:grid-cols-3">
            {isLoadingCommunity
              ? Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={index}
                    className="overflow-hidden rounded-3xl border border-border bg-card"
                  >
                    <div className="h-[256px] w-full animate-pulse bg-muted sm:h-[272px]" />
                    <div className="px-5 py-4 sm:px-6 sm:py-5">
                      <div className="h-5 w-2/3 animate-pulse rounded bg-muted" />
                      <div className="mt-4 flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="h-9 w-9 animate-pulse rounded-full bg-muted" />
                          <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                        </div>
                        <div className="flex gap-3">
                          <div className="h-4 w-8 animate-pulse rounded bg-muted" />
                          <div className="h-4 w-8 animate-pulse rounded bg-muted" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              : communityProjects?.map((project) => (
                  <CommunityTemplateCard
                    key={project.id}
                    projectId={project.id}
                    title={project.title || "Untitled Project"}
                    authorName={project.authorName || "Unknown"}
                    views={pseudoStat(project.id, 120, 900)}
                    likes={
                      project.likes ?? pseudoStat(project.id + "like", 10, 200)
                    }
                    hasLiked={project.hasLiked}
                    remixCount={project.remixCount}
                    screenshotSrc={project.screenshot_url || undefined}
                    vercelUrl={project.vercelUrl}
                    avatarSrc={project.avatarSrc}
                    avatarEmoji={project.avatarEmoji}
                    avatarColor={project.avatarColor}
                    avatarType={project.avatarType}
                    showBadge={project.showBadge ?? true}
                    onRemix={(projectId) =>
                      handleRemix(projectId, "community_card")
                    }
                    onLikeChange={handleLikeChange}
                    onLoginRequired={() => openLogin(window.location.pathname)}
                  />
                ))}
          </div>

          <div className="mt-8 flex justify-center sm:mt-12">
            <Link
              href="/community"
              className="inline-flex items-center gap-2 rounded-full border border-[#B90C3B] bg-transparent px-6 py-3 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-[#B90C3B] transition-all hover:bg-[#B90C3B] hover:text-white hover:shadow-md hover:shadow-rose-500/30"
            >
              Load more
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* Templates — fanned carousel + "build from scratch" CTA */}
      <section
        id="templates"
        className="relative z-10 border-t border-border bg-background py-12 dark:border-transparent dark:bg-transparent sm:py-20"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:max-w-[90rem] lg:px-12">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex h-7 items-center rounded-full bg-[#FEF3F2] px-3 text-center font-[family-name:var(--font-rubik),sans-serif] text-xs font-medium leading-4 tracking-normal uppercase text-[#B90C3B] dark:bg-rose-500/15 dark:text-rose-300">
              Templates
            </span>
            <h2 className="mt-4 text-balance text-center font-[family-name:var(--font-rubik),sans-serif] text-[32px] font-medium leading-10 tracking-normal text-foreground sm:text-[44px] sm:leading-none lg:text-[56px]">
              Start from a Proven Business
            </h2>
            <p className="mx-auto mt-2 max-w-2xl text-center font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 tracking-normal text-[#77869D] dark:text-muted-foreground sm:mt-4">
              Clone a proven app, customize it, and launch in minutes.
            </p>
          </div>

          {(() => {
            type CarouselItem = {
              id: string;
              title: string;
              description?: string;
              imgSrc: string;
              href?: string;
              onSelect?: () => void;
            };

            const items: CarouselItem[] = isLoadingTemplates
              ? []
              : dbTemplates.length > 0
                ? [...dbTemplates]
                    .sort(
                      (a, b) =>
                        homepageTemplateRank(a.slug) -
                        homepageTemplateRank(b.slug),
                    )
                    .slice(0, 8)
                    .map((tpl) => ({
                      id: tpl.slug,
                      title: tpl.name,
                      description: tpl.description ?? undefined,
                      imgSrc:
                        tpl.screenshot_url ??
                        screenshotMap[tpl.slug] ??
                        TEMPLATE_PREVIEW_IMAGES[tpl.slug as TemplateId]?.src ??
                        "https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&w=720&h=480&q=82",
                      href: `/templates/${tpl.slug}`,
                    }))
                : [...TEMPLATE_OPTIONS]
                    .sort(
                      (a, b) =>
                        homepageTemplateRank(a.id) - homepageTemplateRank(b.id),
                    )
                    .slice(0, 8)
                    .map((opt) => {
                      const preview = TEMPLATE_PREVIEW_IMAGES[opt.id];
                      const exampleLabel =
                        EXAMPLES.find((e) => e.templateId === opt.id)?.label ??
                        opt.label;
                      return {
                        id: opt.id,
                        title: exampleLabel,
                        description: opt.description,
                        imgSrc: screenshotMap[opt.id] ?? preview?.src ?? "",
                        onSelect: () => {
                          setTemplateTouched(true);
                          setSelectedTemplate(opt.id);
                          const starter = starterPromptForTemplate(opt.id);
                          if (starter) setPrompt(starter);
                          trackTemplateUsed(opt.id, "homepage");
                          document
                            .getElementById("prompt-input")
                            ?.scrollIntoView({
                              behavior: "smooth",
                              block: "center",
                            });
                        },
                      };
                    });

            const lastIdx = Math.max(0, items.length - 1);
            const activeIdx = Math.min(Math.max(carouselIndex, 0), lastIdx);

            return (
              <>
                <div className="relative mt-12 h-[300px] sm:h-[340px] lg:h-[360px] overflow-visible">
                  {/* Soft radial gradient backdrop for depth focus */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute left-1/2 top-1/2 h-[110%] w-[85%] -translate-x-1/2 -translate-y-1/2"
                    style={{
                      background:
                        "radial-gradient(ellipse at center, rgba(168,135,255,0.14) 0%, rgba(255,180,160,0.07) 35%, transparent 72%)",
                    }}
                  />

                  {isLoadingTemplates ? (
                    <div className="absolute left-1/2 top-1/2 w-[88%] max-w-[420px] -translate-x-1/2 -translate-y-1/2">
                      <Skeleton className="aspect-[16/10] w-full rounded-[1.75rem]" />
                    </div>
                  ) : (
                    items.map((item, idx) => {
                      // Subtle fan: each card translates horizontally with a
                      // small rotateZ tilt — like cards fanned out on a table.
                      const offset = idx - activeIdx;
                      const absOff = Math.abs(offset);
                      const sign = offset === 0 ? 0 : offset > 0 ? 1 : -1;
                      const visible = absOff <= 2;
                      const isActive = offset === 0;
                      // Per-offset config: gentle X spread, minimal Y, small tilt.
                      // Same fanned layout on every breakpoint — only the
                      // horizontal spread is compressed on mobile so the fan
                      // fits the viewport instead of overflowing (the desktop
                      // ±260/440px offsets run off a phone screen). The active
                      // card also drops 1.1→1 on mobile to leave room for the
                      // side cards to peek.
                      const cfg =
                        absOff === 0
                          ? {
                              x: 0,
                              y: 0,
                              rot: 0,
                              scale: isNarrowViewport ? 1 : 1.1,
                              opacity: 1,
                              blur: 0,
                            }
                          : absOff === 1
                            ? {
                                x: isNarrowViewport ? 28 : 260,
                                y: 14,
                                rot: 9,
                                scale: 0.92,
                                opacity: 0.75,
                                blur: 1,
                              }
                            : {
                                x: isNarrowViewport ? 46 : 440,
                                y: 30,
                                rot: 14,
                                scale: 0.85,
                                opacity: 0.6,
                                blur: 2,
                              };
                      const goToTemplate = () => {
                        if (item.href) {
                          // Hard navigation (not router.push). The home page is
                          // served cross-origin-UNisolated (COEP unsafe-none, so
                          // it can embed community preview iframes). A soft nav
                          // keeps that document's headers, so the template detail
                          // page would land with crossOriginIsolated=false and
                          // its WebContainer live preview could never boot — the
                          // user had to manually refresh to get isolated headers.
                          // A full load fetches the template route with its own
                          // isolated (credentialless) headers. Mirrors the
                          // editor navigation (window.location.assign) below.
                          window.location.assign(item.href);
                        } else {
                          item.onSelect?.();
                        }
                      };
                      const handleCardClick = () => {
                        if (!isActive) {
                          setCarouselIndex(idx);
                          return;
                        }
                        goToTemplate();
                      };
                      return (
                        <div
                          key={item.id}
                          role="button"
                          tabIndex={visible ? 0 : -1}
                          onClick={handleCardClick}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleCardClick();
                            }
                          }}
                          aria-label={
                            isActive
                              ? `Use ${item.title} template`
                              : `View ${item.title}`
                          }
                          className="group absolute left-1/2 top-1/2 w-[88%] max-w-[420px] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2"
                          style={{
                            transform: `translate(-50%, -50%) translate(${sign * cfg.x}px, ${cfg.y}px) rotate(${sign * cfg.rot}deg) scale(${cfg.scale})`,
                            opacity: visible ? cfg.opacity : 0,
                            filter: cfg.blur ? `blur(${cfg.blur}px)` : "none",
                            zIndex: 20 - absOff,
                            pointerEvents: visible ? "auto" : "none",
                            transition: "all 500ms ease-out",
                            willChange: "transform, opacity",
                          }}
                        >
                          <div
                            className={`relative overflow-hidden rounded-[1.75rem] border bg-card ${
                              isActive
                                ? "border-border/70 shadow-[0_30px_80px_-20px_rgba(80,40,120,0.35)] transition-transform duration-300 group-hover:scale-[1.02] group-hover:shadow-[0_40px_100px_-20px_rgba(80,40,120,0.45)]"
                                : "border-border/40 shadow-xl"
                            }`}
                          >
                            {/* Side cards get a darkening overlay so they recede behind the active card */}
                            {!isActive && (
                              <div
                                aria-hidden
                                className="pointer-events-none absolute inset-0 z-10 rounded-[1.75rem] bg-linear-to-b from-black/10 via-black/20 to-black/35"
                              />
                            )}
                            {isActive && (
                              <div
                                aria-hidden
                                className="pointer-events-none absolute inset-0 z-10 rounded-[1.75rem] ring-1 ring-inset ring-white/15"
                              />
                            )}
                            <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                              <Image
                                src={item.imgSrc}
                                alt={item.title}
                                width={1200}
                                height={750}
                                className="h-full w-full object-cover object-top"
                                loading="lazy"
                                quality={80}
                                sizes="(max-width: 640px) 100vw, 420px"
                                // Template screenshots are user-uploaded Supabase
                                // storage URLs, which the Next image optimizer
                                // rejects ("url not allowed") and renders as a
                                // broken image. Load them directly, same as the
                                // community cards (which already do this).
                                unoptimized
                              />
                              {isActive && (
                                <div className="absolute inset-x-0 bottom-0 z-10 bg-linear-to-t from-black/85 via-black/40 to-transparent px-5 pb-5 pt-14 text-left text-white transition-opacity duration-300 group-hover:opacity-0">
                                  <p className="font-[family-name:var(--font-rubik),sans-serif] text-lg font-medium leading-7 tracking-normal text-white">
                                    {item.title}
                                  </p>
                                  {item.description && (
                                    <p className="mt-1 line-clamp-1 font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 tracking-normal text-white">
                                      {item.description}
                                    </p>
                                  )}
                                </div>
                              )}
                              {/* Hover overlay with Use template / Preview buttons (active card only) */}
                              {isActive && (
                                <div
                                  className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center gap-3 bg-black/40 opacity-0 transition-opacity duration-300 ease-out group-hover:pointer-events-auto group-hover:opacity-100"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      goToTemplate();
                                    }}
                                    className="inline-flex scale-95 items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-black shadow-md transition-transform duration-200 ease-out group-hover:scale-100 hover:bg-white/95"
                                  >
                                    Use template
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      goToTemplate();
                                    }}
                                    className="inline-flex scale-95 items-center justify-center gap-2 rounded-full bg-black/40 px-6 py-3 text-sm font-semibold text-white shadow-md backdrop-blur-md transition-transform duration-200 ease-out group-hover:scale-100 hover:bg-black/55"
                                  >
                                    <Eye
                                      className="h-4 w-4"
                                      strokeWidth={2.2}
                                    />
                                    Preview
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {!isLoadingTemplates && items.length > 1 && (
                  <div className="mt-6 flex items-center justify-center gap-6 sm:mt-8 sm:gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        setCarouselIndex((i) => Math.max(0, i - 1))
                      }
                      disabled={activeIdx === 0}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background text-foreground transition-all hover:border-foreground/30 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-40 sm:h-11 sm:w-11"
                      aria-label="Previous template"
                    >
                      <ArrowLeft className="h-4 w-4" strokeWidth={2.2} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setCarouselIndex((i) => Math.min(lastIdx, i + 1))
                      }
                      disabled={activeIdx === lastIdx}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background text-foreground transition-all hover:border-foreground/30 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-40 sm:h-11 sm:w-11"
                      aria-label="Next template"
                    >
                      <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
                    </button>
                  </div>
                )}
              </>
            );
          })()}

          {/* "Don't see what you need?" CTA banner with panda-sward.
              Light mode keeps the Figma background SVG (light gradient);
              dark mode uses the exact Figma frame (2728:76083): solid
              #111522 base, plus a 1004 × 248 sub-area pinned bottom-right
              containing the dark panda-sward image scaled 162% × 180%
              with a 209.94° gradient fade to #111522, producing a subtle
              violet glow behind the panda only. */}
          {/* <div className="relative mt-10 overflow-hidden rounded-3xl border border-border/50 sm:mt-16 lg:h-65 dark:border-white/10 dark:bg-[#111522]">
          
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat dark:hidden"
              style={{ backgroundImage: "url('/panda-sward-bg.svg')" }}
            />
           
            <div
              aria-hidden
              className="pointer-events-none absolute bottom-0 right-0 hidden h-full w-[83.67%] overflow-hidden dark:block"
            >
              <Image
                src="/panda-sward-bg-dark.png"
                alt=""
                width={1586}
                height={992}
                aria-hidden
                priority={false}
                className="absolute left-[-41.04%] top-[-32.04%] h-[179.93%] w-[162.02%] max-w-none object-cover"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(209.94deg, rgba(17, 21, 34, 0) 0%, #111522 82.81%)",
                }}
              />
            </div>
            <div className="relative flex flex-col items-stretch gap-4 px-4 pt-6 sm:gap-6 sm:px-10 sm:pt-12 lg:flex-row lg:items-center lg:justify-between lg:gap-8 lg:px-14 lg:pt-0">
              <div className="w-full py-2 text-left lg:w-auto lg:py-12">
                <h3 className="font-[family-name:var(--font-rubik),sans-serif] text-[20px] font-medium leading-7 tracking-normal text-[#1A1A1B] dark:text-foreground sm:text-[32px] lg:text-[40px] lg:leading-12">
                  Don&apos;t see what you need?
                </h3>
                <p className="mt-2 font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 tracking-normal text-[#77869D] dark:text-muted-foreground sm:mt-3">
                  Build from scratch with our AI-powered builder. No template
                  required.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    setTimeout(
                      () => document.getElementById("prompt-input")?.focus(),
                      500,
                    );
                  }}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-linear-to-r from-[#FF5606] via-[#D52A26] to-[#AA0346] px-7 py-3 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-white shadow-md shadow-orange-500/30 transition-all hover:shadow-lg hover:shadow-orange-500/40 hover:brightness-110 sm:mt-7 sm:w-auto"
                >
                  <FreePlanIcon className="h-4 w-4 text-white" />
                  Start building
                </button>
              </div>
              <div className="relative shrink-0 self-center sm:self-end lg:absolute lg:bottom-0 lg:right-16 lg:top-0 lg:self-auto">
                <Image
                  src="/panda-sward-exact.svg"
                  alt="Pirate panda riding a rocket"
                  width={520}
                  height={520}
                  priority={false}
                  className="block h-auto w-53.25 sm:w-65 lg:h-full lg:w-auto"
                />
              </div>
            </div>
          </div> */}
        </div>
      </section>

      {/* Featured proof — the REAL "apps built this week" count shown as a big
          number in the prominent center card (this replaces the old placeholder
          testimonial quote). Real data only, fail-closed: the whole section
          hides when the count isn't available, so it never shows a fake or
          empty figure. Kept OUT of the SHOW_HOME_TESTIMONIALS gate so the real
          counter survives even when the placeholder testimonials are off. */}
      {appsBuilt !== null && appsBuilt > 0 && (
        <section className="relative z-10 border-t border-border bg-background py-12 dark:border-transparent sm:py-20">
          <div className="mx-auto max-w-3xl px-4 sm:px-8 lg:px-12">
            <figure className="relative overflow-hidden rounded-3xl border border-black/[0.06] bg-white px-6 py-14 text-center shadow-[0_24px_70px_-32px_rgba(99,102,241,0.28),0_8px_24px_-12px_rgba(255,90,60,0.14)] dark:border-white/10 dark:bg-[#11131d] sm:px-10 sm:py-16">
              {/* Soft brand glow behind the number — gives the flat card depth
                  and echoes the hero's orange/magenta dark-mode atmosphere. */}
              <div
                aria-hidden
                className="pointer-events-none absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(255,86,6,0.20),rgba(170,3,70,0.12)_45%,transparent_70%)] blur-3xl sm:h-80 sm:w-80"
              />
              <div className="relative">
                <LiveAppsCounter
                  count={appsBuilt}
                  mostBuilt={[]}
                  variant="showcase"
                />
              </div>
            </figure>
          </div>
        </section>
      )}

      {/* Founder testimonials — 3 PLACEHOLDER cards (see HOME_TESTIMONIALS up
          top); hidden when SHOW_HOME_TESTIMONIALS is false, for shipping before
          real quotes exist. */}
      {SHOW_HOME_TESTIMONIALS && (
          <section className="relative z-10 border-t border-border bg-background py-12 dark:border-transparent sm:py-20">
            <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:max-w-[90rem] lg:px-12">
              <div className="mx-auto max-w-3xl text-center">
                <span className="inline-flex h-7 items-center rounded-full bg-[#FEF3F2] px-3 font-[family-name:var(--font-rubik),sans-serif] text-xs font-medium leading-4 tracking-normal uppercase text-[#B90C3B] dark:bg-rose-500/15 dark:text-rose-300">
                  In their words
                </span>
                <h2 className="mt-4 text-balance text-center font-[family-name:var(--font-rubik),sans-serif] text-[32px] font-medium leading-10 tracking-normal text-foreground sm:mt-5 sm:text-[44px] sm:leading-none lg:text-[56px]">
                  What founders are saying.
                </h2>
                <p className="mx-auto mt-2 max-w-2xl text-center font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 tracking-normal text-[#77869D] dark:text-muted-foreground sm:mt-4">
                  Real people. Real customers. Real results.
                </p>
              </div>

            </div>

            {/* Full-bleed auto-scrolling marquee. Cards are duplicated so the
                -50% loop is seamless (see .animate-marquee-scroll in
                globals.css); the second copy is aria-hidden. Pauses on hover;
                static under reduced-motion. Edge gradients fade the cards in and
                out at the rails so they don't hard-cut. */}
            <div className="group relative mt-10 overflow-hidden py-4 sm:mt-14 sm:py-6">
              <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-linear-to-r from-background to-transparent sm:w-28" />
              <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-linear-to-l from-background to-transparent sm:w-28" />
              <div className="flex w-max animate-marquee-scroll group-hover:[animation-play-state:paused]">
                {[...HOME_TESTIMONIALS, ...HOME_TESTIMONIALS].map((t, i) => {
                  // Stable per-founder color (same in both duplicated copies).
                  const color =
                    AVATAR_COLORS[
                      (i % HOME_TESTIMONIALS.length) % AVATAR_COLORS.length
                    ];
                  const rating =
                    TESTIMONIAL_RATINGS[i % HOME_TESTIMONIALS.length] ?? 5;
                  return (
                    <figure
                      key={`${t.name}-${i}`}
                      aria-hidden={i >= HOME_TESTIMONIALS.length}
                      className="mx-3 flex w-[320px] shrink-0 flex-col rounded-3xl border border-black/[0.06] bg-white relative z-0 p-6 shadow-[0_18px_50px_-24px_rgba(99,102,241,0.18),0_6px_18px_-10px_rgba(255,90,60,0.10)] transition-all duration-300 hover:z-10 hover:scale-[1.04] hover:shadow-[0_26px_60px_-24px_rgba(99,102,241,0.30)] dark:border-white/10 dark:bg-[#11131d] dark:hover:shadow-[0_30px_70px_-28px_rgba(0,0,0,0.9)] sm:w-[360px]"
                    >
                      <div className="flex items-start justify-between">
                        <StarRating rating={rating} />
                        <Quote
                          className="h-7 w-7 shrink-0 text-foreground/[0.07] dark:text-white/10"
                          aria-hidden
                        />
                      </div>
                      <blockquote className="mt-4 flex-1 font-[family-name:var(--font-rubik),sans-serif] text-[15px] font-normal leading-relaxed tracking-normal text-foreground/90">
                        “{t.quote}”
                      </blockquote>
                      <figcaption className="mt-6 flex items-center gap-3 border-t border-border/60 pt-5">
                        <span
                          className={cn(
                            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-[family-name:var(--font-rubik),sans-serif] text-sm font-semibold",
                            color,
                          )}
                        >
                          {initialsFor(t.name)}
                        </span>
                        <span className="font-[family-name:var(--font-rubik),sans-serif] leading-5">
                          <span className="block text-sm font-semibold text-foreground">
                            {t.name}
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            {t.role}
                          </span>
                        </span>
                      </figcaption>
                    </figure>
                  );
                })}
              </div>
            </div>
          </section>
      )}

      {/* Recent Ideas Section */}
      {user && recentPrompts.length > 0 && (
        <section className="border-t border-border py-16 bg-muted/40">
          <div className="mx-auto max-w-4xl px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl font-bold tracking-tight text-foreground">
                Recent Ideas
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Quickly regenerate apps from your previous prompts
              </p>
            </div>

            <div className="space-y-3">
              {recentPrompts.map((project, index) => (
                <div
                  key={project.id}
                  className="group flex items-center gap-4 rounded-lg border border-border bg-card p-4 transition-all hover:border-border hover:bg-accent/20 hover:shadow-sm"
                >
                  {/* Timeline indicator */}
                  <div className="flex items-center justify-center">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground group-hover:bg-orange-500/15 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                      <Clock className="h-4 w-4" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-medium text-foreground truncate">
                        {project.title || "Untitled Project"}
                      </h3>
                      <span className="text-[11px] text-muted-foreground">
                        {timeAgo(project.updated_at || project.created_at)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {project.prompt}
                    </p>
                  </div>

                  {/* Action */}
                  <div className="flex items-center">
                    <button
                      onClick={() => handleRegenerate(project)}
                      className="flex items-center justify-center rounded-full p-2 text-muted-foreground transition-colors hover:text-orange-600 hover:bg-orange-500/10 dark:hover:text-orange-400"
                      title="Regenerate from this prompt"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Comparison + inline Pricing — flag-gated off in the conversion
          redesign (SHOW_LEGACY_HOME_SECTIONS); kept so they can be revisited.
          Pricing still lives at the /pricing route, linked from the nav. */}
      {SHOW_LEGACY_HOME_SECTIONS && <WithPandaComparison />}

      {SHOW_LEGACY_HOME_SECTIONS && (
        <PricingSection
          user={user}
          openLogin={openLogin}
          postAuthPath="/pricing"
          anchorId="pricing"
          currentPlan={userPlan}
        />
      )}

      {/* Footer CTA — "start building" prompt at the end of the page (replaces
          the removed parachute-panda banner). Lovable-style gradient backdrop
          that flows into the footer so the prompt card sits on an intentional
          surface. Reuses the hero's prompt state + handlers. */}
      <section className="relative overflow-hidden bg-linear-to-b from-background to-[#fbf3f0] py-16 dark:from-background dark:to-card sm:py-24">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-72 bg-[radial-gradient(70%_90%_at_50%_100%,rgba(255,86,6,0.12),rgba(170,3,70,0.07)_45%,transparent_75%)]"
        />
        <div className="relative mx-auto w-full max-w-3xl px-4 text-center sm:px-8">
          <h2 className="font-[family-name:var(--font-rubik),sans-serif] text-[30px] font-medium leading-tight tracking-normal text-foreground sm:text-[44px] sm:leading-none">
            Ready to build?
          </h2>
          <p className="mx-auto mt-3 max-w-xl font-[family-name:var(--font-rubik),sans-serif] text-base text-muted-foreground">
            Describe your idea — Panda builds and launches it in minutes.
          </p>

          <PromptComposer
            idPrefix="cta-"
            cardRef={footerCardRef}
            heroMode={heroMode}
            setHeroMode={setHeroMode}
            prompt={prompt}
            setPrompt={setPrompt}
            focused={focused}
            setFocused={setFocused}
            ideaError={ideaError}
            setIdeaError={setIdeaError}
            generatingIdea={generatingIdea}
            onSubmitIdea={() => {
              setLastSubmitOrigin("hero");
              void handleGenerate();
            }}
            attachFiles={attachFiles}
            setAttachFiles={setAttachFiles}
            businessUrl={businessUrl}
            setBusinessUrl={setBusinessUrl}
            businessUrlError={businessUrlError}
            setBusinessUrlError={setBusinessUrlError}
            businessUrlLimitReached={businessUrlLimitReached}
            setBusinessUrlLimitReached={setBusinessUrlLimitReached}
            generatingGrowth={generatingGrowth}
            onSubmitGrowth={() => {
              setLastSubmitOrigin("hero");
              void handleGenerateGrowth();
            }}
            normaliseWebsiteUrl={normaliseWebsiteUrl}
            onUpgrade={() => router.push("/settings/billing")}
            canUpgradePlan={hasHigherPlan(userPlan)}
          />
        </div>
      </section>
      

      {/* Home-page footer bar — logo + nav + legal + theme toggle.
          Mobile: vertical nav stack + theme toggle on right, legal row at
          bottom per Figma. Desktop (lg+): horizontal layout. */}
      {/* pb leaves room for the fixed StickyPromptBar so the legal links
          stay reachable at the very bottom of the page. */}
      <footer className="bg-[#F8FAFC] pb-36 dark:bg-card lg:bg-linear-to-r lg:from-[#f5f1f7] lg:via-[#f7eef1] lg:to-[#faece6] lg:pt-6 lg:pb-28 lg:dark:from-card lg:dark:via-card lg:dark:to-card">
        {/* Mobile layout (Figma 2227:50940) — vertical nav stack on left,
            theme toggle on right, legal row at the bottom. */}
        <div className="flex flex-col gap-6 p-4 lg:hidden">
          <div className="flex items-start justify-between">
            <div className="flex flex-col items-start justify-center gap-3">
              <a
                href="/"
                className="flex items-center gap-2 text-foreground transition-opacity hover:opacity-80"
                aria-label="Panda home"
              >
                <PandaLogo size="xs" className="rounded-md" />
                <span className="font-[family-name:var(--font-rubik),sans-serif] text-2xl font-bold leading-none tracking-tight text-foreground">
                  panda
                </span>
              </a>
              <nav className="flex flex-col items-start gap-1">
                {SHOW_LEGACY_HOME_SECTIONS && (
                  <Link
                    href="/community"
                    className="flex h-7 items-center font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-[#1A1A1B] transition-colors hover:text-foreground dark:text-foreground"
                  >
                    Community
                  </Link>
                )}
                <Link
                  href="/pricing"
                  className="flex h-7 items-center font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-[#1A1A1B] transition-colors hover:text-foreground dark:text-foreground"
                >
                  Pricing
                </Link>
                <a
                  href="#templates"
                  className="flex h-7 items-center font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal text-[#1A1A1B] transition-colors hover:text-foreground dark:text-foreground"
                >
                  Templates
                </a>
              </nav>
            </div>
            <FooterThemeToggle />
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/terms"
              className="font-[family-name:var(--font-rubik),sans-serif] text-xs font-normal leading-4 tracking-normal text-[#77869D] dark:text-muted-foreground transition-colors hover:text-foreground"
            >
              Terms &amp; Conditions
            </a>
            <a
              href="/privacy"
              className="font-[family-name:var(--font-rubik),sans-serif] text-xs font-normal leading-4 tracking-normal text-[#77869D] dark:text-muted-foreground transition-colors hover:text-foreground"
            >
              Privacy Policy
            </a>
            <a
              href="/refund"
              className="font-[family-name:var(--font-rubik),sans-serif] text-xs font-normal leading-4 tracking-normal text-[#77869D] dark:text-muted-foreground transition-colors hover:text-foreground"
            >
              Refund Policy
            </a>
            <a
              href="mailto:help@panda.new"
              className="font-[family-name:var(--font-rubik),sans-serif] text-xs font-normal leading-4 tracking-normal text-[#77869D] dark:text-muted-foreground transition-colors hover:text-foreground"
            >
              Contact
            </a>
            <span className="font-[family-name:var(--font-rubik),sans-serif] text-xs font-normal leading-4 tracking-normal text-[#77869D] dark:text-muted-foreground">
              &copy; {currentYear} Panda
            </span>
          </div>
        </div>

        {/* Desktop layout — unchanged horizontal nav/legal */}
        <div className="mx-auto hidden max-w-7xl px-4 pr-20 sm:px-8 sm:pr-24 lg:flex lg:max-w-[90rem] lg:flex-row lg:items-center lg:justify-between lg:gap-8 lg:px-12 lg:pr-24">
          {/* Left: logo + nav */}
          <div className="flex flex-wrap items-center justify-center gap-5 lg:justify-start lg:gap-8">
            <a
              href="/"
              className="flex items-center gap-2 text-foreground transition-opacity hover:opacity-80"
              aria-label="Panda home"
            >
              <PandaLogo size="xs" className="rounded-md" />
              <span className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium leading-5 tracking-normal text-foreground">
                panda
              </span>
            </a>
            <nav className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              {SHOW_LEGACY_HOME_SECTIONS && (
                <Link
                  href="/community"
                  className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium leading-5 tracking-normal text-foreground/85 transition-colors hover:text-foreground"
                >
                  Community
                </Link>
              )}
              <Link
                href="/pricing"
                className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium leading-5 tracking-normal text-foreground/85 transition-colors hover:text-foreground"
              >
                Pricing
              </Link>
              <a
                href="#templates"
                className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium leading-5 tracking-normal text-foreground/85 transition-colors hover:text-foreground"
              >
                Templates
              </a>
            </nav>
          </div>
          {/* Right: legal + theme toggle */}
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-5 lg:justify-end">
            <a
              href="/terms"
              className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 tracking-normal text-[#94A3B8] transition-colors hover:text-foreground"
            >
              Terms &amp; Conditions
            </a>
            <a
              href="/privacy"
              className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 tracking-normal text-[#94A3B8] transition-colors hover:text-foreground"
            >
              Privacy Policy
            </a>
            <a
              href="/refund"
              className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 tracking-normal text-[#94A3B8] transition-colors hover:text-foreground"
            >
              Refund Policy
            </a>
            <a
              href="mailto:help@panda.new"
              className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 tracking-normal text-[#94A3B8] transition-colors hover:text-foreground"
            >
              Contact
            </a>
            <span className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 tracking-normal text-[#94A3B8]">
              &copy; {currentYear} Panda
            </span>
          </div>
        </div>
      </footer>

      {/* Sticky bottom prompt bar — "start from anywhere" action (Figma).
          Shares the hero card's state so text/files stay in sync between the
          two inputs, and submits through the same handlers so the
          build/sign-up flow is identical. Hidden while EITHER the hero card or
          the footer prompt card is in view, so the top and bottom of the page
          never show two identical inputs at once. */}
      <StickyPromptBar
        visible={!heroCardInView && !footerCardInView}
        mode={heroMode}
        prompt={prompt}
        onPromptChange={(value) => {
          setPrompt(value);
          if (ideaError) setIdeaError(null);
        }}
        businessUrl={businessUrl}
        onBusinessUrlChange={(value) => {
          setBusinessUrl(value);
          if (businessUrlError) setBusinessUrlError(null);
          if (businessUrlLimitReached) setBusinessUrlLimitReached(false);
        }}
        attachFiles={attachFiles}
        onAttachFilesChange={setAttachFiles}
        busy={
          heroMode === "idea"
            ? generatingIdea || isGenerating
            : generatingGrowth
        }
        error={
          lastSubmitOrigin === "sticky"
            ? heroMode === "idea"
              ? ideaError
              : businessUrlError
            : null
        }
        onSubmit={() => {
          setLastSubmitOrigin("sticky");
          if (heroMode === "idea") {
            void handleGenerate();
          } else {
            void handleGenerateGrowth();
          }
        }}
      />

      <SignupModal
        open={signupOpen}
        onOpenChange={(o) => {
          setSignupOpen(o);
          if (!o) setSignupInitialEmail("");
        }}
        next={signupNext}
        initialEmail={signupInitialEmail}
        source={signupSource}
      />

      <LoginModal
        open={loginOpen}
        onOpenChange={(o) => {
          setLoginOpen(o);
          if (!o) setLoginInitialError(null);
        }}
        next={loginNext}
        initialError={loginInitialError}
        onSwitchToSignup={(email) => {
          setLoginOpen(false);
          openSignup(loginNext, email, "login_modal_switch");
        }}
      />

      {/* "Never built" survey (PANDA-1245) — only for a signed-in user whose
          projects have loaded and who has none (never started a build). */}
      <NeverBuiltSurvey
        enabled={Boolean(authUserId) && projectsLoaded && projects.length === 0}
        signupAtMs={signupAtMs}
        hasUpgraded={userPlan !== "free"}
      />
    </div>
  );
}
